import React, { useEffect, useMemo, useRef, useState } from "react";
import { getPromptPackSafety } from "../utils/promptPackSafety";
import { ACCOUNT_ACCESS_PRESETS, AccountAccessPresetKey, getAccountAccessPreset } from "../lib/admin/accessPresets";
import { extractDomain, normalizeWebsiteUrl } from "../lib/admin/domain";
import { getDesiredPromptCount } from "../lib/admin/promptCountPolicy";
import { supabase } from "../lib/supabase";
import { postRunAnalysis } from "../lib/runAnalysisApi";
import CompetitiveSetupIntelligence from "../components/admin/CompetitiveSetupIntelligence";
import CaiReviewedAssetPanel from "../components/admin/CaiReviewedAssetPanel";
import CaiDiscoverySourcesPanel from "../components/admin/CaiDiscoverySourcesPanel";
import AccountSelector from "../components/admin/AccountSelector";
import { mergeOnboardingLocalConfig } from "../lib/admin/onboardingLocalConfig";
import {
  buildPromptPackReviewSummary,
  formatPromptPackReviewIntegrityError,
  PromptPackReviewDialog,
  validatePromptPackReviewDraft
} from "./promptPackReview";
import type { PromptPackReviewDraftPack } from "./promptPackReview";

type AdminOnboardingProps = {
  session: any;
};

type PromptPack = {
  id: string;
  pack_key: string;
  label: string;
  product_type: string;
  category_key: string;
  category_label?: string | null;
  expected_prompt_count: number;
  actual_prompt_count: number;
  source_account_id?: string | null;
  source_account_name?: string | null;
  description?: string | null;
  notes?: string | null;
  is_active: boolean;
};

type PromptPackOption = PromptPack & {
  fit_score: number;
  fit_label: string;
  fit_warning?: string | null;
};


type AccountOption = {
  account_id: string;
  account_name: string;
  display_name?: string | null;
  business_name?: string | null;
  website_url?: string | null;
  website_domain?: string | null;
  status?: string | null;
  category?: string | null;
  category_type?: string | null;
  account_type?: string | null;
  market?: string | null;
  market_scope?: string | null;
  geo_prompting_enabled?: boolean | null;
  primary_geo_type?: string | null;
  primary_geo_value?: string | null;
  secondary_geo_value?: string | null;
  geo_notes?: string | null;
  local_config?: Record<string, unknown> | null;
  onboarding_status?: string | null;
  onboarding_notes?: string | null;
  run_status?: string | null;
  last_run_status?: string | null;
  created_at?: string | null;
};

type BackgroundJobStatus = "queued" | "running" | "succeeded" | "failed";
type PromptPackDraftStatus = "queued" | "generating" | "review_ready" | "failed";
type PromptGenerationViewState = "idle" | "queued" | "generating" | "review_ready" | "failed" | "unexpected";

type PersistedDraftPrompt = {
  sort_order: number;
  category: string;
  prompt_intent?: string | null;
  prompt_intent_detail?: string | null;
  prompt_text: string;
  prompt_type: "organic" | "guided";
  prompt_weight: number;
};

type BackgroundJobExecution = {
  id: string;
  type: "prompt_pack_generation";
  status: BackgroundJobStatus;
  account_id?: string;
  subject_id?: string;
  queued_at: string;
  started_at: string | null;
  heartbeat_at: string | null;
  completed_at: string | null;
  failed_at: string | null;
  error_code: string | null;
  error_message: string | null;
  result_summary: Record<string, unknown> | null;
};

type PromptPackDraftLifecycle = {
  id: string;
  account_id: string;
  market_profile_id?: string | null;
  status: PromptPackDraftStatus;
  desired_prompt_count: number;
  prompt_count: number;
  warning_count: number;
  metadata: Record<string, unknown>;
  validation_summary: Record<string, unknown>;
  queued_at?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  failed_at?: string | null;
  error_code?: string | null;
  error_message?: string | null;
  prompts?: PersistedDraftPrompt[];
};

type PromptGenerationEnvelope = {
  ok: true;
  job: BackgroundJobExecution;
  draft: PromptPackDraftLifecycle;
};

type PromptGenerationEnqueueResponse = PromptGenerationEnvelope & {
  queued: true;
  reused: boolean;
};

type PromptGenerationStatusResponse = PromptGenerationEnvelope;

type StructuredPromptGenerationFailure = {
  code: string;
  message: string;
  failedAt: string | null;
};

type PromptPackReviewIntegrityFailure = {
  code: string;
  message: string;
  details: string[];
};

type GeneratedPromptPackDraft = {
  ok: true;
  saved: false;
  applied: false;
  draft_prompt_pack: {
    pack_key: string;
    label: string;
    summary?: string | null;
    product_type: string;
    category_key?: string | null;
    desired_prompt_count: number;
    prompt_count: number;
    counts_by_category?: Record<string, number>;
    prompt_mix_notes?: string | null;
    warnings: string[];
    prompt_diversity?: unknown;
    prompts: PersistedDraftPrompt[];
    [key: string]: unknown;
  };
};

type AccountPromptSummaryCategoryCount = {
  category: string;
  count: number;
};

type AccountPromptSummaryPromptSet = {
  prompt_set?: string | null;
  prompt_set_id?: string | null;
  prompt_pack_label?: string | null;
  prompt_pack_id?: string | null;
  active_prompt_count?: number | null;
  organic_count?: number | null;
  organic_prompt_count?: number | null;
  guided_count?: number | null;
  guided_prompt_count?: number | null;
  geo_specific_count?: number | null;
  last_created_at?: string | null;
  last_applied_at?: string | null;
  last_generated_at?: string | null;
  category_count?: number | null;
  counts_by_category?: Record<string, number> | null;
  category_counts?: Record<string, number> | Array<{ category?: string; count?: number }> | null;
  prompt_mix?: Record<string, unknown> | null;
  prompt_mix_counts?: Record<string, unknown> | null;
  [key: string]: unknown;
};

type ActivePromptSetupSummary = {
  activePromptCount: number;
  activeStatusLabel: "Ready" | "Needs prompts";
  activePackLabel: string | null;
  activePackId: string | null;
  lastEventAt: string | null;
  organicCount: number | null;
  guidedCount: number | null;
  categoryCount: number | null;
  categoryBreakdown: AccountPromptSummaryCategoryCount[];
  promptSets: AccountPromptSummaryPromptSet[];
  sourceLabel: string | null;
};

export type PromptGenerationDiscoveryResult =
  | {
      kind: "found";
      job: BackgroundJobExecution;
      draft: PromptPackDraftLifecycle;
    }
  | {
      kind: "not_found";
    }
  | {
      kind: "transient_failure";
      message: string;
    };

export async function discoverLatestPromptGenerationDraft({
  accountId,
  accessToken,
  signal
}: {
  accountId: string;
  accessToken: string;
  signal?: AbortSignal;
}): Promise<PromptGenerationDiscoveryResult> {
  const response = await fetch(
    `/api/admin-onboarding-generate-prompt-pack-draft-latest?account_id=${encodeURIComponent(accountId)}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`
      },
      signal
    }
  );

  const json = await response.json().catch(() => null) as {
    ok?: boolean;
    found?: boolean;
    job?: BackgroundJobExecution | null;
    draft?: PromptPackDraftLifecycle | null;
    error?: string;
  } | null;

  if (!response.ok || !json?.ok) {
    if (response.status >= 500 || !json) {
      return {
        kind: "transient_failure",
        message: json?.error || "Latest prompt draft discovery is temporarily unavailable."
      };
    }

    return {
      kind: "transient_failure",
      message: json.error || "Latest prompt draft discovery is temporarily unavailable."
    };
  }

  if (!json.found || !json.job || !json.draft) {
    return { kind: "not_found" };
  }

  if (
    (json.job.account_id && json.job.account_id !== accountId) ||
    json.draft.account_id !== accountId ||
    json.job.subject_id !== json.draft.id
  ) {
    return {
      kind: "transient_failure",
      message: "Latest prompt draft discovery returned an unexpected account response."
    };
  }

  return {
    kind: "found",
    job: json.job,
    draft: json.draft
  };
}

export type PromptGenerationRecoveryResult =
  | {
      kind: "found";
      source: "status" | "latest";
      job: BackgroundJobExecution;
      draft: PromptPackDraftLifecycle;
      pointerWasStale: boolean;
    }
  | {
      kind: "failed";
      job: BackgroundJobExecution;
      draft: PromptPackDraftLifecycle;
      pointerWasStale: boolean;
    }
  | {
      kind: "not_found";
      pointerWasStale: boolean;
      message?: string;
    }
  | {
      kind: "transient_failure";
      message: string;
      preservePointer: boolean;
    };

export async function recoverPromptGenerationDraftForAccount({
  accountId,
  accessToken,
  pointerJobId,
  signal
}: {
  accountId: string;
  accessToken: string;
  pointerJobId: string;
  signal?: AbortSignal;
}): Promise<PromptGenerationRecoveryResult> {
  async function recoverLatestDraft(pointerWasStale: boolean) {
    const latestDiscovery = await discoverLatestPromptGenerationDraft({
      accountId,
      accessToken,
      signal
    });

    if (latestDiscovery.kind === "found") {
      return {
        kind: "found" as const,
        source: "latest" as const,
        pointerWasStale,
        job: latestDiscovery.job,
        draft: latestDiscovery.draft
      };
    }

    if (latestDiscovery.kind === "transient_failure") {
      return {
        kind: "not_found" as const,
        pointerWasStale,
        message: latestDiscovery.message
      };
    }

    return {
      kind: "not_found" as const,
      pointerWasStale
    };
  }

  if (pointerJobId) {
    const response = await fetch(
      `/api/admin-onboarding-generate-prompt-pack-draft-status?job_id=${encodeURIComponent(pointerJobId)}&account_id=${encodeURIComponent(accountId)}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`
        },
        signal
      }
    );

    const json = await response.json().catch(() => null) as {
      ok?: boolean;
      job?: BackgroundJobExecution | null;
      draft?: PromptPackDraftLifecycle | null;
      error?: string;
    } | null;

    if (response.status >= 500 || !json) {
      return {
        kind: "transient_failure",
        message: json?.error || "Prompt generation status is temporarily unavailable.",
        preservePointer: true
      };
    }

    const stalePointer =
      !response.ok ||
      !json.ok ||
      !json.job ||
      !json.draft ||
      (json.job.account_id && json.job.account_id !== accountId) ||
      json.draft.account_id !== accountId ||
      json.job.subject_id !== json.draft.id;

    if (stalePointer) {
      return recoverLatestDraft(true);
    }

    if (json.job.status === "failed" || json.draft.status === "failed") {
      const latestDiscovery = await discoverLatestPromptGenerationDraft({
        accountId,
        accessToken,
        signal
      });

      if (latestDiscovery.kind === "found") {
        return {
          kind: "found",
          source: "latest",
          pointerWasStale: true,
          job: latestDiscovery.job,
          draft: latestDiscovery.draft
        };
      }

      return {
        kind: "failed",
        job: json.job,
        draft: json.draft,
        pointerWasStale: false
      };
    }

    return {
      kind: "found",
      pointerWasStale: false,
      source: "status",
      job: json.job,
      draft: json.draft
    };
  }

  const latestDiscovery = await discoverLatestPromptGenerationDraft({
    accountId,
    accessToken,
    signal
  });

  if (latestDiscovery.kind !== "found") {
    return latestDiscovery.kind === "transient_failure"
      ? {
          kind: "transient_failure",
          message: latestDiscovery.message,
          preservePointer: false
        }
      : {
          kind: "not_found",
          pointerWasStale: false
        };
  }

  return {
    kind: "found",
    pointerWasStale: false,
    source: "latest",
    job: latestDiscovery.job,
    draft: latestDiscovery.draft
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function isNonBlankString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function toFiniteNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed || !/^-?\d+(?:\.\d+)?$/.test(trimmed)) {
      return null;
    }

    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

function firstNonBlankString(...values: unknown[]) {
  for (const value of values) {
    if (isNonBlankString(value)) {
      return value.trim();
    }
  }

  return null;
}

function extractCategoryBreakdown(value: unknown): AccountPromptSummaryCategoryCount[] {
  if (!value || typeof value !== "object") {
    return [];
  }

  if (Array.isArray(value)) {
    return value
      .map((entry) => {
        if (!entry || typeof entry !== "object") return null;
        const category = Reflect.get(entry, "category");
        const count = Reflect.get(entry, "count");

        if (!isNonBlankString(category) || toFiniteNumber(count) === null) {
          return null;
        }

        return {
          category: category.trim(),
          count: Number(count)
        };
      })
      .filter((entry): entry is AccountPromptSummaryCategoryCount => Boolean(entry));
  }

  return Object.entries(value as Record<string, unknown>)
    .map(([category, count]) => {
      if (!isNonBlankString(category) || toFiniteNumber(count) === null) {
        return null;
      }

      return {
        category: category.trim(),
        count: Number(count)
      };
    })
    .filter((entry): entry is AccountPromptSummaryCategoryCount => Boolean(entry));
}

function buildActivePromptSetupSummary(summary: any | null): ActivePromptSetupSummary | null {
  if (!summary || typeof summary !== "object") {
    return null;
  }

  const promptSets = Array.isArray(summary.prompt_sets)
    ? summary.prompt_sets.filter((entry: unknown): entry is AccountPromptSummaryPromptSet => Boolean(entry) && typeof entry === "object")
    : [];

  const topLevelCount =
    toFiniteNumber(summary.total_active_prompts) ??
    toFiniteNumber(summary.active_prompt_count) ??
    toFiniteNumber(summary.active_prompts) ??
    toFiniteNumber(summary.prompt_count);

  const promptSetCount = promptSets.reduce((total, set) => {
    const count = toFiniteNumber(set.active_prompt_count) ?? toFiniteNumber(set.prompt_count) ?? 0;
    return total + count;
  }, 0);

  const activePromptCount = topLevelCount ?? promptSetCount;
  const firstSet = promptSets[0] || null;
  const activePackLabel = firstNonBlankString(
    summary.active_pack_label,
    summary.prompt_pack_label,
    summary.prompt_set_label,
    summary.pack_label,
    summary.label,
    summary.active_prompt_pack_label,
    firstSet?.prompt_pack_label,
    firstSet?.prompt_set,
    firstSet?.prompt_set_id
  );
  const activePackId = firstNonBlankString(
    summary.active_pack_id,
    summary.prompt_pack_id,
    summary.prompt_set_id,
    summary.pack_id,
    summary.active_prompt_pack_id,
    firstSet?.prompt_pack_id,
    firstSet?.prompt_set_id
  );
  const lastEventAt = firstNonBlankString(
    summary.last_applied_at,
    summary.last_generated_at,
    summary.last_created_at,
    summary.updated_at,
    firstSet?.last_applied_at,
    firstSet?.last_created_at,
    firstSet?.last_generated_at
  );

  const organicCount =
    toFiniteNumber(summary.organic_count) ??
    toFiniteNumber(summary.organic_prompt_count) ??
    toFiniteNumber(summary.active_organic_prompt_count) ??
    toFiniteNumber(summary.active_organic_count) ??
    toFiniteNumber(summary.prompt_mix?.organic) ??
    toFiniteNumber(summary.prompt_mix?.organic_count) ??
    toFiniteNumber(summary.prompt_mix_counts?.organic) ??
    toFiniteNumber(summary.prompt_mix_counts?.organic_count) ??
    toFiniteNumber(firstSet?.organic_count) ??
    toFiniteNumber(firstSet?.organic_prompt_count) ??
    null;

  const guidedCount =
    toFiniteNumber(summary.guided_count) ??
    toFiniteNumber(summary.guided_prompt_count) ??
    toFiniteNumber(summary.active_guided_prompt_count) ??
    toFiniteNumber(summary.active_guided_count) ??
    toFiniteNumber(summary.prompt_mix?.guided) ??
    toFiniteNumber(summary.prompt_mix?.guided_count) ??
    toFiniteNumber(summary.prompt_mix_counts?.guided) ??
    toFiniteNumber(summary.prompt_mix_counts?.guided_count) ??
    toFiniteNumber(firstSet?.guided_count) ??
    toFiniteNumber(firstSet?.guided_prompt_count) ??
    null;

  const categoryBreakdownCandidates = [
    extractCategoryBreakdown(summary.category_counts),
    extractCategoryBreakdown(summary.counts_by_category),
    extractCategoryBreakdown(summary.category_breakdown),
    extractCategoryBreakdown(summary.prompt_category_counts),
    extractCategoryBreakdown(firstSet?.category_counts),
    extractCategoryBreakdown(firstSet?.counts_by_category),
    extractCategoryBreakdown(firstSet?.category_breakdown)
  ];
  const categoryBreakdown =
    categoryBreakdownCandidates.find((entries) => entries.length > 0) || [];

  const categoryCount =
    toFiniteNumber(summary.category_count) ??
    toFiniteNumber(summary.total_category_count) ??
    toFiniteNumber(firstSet?.category_count) ??
    (categoryBreakdown.length ? categoryBreakdown.length : null);

  return {
    activePromptCount: activePromptCount ?? 0,
    activeStatusLabel: (activePromptCount ?? 0) > 0 ? "Ready" : "Needs prompts",
    activePackLabel,
    activePackId,
    lastEventAt,
    organicCount,
    guidedCount,
    categoryCount,
    categoryBreakdown,
    promptSets,
    sourceLabel: promptSets.length > 1 ? `${promptSets.length} active prompt sets` : promptSets.length === 1 ? "1 active prompt set" : null
  };
}

function formatActivePromptTimestamp(value: string | null | undefined) {
  if (!value) return null;

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
}

function promptGenerationStorageKey(accountId: string) {
  return `toren:prompt-pack-generation:${accountId}`;
}

function derivePromptGenerationViewState(
  job: BackgroundJobExecution | null,
  draft: PromptPackDraftLifecycle | null
): PromptGenerationViewState {
  if (!job || !draft) return "idle";
  if (job.status === "failed" || draft.status === "failed") return "failed";
  if (job.status === "queued" && draft.status === "queued") return "queued";
  if (job.status === "running" && draft.status === "generating") return "generating";
  if (job.status === "succeeded" && draft.status === "review_ready") return "review_ready";
  return "unexpected";
}

function buildGeneratedPromptPackDraftFromStatus(
  draft: PromptPackDraftLifecycle
): GeneratedPromptPackDraft {
  const metadata = isRecord(draft.metadata) ? draft.metadata : {};
  const validationSummary = isRecord(draft.validation_summary) ? draft.validation_summary : null;
  const prompts = Array.isArray(draft.prompts)
    ? [...draft.prompts].sort((left, right) => left.sort_order - right.sort_order)
    : [];
  const packKey = typeof metadata.pack_key === "string" ? metadata.pack_key : "";
  const label = typeof metadata.label === "string" ? metadata.label : "";
  const productType = typeof metadata.product_type === "string" ? metadata.product_type : "";

  if (
    !packKey ||
    !label ||
    !productType ||
    !validationSummary ||
    prompts.length !== draft.prompt_count ||
    !prompts.length
  ) {
    throw new Error("The completed draft is missing required review fields.");
  }

  const derivedCountsByCategory = prompts.reduce((acc, prompt) => {
    const category = String(prompt.category || "").trim();
    if (!category) return acc;
    acc[category] = (acc[category] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const reviewProjection = validatePromptPackReviewDraft({
    ...metadata,
    pack_key: packKey,
    label,
    product_type: productType,
    desired_prompt_count: draft.desired_prompt_count,
    prompt_count: draft.prompt_count,
    validation_summary: validationSummary,
    counts_by_category: derivedCountsByCategory,
    prompt_mix_notes: typeof metadata.prompt_mix_notes === "string" ? metadata.prompt_mix_notes : null,
    warnings: Array.isArray(metadata.warnings)
      ? metadata.warnings.filter((warning): warning is string => typeof warning === "string")
      : [],
    prompt_diversity: metadata.prompt_diversity,
    prompts
  });

  if (!reviewProjection.ok) {
    throw new Error("The completed draft failed review validation.");
  }

  return {
    ok: true,
    saved: false,
    applied: false,
    draft_prompt_pack: {
      ...metadata,
      draft_id: draft.id,
      draft_status: draft.status,
      queued_at: draft.queued_at || null,
      completed_at: draft.completed_at || null,
      warning_count: draft.warning_count,
      generation_governance_summary: isRecord(metadata.generation_governance_summary)
        ? metadata.generation_governance_summary
        : null,
      pack_key: packKey,
      label,
      product_type: productType,
      category_key: typeof metadata.category_key === "string" ? metadata.category_key : null,
      summary: typeof metadata.summary === "string" ? metadata.summary : null,
      desired_prompt_count: draft.desired_prompt_count,
      prompt_count: draft.prompt_count,
      validation_summary: validationSummary,
      counts_by_category: derivedCountsByCategory,
      prompt_mix_notes: typeof metadata.prompt_mix_notes === "string" ? metadata.prompt_mix_notes : null,
      warnings: Array.isArray(metadata.warnings)
        ? metadata.warnings.filter((warning): warning is string => typeof warning === "string")
        : [],
      prompt_diversity: metadata.prompt_diversity,
      prompts: reviewProjection.normalizedPrompts
    }
  };
}

export function buildRecoveredPreviousDraftNotice({
  failure,
  job,
  draft,
  hasReviewReadyDraft
}: {
  failure: StructuredPromptGenerationFailure | null;
  job: BackgroundJobExecution | null;
  draft: PromptPackDraftLifecycle | null;
  hasReviewReadyDraft: boolean;
}) {
  if (!failure || !hasReviewReadyDraft || !job || !draft) return null;

  return {
    title: "Recovered previous draft",
    message: "The latest generation failed and did not replace this persisted draft.",
    jobStatus: job.status,
    draftStatus: draft.status,
    draftCompletedAt: draft.completed_at || job.completed_at || null
  };
}

function normalizePromptPackKey(value: string | null | undefined) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function promptPackTextHaystack(pack: PromptPack) {
  return [
    pack.pack_key,
    pack.label,
    pack.category_key,
    pack.category_label,
    pack.product_type,
    pack.description,
    pack.notes,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

const modelProfiles = [
  {
    key: "demo_competitive",
    label: "Demo Competitive",
    description: "Fast demo profile using OpenAI + Gemini.",
    runLabel: "demo-admin-run",
  },
  {
    key: "production_competitive",
    label: "Production Competitive",
    description: "Full production profile using all active competitive providers.",
    runLabel: "manual-admin-run",
  },
  {
    key: "production_competitive_core",
    label: "Production Competitive Core",
    description: "Core production profile without Perplexity.",
    runLabel: "manual-admin-run",
  },
];

function AdminOnboarding({ session }: AdminOnboardingProps) {
  const [isCheckingAdmin, setIsCheckingAdmin] = useState(true);
  const [hasAdminAccess, setHasAdminAccess] = useState(false);
  const [onboardingMode, setOnboardingMode] = useState<"internal" | "partner">("internal");
  const [accountName, setAccountName] = useState("");
  const [website, setWebsite] = useState("");
  const [category, setCategory] = useState("");
  const [accountStatus, setAccountStatus] = useState("demo");
  const [accountType, setAccountType] = useState<"competitive" | "local" | "retail">("competitive");
  const [businessName, setBusinessName] = useState("");
  const [market, setMarket] = useState("");
  const [marketScope, setMarketScope] = useState("");
  const [primaryGeoType, setPrimaryGeoType] = useState("");
  const [primaryGeoValue, setPrimaryGeoValue] = useState("");
  const [secondaryGeoValue, setSecondaryGeoValue] = useState("");
  const [geoPromptingEnabled, setGeoPromptingEnabled] = useState(false);
  const [geoNotes, setGeoNotes] = useState("");
  const [accessPresetKey, setAccessPresetKey] = useState<AccountAccessPresetKey>("viewer");
  const [batchUsers, setBatchUsers] = useState("");
  const [modelProfileKey, setModelProfileKey] = useState("demo_competitive");
  const [isRunningAnalysis, setIsRunningAnalysis] = useState(false);
  const [runControlStatus, setRunControlStatus] = useState("");
  const [copySetupSummaryStatus, setCopySetupSummaryStatus] = useState("");
  const [copyPromptGenerationBriefStatus, setCopyPromptGenerationBriefStatus] = useState("");
  const [generatedPromptPackDraft, setGeneratedPromptPackDraft] = useState<GeneratedPromptPackDraft | null>(null);
  const [promptGenerationBrief, setPromptGenerationBrief] = useState<any | null>(null);
  const [isGeneratingPromptPackDraft, setIsGeneratingPromptPackDraft] = useState(false);
  const [isSavingGeneratedPromptPackDraft, setIsSavingGeneratedPromptPackDraft] = useState(false);
  const [isPromptPackReviewOpen, setIsPromptPackReviewOpen] = useState(false);
  const [isRefreshingLatestPromptPackReview, setIsRefreshingLatestPromptPackReview] = useState(false);
  const [generatedPromptPackReviewSaveStatus, setGeneratedPromptPackReviewSaveStatus] = useState("");
  const [promptPackReviewIntegrityFailure, setPromptPackReviewIntegrityFailure] = useState<PromptPackReviewIntegrityFailure | null>(null);
  const [savedGeneratedPromptPack, setSavedGeneratedPromptPack] = useState<any | null>(null);
  const [promptPackDraftStatus, setPromptPackDraftStatus] = useState("");
  const [promptGenerationJob, setPromptGenerationJob] = useState<BackgroundJobExecution | null>(null);
  const [promptGenerationDraft, setPromptGenerationDraft] = useState<PromptPackDraftLifecycle | null>(null);
  const [promptGenerationFailure, setPromptGenerationFailure] = useState<StructuredPromptGenerationFailure | null>(null);
  const [promptGenerationTransportWarning, setPromptGenerationTransportWarning] = useState("");
  const [isRecoveringPromptGeneration, setIsRecoveringPromptGeneration] = useState(false);
  const promptGenerationRequestPendingRef = useRef(false);
  const promptGenerationPollTimerRef = useRef<number | null>(null);
  const promptGenerationAbortRef = useRef<AbortController | null>(null);
  const promptGenerationScopeRef = useRef(0);
  const promptGenerationAccountRef = useRef("");
  const [recentRuns, setRecentRuns] = useState<any[]>([]);
  const [isLoadingRecentRuns, setIsLoadingRecentRuns] = useState(false);
  const [intelligenceReadiness, setIntelligenceReadiness] = useState<any | null>(null);
  const [isLoadingIntelligenceReadiness, setIsLoadingIntelligenceReadiness] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [discoveryRefreshVersion, setDiscoveryRefreshVersion] = useState(0);
  const [accountOptions, setAccountOptions] = useState<AccountOption[]>([]);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
  const [accountListStatus, setAccountListStatus] = useState("");
  const [saveStatus, setSaveStatus] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [promptPacks, setPromptPacks] = useState<PromptPack[]>([]);
  const [promptPackStackIds, setPromptPackStackIds] = useState<string[]>([""]);
  const [isLoadingPromptPacks, setIsLoadingPromptPacks] = useState(false);
  const [isApplyingPromptPack, setIsApplyingPromptPack] = useState(false);
  const [promptPackStatus, setPromptPackStatus] = useState("");
  const [promptPackAudit, setPromptPackAudit] = useState<any | null>(null);
  const [promptPackPreview, setPromptPackPreview] = useState<any | null>(null);
  const [isLoadingPromptPackPreview, setIsLoadingPromptPackPreview] = useState(false);
  const [accountPromptSummary, setAccountPromptSummary] = useState<any | null>(null);
  const [isAuditingPromptPack, setIsAuditingPromptPack] = useState(false);
  const [isPreviewingLocalizedPromptPack, setIsPreviewingLocalizedPromptPack] = useState(false);
  const [isApplyingLocalizedPromptPack, setIsApplyingLocalizedPromptPack] = useState(false);

  const [brandSeedLoading, setBrandSeedLoading] = useState(false);
  const [brandSeedResult, setBrandSeedResult] = useState<any | null>(null);
  const [brandSeedError, setBrandSeedError] = useState<string | null>(null);
  const [brandSeedReviewLoading, setBrandSeedReviewLoading] = useState(false);
  const [brandSeedReview, setBrandSeedReview] = useState<any | null>(null);
  const [brandSeedReviewError, setBrandSeedReviewError] = useState<string | null>(null);
  const [brandSeedItemUpdatingId, setBrandSeedItemUpdatingId] = useState<string | null>(null);
  const [brandSeedBulkUpdatingType, setBrandSeedBulkUpdatingType] = useState<string | null>(null);
  const [brandSeedApplyLoading, setBrandSeedApplyLoading] = useState(false);
  const [brandSeedApplyMessage, setBrandSeedApplyMessage] = useState("");
  const [brandSeedApplyError, setBrandSeedApplyError] = useState("");
  const [brandSeedItemUpdateError, setBrandSeedItemUpdateError] = useState<string | null>(null);

  promptGenerationAccountRef.current = selectedAccountId;

  const selectedPreset = useMemo(
    () => getAccountAccessPreset(accessPresetKey),
    [accessPresetKey]
  );

  const selectedModelProfile = useMemo(
    () => modelProfiles.find((profile) => profile.key === modelProfileKey) || modelProfiles[0],
    [modelProfileKey]
  );

  const selectedPromptPackStack = useMemo(
    () =>
      promptPackStackIds
        .map((packId) => promptPacks.find((pack) => pack.id === packId) || null)
        .filter((pack): pack is PromptPack => Boolean(pack)),
    [promptPacks, promptPackStackIds]
  );

  const promptGenerationViewState = useMemo(
    () => derivePromptGenerationViewState(promptGenerationJob, promptGenerationDraft),
    [promptGenerationJob, promptGenerationDraft]
  );
  const generatedPromptPackReviewSummary = useMemo(
    () =>
      generatedPromptPackDraft?.draft_prompt_pack
        ? buildPromptPackReviewSummary(generatedPromptPackDraft.draft_prompt_pack)
        : null,
    [generatedPromptPackDraft]
  );
  const activePromptSetupSummary = useMemo(
    () => buildActivePromptSetupSummary(accountPromptSummary),
    [accountPromptSummary]
  );
  const generatedPromptPackDisplay = useMemo(() => {
    const draftPromptPack = generatedPromptPackDraft?.draft_prompt_pack || null;

    if (!draftPromptPack) {
      return null;
    }

    const draftPromptCount =
      toFiniteNumber(draftPromptPack.prompt_count) ??
      (Array.isArray(draftPromptPack.prompts) ? draftPromptPack.prompts.length : null) ??
      0;

    const savedPackId = firstNonBlankString(
      savedGeneratedPromptPack?.id,
      savedGeneratedPromptPack?.pack_id,
      savedGeneratedPromptPack?.prompt_pack_id
    );
    const savedPackLabel = firstNonBlankString(
      savedGeneratedPromptPack?.label,
      savedGeneratedPromptPack?.label_text,
      savedGeneratedPromptPack?.name
    );
    const draftPackLabel = firstNonBlankString(draftPromptPack.label, draftPromptPack.pack_key);
    const referencePackLabel = savedPackLabel || draftPackLabel;
    const activePackId = activePromptSetupSummary?.activePackId;
    const activePackLabel = activePromptSetupSummary?.activePackLabel;
    const activePromptCount = activePromptSetupSummary?.activePromptCount ?? null;
    const lastSavedPackId = firstNonBlankString(
      draftPromptPack.last_saved_pack_id
    );
    const normalizedReferenceLabel = normalizePromptPackKey(referencePackLabel || "");
    const normalizedActiveLabel = normalizePromptPackKey(activePackLabel || "");
    const activePromptPackMatchesSavedPack =
      Boolean(referencePackLabel) &&
      (
        Boolean(savedPackId && activePackId && savedPackId === activePackId) ||
        Boolean(lastSavedPackId && activePackId && lastSavedPackId === activePackId) ||
        Boolean(
          normalizedReferenceLabel &&
          normalizedActiveLabel &&
          (
            normalizedReferenceLabel === normalizedActiveLabel ||
            normalizedActiveLabel.startsWith(normalizedReferenceLabel)
          )
        )
      );
    const activePromptSetupMatchesDraft =
      activePromptCount !== null &&
      activePromptCount > 0 &&
      (
        activePromptPackMatchesSavedPack ||
        (savedGeneratedPromptPack?.applied_to_account === true && activePromptCount === draftPromptCount)
      );
    const isApplied =
      activePromptSetupMatchesDraft ||
      Boolean(savedGeneratedPromptPack?.applied_to_account === true && activePromptCount === draftPromptCount);
    const displayCount = isApplied ? (activePromptCount ?? draftPromptCount) : draftPromptCount;
    const displayLabel = isApplied
      ? activePackLabel || savedPackLabel || draftPromptPack.label
      : draftPromptPack.label;

    return {
      isApplied,
      displayCount,
      displayLabel,
      header: isApplied ? "Active Applied Prompt Pack" : "Draft Prompt Pack Preview · Review Ready",
      subtext: isApplied
        ? `${displayLabel} · ${displayCount} prompts · Applied`
        : `${draftPromptPack.label} · ${displayCount} prompts · Not saved`,
      badge: isApplied ? "Active" : "Draft only",
      supportingText: isApplied
        ? "This account-specific prompt pack is active for the account."
        : "Full review happens in the dialog before saving.",
      reviewButtonLabel: "Review Latest Draft"
    };
  }, [activePromptSetupSummary, generatedPromptPackDraft, savedGeneratedPromptPack]);
  const recoveredPreviousDraftNotice = useMemo(() => {
    return buildRecoveredPreviousDraftNotice({
      failure: promptGenerationFailure,
      job: promptGenerationJob,
      draft: promptGenerationDraft,
      hasReviewReadyDraft: Boolean(generatedPromptPackDraft?.draft_prompt_pack)
    });
  }, [generatedPromptPackDraft, promptGenerationDraft, promptGenerationFailure, promptGenerationJob]);
  const promptGenerationIsActive =
    promptGenerationViewState === "queued" ||
    promptGenerationViewState === "generating" ||
    isRecoveringPromptGeneration;
  const canSaveGeneratedPromptPackDraft =
    promptGenerationViewState === "review_ready" &&
    Boolean(generatedPromptPackDraft?.draft_prompt_pack) &&
    !promptPackReviewIntegrityFailure;

  function isActiveRunStatus(status: string | null | undefined) {
    const value = String(status || "").toLowerCase();
    return (
      value.includes("started") ||
      value.includes("running") ||
      value.includes("queued") ||
      value.includes("processing") ||
      value.includes("in_progress")
    );
  }

  const projectedPromptStackCount = useMemo(
    () =>
      selectedPromptPackStack.reduce(
        (total, pack) => total + (pack.actual_prompt_count || pack.expected_prompt_count || 0),
        0
      ),
    [selectedPromptPackStack]
  );

  const selectedPromptPackSafetyResults = useMemo(
    () =>
      selectedPromptPackStack.map((pack) => ({
        pack,
        safety: getPromptPackSafety({
          accountName,
          businessName,
          displayName: accountOptions.find((account) => account.account_id === selectedAccountId)?.display_name,
          packKey: pack.pack_key,
          packLabel: pack.label,
          sourceAccountName: pack.source_account_name,
          categoryKey: pack.category_key,
          productType: pack.product_type,
        }),
      })),
    [selectedPromptPackStack, accountName, businessName, accountOptions, selectedAccountId]
  );

  const blockedPromptPackSafetyResults = useMemo(
    () => selectedPromptPackSafetyResults.filter((result) => result.safety.status === "blocked"),
    [selectedPromptPackSafetyResults]
  );

  const hasBlockedPromptPackStack = blockedPromptPackSafetyResults.length > 0;

  const promptPackOptions = useMemo<PromptPackOption[]>(() => {
    const accountTypeKey = normalizePromptPackKey(accountType);
    const categoryKey = normalizePromptPackKey(category);
    const businessKey = normalizePromptPackKey(businessName || accountName);

    return promptPacks
      .map((pack) => {
        const packProductKey = normalizePromptPackKey(pack.product_type);
        const packCategoryKey = normalizePromptPackKey(pack.category_key || pack.category_label || "");
        const haystack = promptPackTextHaystack(pack);

        const isAccountSpecific = Boolean(
          selectedAccountId &&
          pack.source_account_id &&
          pack.source_account_id === selectedAccountId
        );

        const productMatch = Boolean(accountTypeKey && packProductKey === accountTypeKey);
        const categoryExactMatch = Boolean(categoryKey && packCategoryKey === categoryKey);
        const categoryTextMatch = Boolean(categoryKey && haystack.includes(categoryKey.replace(/_/g, " ")));
        const businessMatch = Boolean(businessKey && haystack.includes(businessKey.replace(/_/g, " ")));

        let fitScore = 0;
        if (isAccountSpecific) fitScore += 100;
        if (productMatch) fitScore += 50;
        if (categoryExactMatch) fitScore += 35;
        if (categoryTextMatch) fitScore += 20;
        if (businessMatch) fitScore += 15;
        if (accountTypeKey && packProductKey && packProductKey !== accountTypeKey) fitScore -= 40;

        let fitLabel = "Catalog";
        let fitWarning: string | null = null;

        if (isAccountSpecific) {
          fitLabel = "Account custom";
        } else if (productMatch && (categoryExactMatch || categoryTextMatch)) {
          fitLabel = "Recommended";
        } else if (productMatch) {
          fitLabel = "Same product";
        } else if (accountTypeKey && packProductKey && packProductKey !== accountTypeKey) {
          fitLabel = "Mismatch";
          fitWarning = `This pack is ${pack.product_type}, but the selected account is ${accountType}.`;
        }

        return {
          ...pack,
          fit_score: fitScore,
          fit_label: fitLabel,
          fit_warning: fitWarning,
        };
      })
      .sort((a, b) => {
        if (b.fit_score !== a.fit_score) return b.fit_score - a.fit_score;
        if ((a.product_type || "") !== (b.product_type || "")) {
          return String(a.product_type || "").localeCompare(String(b.product_type || ""));
        }
        return String(a.label || "").localeCompare(String(b.label || ""));
      });
  }, [promptPacks, accountType, category, businessName, accountName, selectedAccountId]);

  const hasActiveRecentRun = useMemo(
    () => recentRuns.some((run: any) => isActiveRunStatus(run.status)),
    [recentRuns]
  );

  const latestActiveRun = useMemo(
    () => recentRuns.find((run: any) => isActiveRunStatus(run.status)) || null,
    [recentRuns]
  );

  const intelligenceReadinessSummary = useMemo(() => {
    const marketProfileApproved =
      String(intelligenceReadiness?.marketProfile?.status || "").toLowerCase() === "approved";

    if (isLoadingIntelligenceReadiness) {
      return {
        status: "Refreshing intelligence",
        passing: 0,
        total: 6,
        tone: "loading"
      };
    }

    if (intelligenceReadiness?.error) {
      return {
        status: "Unavailable",
        passing: 0,
        total: 6,
        tone: "error"
      };
    }

    const checks = [
      marketProfileApproved,
      Boolean(intelligenceReadiness?.brandReview),
      Number(intelligenceReadiness?.entityRoleCount || 0) > 0,
      Number(intelligenceReadiness?.directCompetitorCount || 0) > 0,
      Number(intelligenceReadiness?.suppressionCount || 0) > 0,
      Number(intelligenceReadiness?.customerEntityCount || 0) > 0
    ];

    const passing = checks.filter(Boolean).length;
    const total = checks.length;

    if (passing >= 5) {
      return {
        status: "Strong intelligence",
        passing,
        total,
        tone: "strong"
      };
    }

    if (passing >= 3) {
      return {
        status: "Partial intelligence",
        passing,
        total,
        tone: "partial"
      };
    }

    if (passing >= 1) {
      return {
        status: "Light intelligence",
        passing,
        total,
        tone: "light"
      };
    }

    return {
      status: "Not started",
      passing,
      total,
      tone: "empty"
    };
  }, [intelligenceReadiness, isLoadingIntelligenceReadiness]);

  const parsedUsers = useMemo(() => {
    return batchUsers
      .split(/\n|,/)
      .map((email) => email.trim())
      .filter(Boolean);
  }, [batchUsers]);

  const websiteUrl = useMemo(() => normalizeWebsiteUrl(website), [website]);
  const websiteDomain = useMemo(() => extractDomain(website), [website]);

  const onboardingReadiness = useMemo(() => {
    const checks = [
      {
        key: "account",
        label: "Account saved",
        ok: Boolean(selectedAccountId),
        help: selectedAccountId ? "Account has a saved ID." : "Save or select an account."
      },
      {
        key: "website",
        label: "Website/domain present",
        ok: Boolean(websiteDomain || websiteUrl),
        help: websiteDomain || websiteUrl ? "Website context is available." : "Add a website before launch."
      },
      {
        key: "category",
        label: "Category present",
        ok: Boolean(category.trim()),
        help: category.trim() ? "Category is set." : "Add the account category."
      },
      {
        key: "geo",
        label: "Geo ready",
        ok: accountType !== "local" || Boolean(primaryGeoValue.trim() || market.trim()),
        help:
          accountType !== "local"
            ? "Geo fields are optional for non-local accounts."
            : primaryGeoValue.trim() || market.trim()
              ? "Local geo context is set."
              : "Add market or primary geo for local prompting."
      },
      {
        key: "prompts",
        label: "Active prompts",
        ok: Boolean(activePromptSetupSummary?.activePromptCount),
        help: activePromptSetupSummary?.activePromptCount
          ? `${activePromptSetupSummary.activePromptCount} active prompts are assigned.`
          : "Apply a prompt pack before running."
      },
      {
        key: "run",
        label: "Run slot available",
        ok: !hasActiveRecentRun,
        help: hasActiveRecentRun
          ? "An active run is already in progress."
          : "No active run is blocking launch."
      }
    ];

    const failed = checks.filter((check) => !check.ok);

    let status = "Ready to run";
    let tone: "ready" | "warning" | "blocked" = "ready";

    if (hasActiveRecentRun) {
      status = "Active run in progress";
      tone = "blocked";
    } else if (failed.some((check) => check.key === "prompts")) {
      status = "Needs prompts";
      tone = "warning";
    } else if (failed.some((check) => check.key === "geo")) {
      status = "Needs geo";
      tone = "warning";
    } else if (failed.length > 0) {
      status = "Needs setup";
      tone = "warning";
    }

    return {
      checks,
      failed,
      status,
      tone,
      isReady: failed.length === 0
    };
  }, [
    selectedAccountId,
    websiteDomain,
    websiteUrl,
    category,
    accountType,
    primaryGeoValue,
    market,
    activePromptSetupSummary?.activePromptCount,
    hasActiveRecentRun
  ]);

  useEffect(() => {
    let isMounted = true;

    async function checkAdminAccess() {
      if (!session?.user?.id) {
        if (isMounted) {
          setHasAdminAccess(false);
          setIsCheckingAdmin(false);
        }
        return;
      }

      const { data, error } = await supabase
        .from("account_users")
        .select("id, role, is_active")
        .eq("user_id", session.user.id)
        .eq("is_active", true)
        .in("role", ["super_admin", "superadmin", "admin"])
        .limit(1);

      if (error) {
        console.error("Failed to check admin onboarding access:", error);
      }

      if (isMounted) {
        setHasAdminAccess(Boolean(data && data.length > 0));
        setIsCheckingAdmin(false);
      }
    }

    checkAdminAccess();

return () => {
      isMounted = false;
    };
  }, [session?.user?.id]);

  useEffect(() => {
    let isMounted = true;

    async function loadAccounts() {
      if (!hasAdminAccess || !session?.access_token) {
        return;
      }

      setIsLoadingAccounts(true);
      setAccountListStatus("");

      async function loadAccountsFromSupabaseFallback(reason: string) {
        console.warn("Loading onboarding accounts directly from Supabase fallback:", reason);

        const { data: fallbackAccounts, error: fallbackError } = await supabase
          .from("accounts")
          .select(`
            account_id,
            account_name,
            display_name,
            business_name,
            website_url,
            website_domain,
            status,
            category,
            category_type,
            account_type,
            market,
            market_scope,
            geo_prompting_enabled,
            primary_geo_type,
            primary_geo_value,
            secondary_geo_value,
            geo_notes,
            local_config,
            onboarding_status,
            onboarding_notes,
            run_status,
            last_run_status,
            created_at
          `)
          .order("created_at", { ascending: false });

        if (fallbackError) {
          throw fallbackError;
        }

        if (isMounted) {
          setAccountOptions(fallbackAccounts || []);
          setAccountListStatus(`Loaded ${fallbackAccounts?.length || 0} accounts directly from Supabase fallback.`);
        }
      }

      try {
        const res = await fetch("/api/admin-onboarding-accounts", {
          method: "GET",
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        });

        const rawText = await res.text();
        let data: any = {};

        try {
          data = rawText ? JSON.parse(rawText) : {};
        } catch {
          data = {
            error: rawText.slice(0, 500) || "Account list returned a non-JSON response",
          };
        }

        if (!res.ok) {
          throw new Error(data.error || "Failed to load accounts");
        }

        const accounts = data.accounts || [];

        if (!accounts.length) {
          await loadAccountsFromSupabaseFallback("API returned 0 accounts");
          return;
        }

        if (isMounted) {
          setAccountOptions(accounts);
        }
      } catch (error: any) {
        console.error("Failed to load onboarding accounts via API:", error);

        try {
          await loadAccountsFromSupabaseFallback(error?.message || "API failed");
        } catch (fallbackError: any) {
          console.error("Failed to load onboarding accounts via Supabase fallback", fallbackError);
          if (isMounted) {
            setAccountListStatus(fallbackError?.message || error?.message || "Failed to load accounts");
          }
        }
      } finally {
        if (isMounted) {
          setIsLoadingAccounts(false);
        }
      }
    }


    async function loadPromptPacks() {
      if (!hasAdminAccess || !session?.access_token) {
        return;
      }

      setIsLoadingPromptPacks(true);

      try {
        const promptPackUrl = selectedAccountId
        ? `/api/admin-onboarding-prompt-packs?account_id=${encodeURIComponent(selectedAccountId)}`
        : "/api/admin-onboarding-prompt-packs";

      const res = await fetch(promptPackUrl, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        });

        const rawText = await res.text();
        let data: any = {};

        try {
          data = rawText ? JSON.parse(rawText) : {};
        } catch {
          data = {
            error: rawText.slice(0, 500) || "Prompt packs returned a non-JSON response",
          };
        }

        if (!res.ok) {
          throw new Error(data?.error || "Failed to load prompt packs.");
        }

        if (isMounted) {
          const packs = data?.packs || [];
          setPromptPacks(packs);
        }
      } catch (error: any) {
        console.error("Failed to load prompt packs via API, falling back to Supabase:", error);

        try {
          const { data: packs, error: packsError } = await supabase
            .from("prompt_packs")
            .select(`
              id,
              pack_key,
              label,
              product_type,
              category_key,
              category_label,
              expected_prompt_count,
              source_account_id,
              source_account_name,
              description,
              notes,
              is_active,
              created_at,
              updated_at
            `)
            .eq("is_active", true)
            .order("product_type", { ascending: true })
            .order("label", { ascending: true });

          if (packsError) {
            throw packsError;
          }

          if (isMounted) {
            setPromptPacks((packs || []).map((pack: any) => ({
              ...pack,
              actual_prompt_count: pack.expected_prompt_count,
            })));
            setPromptPackStatus("Loaded prompt packs directly from Supabase fallback.");
          }
        } catch (fallbackError: any) {
          console.error("Failed to load prompt packs via Supabase fallback", fallbackError);
          if (isMounted) {
            setPromptPackStatus(fallbackError?.message || error?.message || "Failed to load prompt packs.");
          }
        }
      } finally {
        if (isMounted) {
          setIsLoadingPromptPacks(false);
        }
      }
    }


    loadAccounts();
    loadPromptPacks();

    return () => {
      isMounted = false;
    };
  }, [hasAdminAccess, session?.access_token, selectedAccountId]);

  useEffect(() => {
    if (!selectedAccountId) {
      setAccountPromptSummary(null);
      setRecentRuns([]);
      setIntelligenceReadiness(null);
      return;
    }

    loadAccountPromptSummary(selectedAccountId);
    loadRecentRuns(selectedAccountId);
    loadIntelligenceReadiness(selectedAccountId);
  }, [selectedAccountId]);

  useEffect(() => {
    const scope = ++promptGenerationScopeRef.current;
    stopPromptGenerationPolling();
    promptGenerationRequestPendingRef.current = false;
    setIsGeneratingPromptPackDraft(false);
    setPromptGenerationJob(null);
    setPromptGenerationDraft(null);
    setPromptGenerationFailure(null);
    setPromptGenerationTransportWarning("");
    setIsRecoveringPromptGeneration(false);
    setGeneratedPromptPackDraft(null);
    setSavedGeneratedPromptPack(null);
    setIsPromptPackReviewOpen(false);
    setGeneratedPromptPackReviewSaveStatus("");
    setPromptPackReviewIntegrityFailure(null);
    setPromptPackDraftStatus("");

    if (!selectedAccountId || !session?.access_token) {
      return () => {
        if (promptGenerationScopeRef.current === scope) promptGenerationScopeRef.current += 1;
        stopPromptGenerationPolling();
      };
    }

    let jobId = "";
    const latestDiscoveryAbortController = new AbortController();
    try {
      jobId = window.localStorage.getItem(promptGenerationStorageKey(selectedAccountId)) || "";
    } catch (error) {
      console.warn("Unable to restore prompt generation pointer:", error);
    }
    if (jobId) {
      setIsRecoveringPromptGeneration(true);
      void (async () => {
        try {
          const recovery = await recoverPromptGenerationDraftForAccount({
            accountId: selectedAccountId,
            accessToken: session.access_token,
            pointerJobId: jobId,
            signal: latestDiscoveryAbortController.signal
          });

          if (
            latestDiscoveryAbortController.signal.aborted ||
            scope !== promptGenerationScopeRef.current ||
            promptGenerationAccountRef.current !== selectedAccountId
          ) {
            return;
          }

          if (recovery.kind === "transient_failure") {
            setIsRecoveringPromptGeneration(false);
            setPromptGenerationTransportWarning(recovery.message);
            if (recovery.preservePointer) {
              void pollPromptGenerationStatus({
                jobId,
                accountId: selectedAccountId,
                scope,
                delayMs: 3000
              });
            }
            return;
          }

          if (recovery.kind === "not_found") {
            if (recovery.pointerWasStale) clearPromptGenerationPointer(selectedAccountId);
            setIsRecoveringPromptGeneration(false);
            setPromptGenerationTransportWarning(recovery.message || "");
            setPromptPackDraftStatus(
              recovery.pointerWasStale
                ? recovery.message || "No recoverable prompt generation found."
                : "No recoverable prompt generation found."
            );
            return;
          }

          if (recovery.pointerWasStale) clearPromptGenerationPointer(selectedAccountId);
          storePromptGenerationPointer(selectedAccountId, recovery.job.id);

          const viewState = applyPromptGenerationEnvelope(
            {
              ok: true,
              job: recovery.job,
              draft: recovery.draft
            },
            selectedAccountId,
            recovery.job.id,
            scope
          );

          if (viewState === "queued" || viewState === "generating") {
            void pollPromptGenerationStatus({
              jobId: recovery.job.id,
              accountId: selectedAccountId,
              scope,
              delayMs: 3000
            });
          }
        } catch (error: any) {
          if (
            latestDiscoveryAbortController.signal.aborted ||
            scope !== promptGenerationScopeRef.current ||
            promptGenerationAccountRef.current !== selectedAccountId
          ) {
            return;
          }

          setIsRecoveringPromptGeneration(false);
          setPromptGenerationTransportWarning(
            error?.message || "Prompt generation status is temporarily unavailable."
          );
        }
      })();
    } else {
      setIsRecoveringPromptGeneration(true);
      void (async () => {
        try {
          const discovery = await discoverLatestPromptGenerationDraft({
            accountId: selectedAccountId,
            accessToken: session.access_token,
            signal: latestDiscoveryAbortController.signal
          });

          if (scope !== promptGenerationScopeRef.current || promptGenerationAccountRef.current !== selectedAccountId) {
            return;
          }

          if (discovery.kind === "transient_failure") {
            setIsRecoveringPromptGeneration(false);
            setPromptGenerationTransportWarning(discovery.message);
            return;
          }

          if (discovery.kind === "not_found") {
            setIsRecoveringPromptGeneration(false);
            setPromptGenerationTransportWarning("");
            return;
          }

          const viewState = applyPromptGenerationEnvelope(
            {
              ok: true,
              job: discovery.job,
              draft: discovery.draft
            },
            selectedAccountId,
            discovery.job.id,
            scope
          );
          storePromptGenerationPointer(selectedAccountId, discovery.job.id);
          if (viewState === "queued" || viewState === "generating") {
            void pollPromptGenerationStatus({
              jobId: discovery.job.id,
              accountId: selectedAccountId,
              scope,
              delayMs: 3000
            });
          }
        } catch (error: any) {
          if (
            latestDiscoveryAbortController.signal.aborted ||
            scope !== promptGenerationScopeRef.current ||
            promptGenerationAccountRef.current !== selectedAccountId
          ) {
            return;
          }

          setIsRecoveringPromptGeneration(false);
          setPromptGenerationTransportWarning(
            error?.message || "Latest prompt draft discovery is temporarily unavailable."
          );
        }
      })();
    }

    return () => {
      latestDiscoveryAbortController.abort();
      if (promptGenerationScopeRef.current === scope) promptGenerationScopeRef.current += 1;
      stopPromptGenerationPolling();
    };
    // Polling functions deliberately use refs to invalidate stale account responses.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccountId, session?.access_token]);

  useEffect(() => {
    if (!selectedAccountId || recentRuns.length === 0) {
      return;
    }

    const latestRun = recentRuns[0];

    if (!isActiveRunStatus(latestRun?.status)) {
      return;
    }

    const timer = window.setTimeout(() => {
      loadRecentRuns(selectedAccountId);
    }, 15000);

    return () => window.clearTimeout(timer);
  }, [selectedAccountId, recentRuns]);

  function handleSelectExistingAccount(accountId: string) {
    setSelectedAccountId(accountId);
    setPromptPackStackIds([""]);
    setPromptPackAudit(null);
    setPromptPackPreview(null);
    setGeneratedPromptPackDraft(null);
    setPromptGenerationBrief(null);
    setSavedGeneratedPromptPack(null);
    setPromptPackDraftStatus("");
    setPromptPackStatus(accountId ? "Loading account-specific prompt packs..." : "");
    setRunControlStatus("");
    setPromptPackReviewIntegrityFailure(null);

    if (!accountId) {
      setAccountName("");
      setWebsite("");
      setCategory("");
      setAccountStatus("demo");
      setAccountType("competitive");
      setBusinessName("");
      setMarket("");
      setMarketScope("");
      setPrimaryGeoType("");
      setPrimaryGeoValue("");
      setSecondaryGeoValue("");
      setGeoPromptingEnabled(false);
      setGeoNotes("");
      setSaveStatus("");
      setPromptPackStatus("");
      setRecentRuns([]);
      setIntelligenceReadiness(null);
      setPromptPackReviewIntegrityFailure(null);
      return;
    }

    const selected = accountOptions.find((account) => account.account_id === accountId);

    if (!selected) {
      setSaveStatus("Selected account was not found in the loaded account list.");
      return;
    }

    setAccountName(selected.account_name || "");
    const selectedAny = selected as any;

    const rawWebsite =
      selectedAny.website_url ||
      selectedAny.website ||
      selectedAny.url ||
      selectedAny.site_url ||
      selectedAny.domain ||
      selectedAny.website_domain ||
      "";

    const hydratedWebsite =
      rawWebsite && !String(rawWebsite).startsWith("http")
        ? `https://${rawWebsite}`
        : String(rawWebsite || "");

    setWebsite(hydratedWebsite);

    if (!hydratedWebsite) {
      setAccountListStatus("No website saved for this account yet.");
    }
    setCategory(selected.category || selected.category_type || "");
    setAccountStatus(selected.status || "demo");
    setAccountType((selected.account_type as "competitive" | "local" | "retail") || "competitive");
    setBusinessName(selected.business_name || selected.account_name || "");
    setMarket(selected.market || "");
    setMarketScope(selected.market_scope || "");
    setPrimaryGeoType(selected.primary_geo_type || "");
    setPrimaryGeoValue(selected.primary_geo_value || "");
    setSecondaryGeoValue(selected.secondary_geo_value || "");
    setGeoPromptingEnabled(Boolean(selected.geo_prompting_enabled));
    setGeoNotes(selected.geo_notes || "");
    setSaveStatus(`Loaded ${selected.account_name}`);
    setPromptPackStatus("");
  }

  function handleStartNewAccount() {
    setSelectedAccountId("");
    setAccountName("");
    setWebsite("");
    setCategory("");
    setAccountStatus("demo");
    setAccountType("competitive");
    setBusinessName("");
    setMarket("");
    setMarketScope("");
    setPrimaryGeoType("");
    setPrimaryGeoValue("");
    setSecondaryGeoValue("");
    setGeoPromptingEnabled(false);
    setGeoNotes("");
    setSaveStatus("Ready to create a new account.");
    setPromptPackStatus("");
    setPromptPackReviewIntegrityFailure(null);
    setRecentRuns([]);
    setIntelligenceReadiness(null);
  }

  async function refreshAccountOptions() {
    if (!session?.access_token) {
      setAccountListStatus("Missing session token.");
      return;
    }

    setAccountListStatus("Refreshing account list...");

    try {
      setIsLoadingAccounts(true);

      const res = await fetch("/api/admin-onboarding-accounts", {
        method: "GET",
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      const rawText = await res.text();
      let data: any = {};

      try {
        data = rawText ? JSON.parse(rawText) : {};
      } catch {
        data = {
          error: rawText.slice(0, 500) || "Account list returned a non-JSON response",
        };
      }

      if (!res.ok) {
        throw new Error(data.error || "Failed to refresh accounts");
      }

      setAccountOptions(data.accounts || []);
      setAccountListStatus("Account list refreshed.");
    } catch (error: any) {
      setAccountListStatus(error.message || "Failed to refresh accounts");
    } finally {
      setIsLoadingAccounts(false);
    }
  }

  async function handleSaveOnboardingProfile() {
    if (!accountName.trim()) {
      alert("Account name is required.");
      return;
    }

    setIsSaving(true);
    setSaveStatus("");

    try {
      let existingLocalConfig: Record<string, unknown> = {};
      if (selectedAccountId) {
        // Preserve unrelated local_config keys while building the legacy profile
        // payload. This read is not a concurrency guard: the Runner PATCH must
        // preserve current_asset_intelligence atomically from the row it updates.
        const latestRes = await fetch("/api/admin-onboarding-accounts", {
          method: "GET",
          headers: { Authorization: `Bearer ${session?.access_token || ""}` },
        });
        const latestData = await latestRes.json().catch(() => ({}));
        const latestAccount = latestData.accounts?.find((account: AccountOption) => account.account_id === selectedAccountId);
        if (!latestRes.ok || !latestAccount) throw new Error("Could not load the latest account configuration. No account changes were saved.");
        existingLocalConfig = latestAccount.local_config || {};
      }
      const localConfig = mergeOnboardingLocalConfig(existingLocalConfig, accountType, {
        business_name: businessName.trim() || accountName.trim(),
        market: market.trim() || null,
        market_scope: marketScope || null,
        primary_geo_type: primaryGeoType || null,
        primary_geo_value: primaryGeoValue.trim() || null,
        secondary_geo_value: secondaryGeoValue.trim() || null,
        geo_notes: geoNotes.trim() || null,
      });
      const payload = {
        account_id: selectedAccountId || null,
        account_name: accountName.trim(),
        display_name: accountName.trim(),
        business_name: businessName.trim() || accountName.trim(),
        website_url: normalizeWebsiteUrl(websiteUrl) || null,
        website_domain: websiteDomain || null,
        status: accountStatus,
        category: category.trim() || null,
        category_type: category.trim() || null,
        account_type: accountType,
        market: market.trim() || null,
        market_scope: marketScope || null,
        geo_prompting_enabled: accountType === "local" ? geoPromptingEnabled : false,
        primary_geo_type: accountType === "local" ? primaryGeoType || null : null,
        primary_geo_value: accountType === "local" ? primaryGeoValue.trim() || null : null,
        secondary_geo_value: accountType === "local" ? secondaryGeoValue.trim() || null : null,
        geo_notes: geoNotes.trim() || null,
        local_config: localConfig,
        is_active: accountStatus !== "inactive",
        ...(!selectedAccountId ? { onboarding_status: "draft" } : {}),
      };

      const res = await fetch("/api/admin-onboarding-account", {
        method: selectedAccountId ? "PATCH" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token || ""}`,
        },
        body: JSON.stringify(payload),
      });

      const responseText = await res.text();

      let data: any = null;
      try {
        data = responseText ? JSON.parse(responseText) : null;
      } catch {
        data = responseText;
      }

      if (!res.ok) {
        console.error("Failed to save onboarding profile:", {
          status: res.status,
          data,
        });

        alert(data?.error || `Failed to save onboarding profile. Status: ${res.status}`);
        return;
      }

      const savedAccountId = data?.account?.account_id;

      if (savedAccountId) {
        setSelectedAccountId(savedAccountId);
        if (data.account) setAccountOptions((current) => current.map((account) => account.account_id === savedAccountId ? { ...account, ...data.account } : account));
      }

      setSaveStatus(
        selectedAccountId
          ? "Account onboarding profile updated."
          : "Account onboarding profile created."
      );
    } catch (error: any) {
      console.error("Failed to save onboarding profile:", error);
      alert(error?.message || "Failed to save onboarding profile.");
    } finally {
      setIsSaving(false);
    }
  }

  function handlePromptPackStackChange(index: number, packId: string) {
    setPromptPackStackIds((current) =>
      current.map((currentPackId, currentIndex) =>
        currentIndex === index ? packId : currentPackId
      )
    );
    setPromptPackStatus("");
    setPromptPackAudit(null);
    setPromptPackPreview(null);
  }

  function handleAddPromptPackStackRow() {
    setPromptPackStackIds((current) => [...current, ""]);
    setPromptPackStatus("");
    setPromptPackAudit(null);
    setPromptPackPreview(null);
  }

  function handleRemovePromptPackStackRow(index: number) {
    setPromptPackStackIds((current) => {
      const next = current.filter((_, currentIndex) => currentIndex !== index);
      return next.length > 0 ? next : [""];
    });
    setPromptPackStatus("");
    setPromptPackAudit(null);
    setPromptPackPreview(null);
  }

  async function loadAccountPromptSummary(accountId = selectedAccountId) {
    if (!accountId) {
      setAccountPromptSummary(null);
      return;
    }

    try {
      const res = await fetch(
        `/api/admin-onboarding-account-prompt-summary?account_id=${encodeURIComponent(accountId)}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${session?.access_token || ""}`,
          },
        }
      );

      const data = await res.json();

      if (!res.ok) {
        console.error("Failed to load account prompt summary:", data);
        setAccountPromptSummary(null);
        return;
      }

      setAccountPromptSummary(data);
    } catch (error) {
      console.error("Failed to load account prompt summary:", error);
      setAccountPromptSummary(null);
    }
  }

  async function handlePreviewPromptPack() {
    const selectedPackIds = promptPackStackIds.filter(Boolean);
    const uniqueSelectedPackIds = Array.from(new Set(selectedPackIds));

    if (uniqueSelectedPackIds.length !== 1) {
      alert("Select exactly one prompt pack to preview.");
      return;
    }

    setIsLoadingPromptPackPreview(true);
    setPromptPackStatus("");
    setPromptPackPreview(null);

    try {
      const res = await fetch(
        `/api/admin-onboarding-prompt-pack-preview?pack_id=${encodeURIComponent(uniqueSelectedPackIds[0])}&account_id=${encodeURIComponent(selectedAccountId)}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${session?.access_token || ""}`,
          },
        }
      );

      const responseText = await res.text();
      let data: any = {};

      try {
        data = responseText ? JSON.parse(responseText) : {};
      } catch {
        data = {
          error: responseText.slice(0, 500) || "Prompt pack preview returned a non-JSON response",
        };
      }

      if (!res.ok) {
        console.error("Failed to load prompt pack preview:", data);
        alert(data?.error || "Failed to load prompt pack preview.");
        setPromptPackStatus(data?.error || "Failed to load prompt pack preview.");
        return;
      }

      setPromptPackPreview(data);
      setPromptPackStatus(`Preview loaded: ${data.prompt_count || 0} active prompts.`);
    } catch (error: any) {
      console.error("Failed to load prompt pack preview:", error);
      alert(error?.message || "Failed to load prompt pack preview.");
      setPromptPackStatus(error?.message || "Failed to load prompt pack preview.");
    } finally {
      setIsLoadingPromptPackPreview(false);
    }
  }

  async function handleCopyAccountSetupSummary() {
    const latestRun = recentRuns[0] || null;

    const lines = [
      "Toren Admin Onboarding Summary",
      "",
      `Account: ${accountName || businessName || "Unnamed account"}`,
      `Account ID: ${selectedAccountId || "Not saved"}`,
      `Type: ${accountType || "Not set"}`,
      `Category: ${category || "Not set"}`,
      `Website: ${websiteUrl || websiteDomain || "Not set"}`,
      `Market: ${market || "Not set"}`,
      `Primary geo: ${primaryGeoValue || "Not set"}`,
      "",
      "Core Readiness",
      `Status: ${onboardingReadiness.status}`,
      `Checks: ${onboardingReadiness.checks.filter((check: any) => check.ok).length}/${onboardingReadiness.checks.length}`,
      ...onboardingReadiness.checks.map((check: any) => `- ${check.ok ? "PASS" : "MISSING"}: ${check.label} - ${check.help}`),
      "",
      "Competitive Setup Intelligence",
      `Strength: ${intelligenceReadinessSummary.status} (${intelligenceReadinessSummary.passing}/${intelligenceReadinessSummary.total})`,
      `Market profile: ${intelligenceReadiness?.marketProfile ? "Present" : "Missing"}`,
      `Brand guidance: ${intelligenceReadiness?.brandReview ? "Present" : "Missing"}`,
      `Entity roles: ${intelligenceReadiness?.entityRoleCount || 0}`,
      `Direct competitors: ${intelligenceReadiness?.directCompetitorCount || 0}`,
      `Suppressions: ${intelligenceReadiness?.suppressionCount || 0}`,
      `Customer/self mappings: ${intelligenceReadiness?.customerEntityCount || 0}`,
      "",
      "Prompt Setup",
      `Active prompts: ${activePromptSetupSummary?.activePromptCount || 0}`,
      `Selected prompt packs: ${selectedPromptPackStack.map((pack: any) => pack.label || pack.pack_key).join(", ") || "None selected"}`,
      "",
      "Latest Run",
      latestRun
        ? `Run ID: ${latestRun.run_id}`
        : "Run ID: None found",
      latestRun
        ? `Status: ${latestRun.status || "unknown"}`
        : "Status: None found",
      latestRun
        ? `Run label: ${latestRun.run_label || "Not set"}`
        : "Run label: None found",
      latestRun
        ? `Created: ${latestRun.created_at || "Not set"}`
        : "Created: None found",
    ];

    const summary = lines.join("\\n");

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(summary);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = summary;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }

      setCopySetupSummaryStatus("Setup summary copied.");
      window.setTimeout(() => setCopySetupSummaryStatus(""), 2500);
    } catch (error: any) {
      console.error("Failed to copy setup summary:", error);
      setCopySetupSummaryStatus(error?.message || "Failed to copy setup summary.");
    }
  }

  function buildPromptGenerationBrief() {
    const selectedPackLabels = selectedPromptPackStack
      .map((pack: any) => pack.label || pack.pack_key)
      .filter(Boolean);

    const marketProfile = intelligenceReadiness?.marketProfile || null;
    const brandReview = intelligenceReadiness?.brandReview || null;

    return {
      purpose: "Generate an account-specific prompt pack for Toren competitive or local AI visibility analysis.",
      generation_mode: "draft_review_first",
      account: {
        account_id: selectedAccountId || null,
        account_name: accountName || businessName || null,
        business_name: businessName || null,
        account_type: accountType || null,
        category: category || null,
        market: market || null,
        website: websiteUrl || websiteDomain || null,
        website_domain: websiteDomain || null
      },
      geography: {
        primary_geo_type: primaryGeoType || null,
        primary_geo_value: primaryGeoValue || null,
        secondary_geo_values: secondaryGeoValue || null
      },
      competitive_context: {
        intelligence_strength: intelligenceReadinessSummary.status,
        intelligence_score: `${intelligenceReadinessSummary.passing}/${intelligenceReadinessSummary.total}`,
        market_profile_status: marketProfile?.status || null,
        market_profile_source: marketProfile?.source || null,
        market_profile_confidence_score: marketProfile?.confidence_score || null,
        entity_role_count: intelligenceReadiness?.entityRoleCount || 0,
        direct_competitor_count: intelligenceReadiness?.directCompetitorCount || 0,
        suppression_count: intelligenceReadiness?.suppressionCount || 0,
        customer_entity_count: intelligenceReadiness?.customerEntityCount || 0,
        brand_review_status: brandReview?.review_status || null,
        brand_review_seed_count: brandReview?.seed_count || 0,
        brand_review_suppression_count: brandReview?.suppression_count || 0
      },
        prompt_pack_inputs: {
          selected_source_packs: selectedPackLabels,
        active_prompt_count: activePromptSetupSummary?.activePromptCount || 0,
        recommended_output: "Create a reviewable account-specific prompt pack before applying it.",
        desired_prompt_count: getDesiredPromptCount(category),
        prompt_mix_guidance:
          accountType === "local"
            ? [
                "Local discovery prompts",
                "Service/category prompts",
                "Comparison prompts",
                "Trust and reputation prompts",
                "Location/service-area prompts",
                "Next-step buyer prompts"
              ]
            : [
                "Category discovery prompts",
                "Alternatives/replacement prompts",
                "Comparison prompts",
                "Use-case fit prompts",
                "Pain-point-led prompts",
                "Purchase shortlist prompts"
              ]
      },
      guardrails: [
        "Do not invent competitors, service areas, product claims, awards, prices, or customer proof.",
        "Use market profile and entity governance when available.",
        "Respect suppressed entities and non-competitor classifications.",
        "Generate prompts customers or buyers would realistically ask AI systems.",
        "Prefer prompts that reveal recommendations, comparisons, trust, fit, and purchase intent.",
        "Return draft prompts for review before saving or applying."
      ]
    };
  }

  async function handleCopyPromptGenerationBrief() {
    const brief = buildPromptGenerationBrief();

    const text = [
      "Agent Smith Prompt Generation Brief",
      "",
      JSON.stringify(brief, null, 2)
    ].join("\n");

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }

      setCopyPromptGenerationBriefStatus("Prompt generation brief copied.");
      window.setTimeout(() => setCopyPromptGenerationBriefStatus(""), 2500);
    } catch (error: any) {
      console.error("Failed to copy prompt generation brief:", error);
      setCopyPromptGenerationBriefStatus(error?.message || "Failed to copy prompt generation brief.");
    }
  }

  function stopPromptGenerationPolling() {
    if (promptGenerationPollTimerRef.current !== null) {
      window.clearTimeout(promptGenerationPollTimerRef.current);
      promptGenerationPollTimerRef.current = null;
    }
    promptGenerationAbortRef.current?.abort();
    promptGenerationAbortRef.current = null;
  }

  function clearPromptGenerationPointer(accountId: string) {
    try {
      window.localStorage.removeItem(promptGenerationStorageKey(accountId));
    } catch (error) {
      console.warn("Unable to clear prompt generation pointer:", error);
    }
  }

  function storePromptGenerationPointer(accountId: string, jobId: string) {
    try {
      window.localStorage.setItem(promptGenerationStorageKey(accountId), jobId);
    } catch (error) {
      console.warn("Unable to persist prompt generation pointer:", error);
    }
  }

  function applyPromptGenerationEnvelope(
    envelope: PromptGenerationEnvelope,
    accountId: string,
    jobId: string,
    scope: number
  ) {
    if (scope !== promptGenerationScopeRef.current || promptGenerationAccountRef.current !== accountId) return "stale";
    if (!envelope?.job?.id || !envelope?.draft?.id || envelope.job.id !== jobId) {
      throw new Error("Prompt generation returned a malformed job response.");
    }
    if (envelope.draft.account_id !== accountId) {
      throw new Error("Prompt generation returned a draft for a different account.");
    }

    setPromptGenerationJob(envelope.job);
    setPromptGenerationDraft(envelope.draft);
    setPromptGenerationTransportWarning("");
    setIsRecoveringPromptGeneration(false);
    const viewState = derivePromptGenerationViewState(envelope.job, envelope.draft);

    if (viewState === "queued") {
      setPromptPackDraftStatus("Queued");
      setPromptPackReviewIntegrityFailure(null);
    } else if (viewState === "generating") {
      setPromptPackDraftStatus("Generating");
      setPromptPackReviewIntegrityFailure(null);
    } else if (viewState === "review_ready") {
      const projected = buildGeneratedPromptPackDraftFromStatus(envelope.draft);
      const reviewValidation = validatePromptPackReviewDraft(projected.draft_prompt_pack);
      if (!reviewValidation.ok) {
        const integrityError = formatPromptPackReviewIntegrityError(reviewValidation);
        setGeneratedPromptPackDraft(null);
        setPromptPackReviewIntegrityFailure({
          code: integrityError.code,
          message: integrityError.message,
          details: integrityError.details || []
        });
        setPromptPackDraftStatus("Review Ready");
      } else {
        setGeneratedPromptPackDraft(projected);
        setPromptPackReviewIntegrityFailure(null);
        setPromptPackDraftStatus("Review Ready");
      }
    } else if (viewState === "failed") {
      const code = envelope.job.error_code || envelope.draft.error_code || "prompt_generation_failed";
      const message = envelope.job.error_message || envelope.draft.error_message || "Prompt generation failed.";
      setPromptGenerationFailure({
        code,
        message,
        failedAt: envelope.job.failed_at || envelope.draft.failed_at || null
      });
      setPromptPackDraftStatus("Failed");
    } else {
      throw new Error("Prompt generation returned an unexpected job and draft state.");
    }
    return viewState;
    }

  async function pollPromptGenerationStatus({
    jobId,
    accountId,
    scope,
    delayMs = 3000
  }: {
    jobId: string;
    accountId: string;
    scope: number;
    delayMs?: number;
  }) {
    stopPromptGenerationPolling();
    const run = async () => {
      if (scope !== promptGenerationScopeRef.current || promptGenerationAccountRef.current !== accountId) return;
      const controller = new AbortController();
      let clearPointerOnFailure = false;
      let retryTransientFailure = false;
      promptGenerationAbortRef.current = controller;
      try {
        if (!session?.access_token) throw new Error("Missing session token.");
        const response = await fetch(
          `/api/admin-onboarding-generate-prompt-pack-draft-status?job_id=${encodeURIComponent(jobId)}&account_id=${encodeURIComponent(accountId)}`,
          {
            method: "GET",
            headers: { Authorization: `Bearer ${session.access_token}` },
            signal: controller.signal
          }
        );
        const json = await response.json().catch(() => null) as PromptGenerationStatusResponse | null;
        if (!response.ok || !json?.ok) {
          retryTransientFailure = response.status >= 500;
          clearPointerOnFailure =
            [400, 401, 403, 404].includes(response.status) ||
            (response.ok && !json?.ok) ||
            (!json && !retryTransientFailure);
          if (retryTransientFailure) {
            throw new Error((json as unknown as { error?: string } | null)?.error || "Failed to load prompt generation status.");
          }

          const latestDiscovery = await discoverLatestPromptGenerationDraft({
            accountId,
            accessToken: session.access_token,
            signal: controller.signal
          });

          if (latestDiscovery.kind === "found") {
            clearPromptGenerationPointer(accountId);
            storePromptGenerationPointer(accountId, latestDiscovery.job.id);
            const latestViewState = applyPromptGenerationEnvelope(
              {
                ok: true,
                job: latestDiscovery.job,
                draft: latestDiscovery.draft
              },
              accountId,
              latestDiscovery.job.id,
              scope
            );
            if (latestViewState === "queued" || latestViewState === "generating") {
              promptGenerationPollTimerRef.current = window.setTimeout(run, 3000);
            }
            return;
          }

          clearPromptGenerationPointer(accountId);
          setIsRecoveringPromptGeneration(false);
          setPromptGenerationTransportWarning("");

          if (latestDiscovery.kind === "transient_failure") {
            setPromptPackDraftStatus("No recoverable prompt generation found.");
            return;
          }

          setPromptPackDraftStatus("No recoverable prompt generation found.");
          return;
        }
        let viewState: PromptGenerationViewState | "stale";
        try {
          viewState = applyPromptGenerationEnvelope(json, accountId, jobId, scope);
        } catch (error) {
          clearPointerOnFailure = true;
          throw error;
        }
        if (viewState === "failed") {
          const latestDiscovery = await discoverLatestPromptGenerationDraft({
            accountId,
            accessToken: session.access_token,
            signal: controller.signal
          });

          if (latestDiscovery.kind === "found") {
            clearPromptGenerationPointer(accountId);
            storePromptGenerationPointer(accountId, latestDiscovery.job.id);
            const latestViewState = applyPromptGenerationEnvelope(
              {
                ok: true,
                job: latestDiscovery.job,
                draft: latestDiscovery.draft
              },
              accountId,
              latestDiscovery.job.id,
              scope
            );
            if (latestViewState === "queued" || latestViewState === "generating") {
              promptGenerationPollTimerRef.current = window.setTimeout(run, 3000);
            }
            return;
          }
        }
        if (viewState === "queued" || viewState === "generating") {
          promptGenerationPollTimerRef.current = window.setTimeout(run, 3000);
        }
      } catch (error: any) {
        if (
          controller.signal.aborted ||
          scope !== promptGenerationScopeRef.current ||
          promptGenerationAccountRef.current !== accountId
        ) return;
        if (!clearPointerOnFailure) {
          setIsRecoveringPromptGeneration(true);
          setPromptGenerationTransportWarning("Status temporarily unavailable; retrying…");
          promptGenerationPollTimerRef.current = window.setTimeout(run, 3000);
          return;
        }
        if (clearPointerOnFailure) clearPromptGenerationPointer(accountId);
        setIsRecoveringPromptGeneration(false);
        setPromptGenerationTransportWarning("");
        setPromptGenerationFailure({
          code: "prompt_generation_status_unavailable",
          message: error?.message || "Failed to restore prompt generation status.",
          failedAt: null
        });
        setPromptPackDraftStatus("Failed");
      } finally {
        if (promptGenerationAbortRef.current === controller) promptGenerationAbortRef.current = null;
      }
    };

    promptGenerationPollTimerRef.current = window.setTimeout(run, delayMs);
  }

  async function handleGeneratePromptPackDraft() {
    if (!selectedAccountId) {
      setPromptPackDraftStatus("Select or save an account first.");
      return;
    }
    if (promptGenerationRequestPendingRef.current || promptGenerationIsActive) return;

    const desiredPromptCount = getDesiredPromptCount(category);

    const confirmed = window.confirm(
      [
        "Generate a draft prompt pack?",
        "",
        `Account: ${accountName || businessName || "Unnamed account"}`,
        `Type: ${accountType || "Not set"}`,
        `Category: ${category || "Not set"}`,
        `Desired prompts: ${desiredPromptCount}`,
        `Intelligence: ${intelligenceReadinessSummary.status} (${intelligenceReadinessSummary.passing}/${intelligenceReadinessSummary.total})`,
        "",
        "This will generate a reviewable draft only.",
        "It will not save, apply, replace, or run prompts."
      ].join("\n")
    );

    if (!confirmed) {
      return;
    }

    setIsGeneratingPromptPackDraft(true);
    promptGenerationRequestPendingRef.current = true;
    stopPromptGenerationPolling();
    const accountId = selectedAccountId;
    const scope = promptGenerationScopeRef.current;
    setPromptPackDraftStatus("Submitting prompt generation...");
    setPromptGenerationFailure(null);
    setPromptGenerationTransportWarning("");
    setIsRecoveringPromptGeneration(false);
    setPromptGenerationJob(null);
    setPromptGenerationDraft(null);
    setGeneratedPromptPackDraft(null);
    setSavedGeneratedPromptPack(null);
    setIsPromptPackReviewOpen(false);
    setGeneratedPromptPackReviewSaveStatus("");
    setPromptPackReviewIntegrityFailure(null);

    const generationBrief = buildPromptGenerationBrief();
    setPromptGenerationBrief(generationBrief);

    try {
      if (!session?.access_token) throw new Error("Missing session token.");
      const response = await fetch("/api/admin-onboarding-generate-prompt-pack-draft", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          account_id: accountId,
          desired_prompt_count: desiredPromptCount,
          source_pack_ids: promptPackStackIds.filter(Boolean)
        })
      });

      const json = await response.json().catch(() => null) as PromptGenerationEnqueueResponse | null;

      if (response.status !== 202 || !json?.ok || !json.queued) {
        throw new Error((json as unknown as { error?: string } | null)?.error || "Failed to enqueue prompt pack generation.");
      }
      if (scope !== promptGenerationScopeRef.current || promptGenerationAccountRef.current !== accountId) return;
      const viewState = applyPromptGenerationEnvelope(json, accountId, json.job.id, scope);
      storePromptGenerationPointer(accountId, json.job.id);
      if (viewState === "queued" || viewState === "generating") {
        void pollPromptGenerationStatus({ jobId: json.job.id, accountId, scope, delayMs: 3000 });
      }
    } catch (error: any) {
      console.error("Failed to generate prompt pack draft:", error);
      if (scope === promptGenerationScopeRef.current && promptGenerationAccountRef.current === accountId) {
        setPromptGenerationFailure({
          code: "prompt_generation_enqueue_failed",
          message: error?.message || "Failed to enqueue prompt pack generation.",
          failedAt: null
        });
        setPromptPackDraftStatus("Failed");
      }
    } finally {
      promptGenerationRequestPendingRef.current = false;
      if (
        scope === promptGenerationScopeRef.current &&
        promptGenerationAccountRef.current === accountId
      ) setIsGeneratingPromptPackDraft(false);
    }
  }

  async function handleOpenLatestPromptPackReview() {
    if (!selectedAccountId) {
      setPromptPackDraftStatus("Select or save an account first.");
      return;
    }
    if (!session?.access_token) {
      setPromptPackDraftStatus("Missing session token.");
      return;
    }

    setIsRefreshingLatestPromptPackReview(true);
    setGeneratedPromptPackReviewSaveStatus("");
    try {
      const latest = await discoverLatestPromptGenerationDraft({
        accountId: selectedAccountId,
        accessToken: session.access_token
      });
      if (latest.kind !== "found") {
        throw new Error(latest.kind === "transient_failure"
          ? latest.message
          : "No review-ready generated draft is available.");
      }

      const scope = promptGenerationScopeRef.current;
      const viewState = applyPromptGenerationEnvelope(
        { ok: true, job: latest.job, draft: latest.draft },
        selectedAccountId,
        latest.job.id,
        scope
      );
      if (viewState !== "review_ready") {
        throw new Error("The latest generated draft is not review-ready yet.");
      }
      storePromptGenerationPointer(selectedAccountId, latest.job.id);
      setPromptPackDraftStatus("Showing latest generated draft.");
      setIsPromptPackReviewOpen(true);
    } catch (error: any) {
      console.error("Failed to refresh latest prompt pack draft:", error);
      setPromptPackDraftStatus(error?.message || "Failed to load the latest generated draft.");
    } finally {
      setIsRefreshingLatestPromptPackReview(false);
    }
  }

  async function handleSaveGeneratedPromptPackDraft(editedDraftPromptPack?: PromptPackReviewDraftPack) {
    const draftPromptPack = editedDraftPromptPack || generatedPromptPackDraft?.draft_prompt_pack;
    if (!selectedAccountId) {
      setPromptPackDraftStatus("Select or save an account first.");
      setGeneratedPromptPackReviewSaveStatus("Select or save an account first.");
      return;
    }

    const editedReviewValidation = draftPromptPack
      ? validatePromptPackReviewDraft(draftPromptPack)
      : null;
    if (editedReviewValidation && !editedReviewValidation.ok) {
      const editedFailure = formatPromptPackReviewIntegrityError(editedReviewValidation);
      setPromptPackDraftStatus(editedFailure.message);
      setGeneratedPromptPackReviewSaveStatus(editedFailure.message);
      return;
    }

    if (promptPackReviewIntegrityFailure) {
      setPromptPackDraftStatus(promptPackReviewIntegrityFailure.message);
      setGeneratedPromptPackReviewSaveStatus(promptPackReviewIntegrityFailure.message);
      return;
    }

    if (!canSaveGeneratedPromptPackDraft || !draftPromptPack) {
      setPromptPackDraftStatus("A review-ready generated draft is required before saving.");
      setGeneratedPromptPackReviewSaveStatus("A review-ready generated draft is required before saving.");
      return;
    }

    const isReapplying =
      Boolean(savedGeneratedPromptPack?.applied_to_account) ||
      Boolean(generatedPromptPackDisplay?.isApplied);

    const confirmed = window.confirm(
      [
        isReapplying
          ? "Save these edits and reapply them as a new account prompt-pack version?"
          : "Save and apply this draft as an account-specific prompt pack?",
        "",
        `Account: ${accountName || businessName || "Unnamed account"}`,
        `Draft: ${draftPromptPack.label}`,
        `Prompts: ${draftPromptPack.prompt_count || draftPromptPack.prompts?.length || 0}`,
        "",
        isReapplying
          ? "This will update the persisted draft, create a new prompt-pack version, archive the current active prompts, and apply the edited version."
          : "This will save the draft, archive existing active prompts, and apply the generated prompts to the account."
      ].join("\n")
    );

    if (!confirmed) {
      return;
    }

    setIsSavingGeneratedPromptPackDraft(true);
    setGeneratedPromptPackReviewSaveStatus("Saving generated draft as account-specific prompt pack...");
    setPromptPackDraftStatus("Saving generated draft as account-specific prompt pack...");

    try {
      if (!session?.access_token) throw new Error("Missing session token.");
      const response = await fetch("/api/admin-onboarding-save-generated-prompt-pack-draft", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          account_id: selectedAccountId,
          draft_prompt_pack: draftPromptPack,
          change_source: "manual_admin",
          change_reason: isReapplying
            ? "post_apply_prompt_edit"
            : "initial_prompt_review"
        })
      });

      const json = await response.json().catch(() => null);

      if (!response.ok || !json?.ok) {
        throw new Error(json?.error || "Failed to save generated prompt pack draft.");
      }

      const savedPack = json.pack || null;
      const savedPackId = savedPack?.id || "";

      if (!savedPackId) {
        throw new Error("Saved generated prompt pack response did not include a pack id.");
      }

      const savedPromptPackRecord = {
        ...savedPack,
        actual_prompt_count:
          savedPack.actual_prompt_count ??
          savedPack.prompt_count ??
          savedPack.prompts?.length ??
          0
      };

      setSavedGeneratedPromptPack(savedPack);
      setPromptPackStackIds([savedPackId]);
      setPromptPacks((current) => {
        const next = current.filter((pack: PromptPack) => pack.id !== savedPackId);
        return [savedPromptPackRecord, ...next];
      });
      clearPromptGenerationPointer(selectedAccountId);
      setPromptPackDraftStatus("Applying saved account-specific prompt pack...");
      setGeneratedPromptPackReviewSaveStatus("Applying saved account-specific prompt pack...");

      const applyResponse = await fetch("/api/admin-onboarding-apply-prompt-pack-stack", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          account_id: selectedAccountId,
          pack_ids: [savedPackId]
        })
      });

      const applyJson = await applyResponse.json().catch(() => null);

      if (!applyResponse.ok || !applyJson?.ok) {
        const reason = applyJson?.error || "Failed to apply saved prompt pack.";
        const packLabel = savedPack.label || savedPack.label_text || savedPack.name || savedPackId;
        const partialMessage = `Saved account-specific prompt pack ${packLabel} (${savedPackId}), but applying it failed: ${reason}`;
        setPromptPackDraftStatus(partialMessage);
        setGeneratedPromptPackReviewSaveStatus(partialMessage);
        setPromptPackReviewIntegrityFailure(null);
        return;
      }

      await loadAccountPromptSummary(selectedAccountId);

      const activePromptCount =
        applyJson?.resulting_active_prompt_count ??
        applyJson?.active_prompt_count ??
        applyJson?.prompt_count ??
        json.prompt_count ??
        savedPack.prompt_count ??
        savedPack.prompts?.length ??
        0;

      setSavedGeneratedPromptPack({
        ...savedPack,
        applied_to_account: true,
        active_prompt_count: activePromptCount
      });

      setGeneratedPromptPackDraft((current) =>
        current
          ? {
              ...current,
              draft_prompt_pack: {
                ...current.draft_prompt_pack,
                ...draftPromptPack,
                prompts:
                  editedReviewValidation?.normalizedPrompts ||
                  current.draft_prompt_pack.prompts,
                prompt_count:
                  editedReviewValidation?.promptCount ||
                  current.draft_prompt_pack.prompt_count,
                last_saved_pack_id: savedPackId,
                last_saved_pack_key: savedPack.pack_key || null,
                last_saved_source: "manual_admin",
                last_saved_reason: isReapplying
                  ? "post_apply_prompt_edit"
                  : "initial_prompt_review"
              }
            }
          : current
      );

      const appliedMessage = isReapplying
        ? `Saved edits as a new prompt-pack version and reapplied ${activePromptCount} active prompts.`
        : `Saved and applied account-specific prompt pack with ${activePromptCount} active prompts.`;

      setPromptPackDraftStatus(appliedMessage);
      setGeneratedPromptPackReviewSaveStatus(appliedMessage);
      setPromptPackReviewIntegrityFailure(null);
      setIsPromptPackReviewOpen(false);
    } catch (error: any) {
      console.error("Failed to save generated prompt pack draft:", error);
      const errorMessage = error?.message || "Failed to save generated prompt pack draft.";
      setPromptPackDraftStatus(errorMessage);
      setGeneratedPromptPackReviewSaveStatus(errorMessage);
    } finally {
      setIsSavingGeneratedPromptPackDraft(false);
    }
  }

  async function loadRecentRuns(accountId = selectedAccountId) {
    if (!accountId) {
      setRecentRuns([]);
      return;
    }

    setIsLoadingRecentRuns(true);

    try {
      const { data, error } = await supabase
        .from("runs")
        .select("run_id, account_id, status, prompt_count, run_label, model_version, run_date, created_at, completed_at, started_at")
        .eq("account_id", accountId)
        .order("created_at", { ascending: false })
        .limit(5);

      if (error) {
        throw error;
      }

      setRecentRuns(data || []);
    } catch (error: any) {
      console.error("Failed to load recent runs:", error);
      setRunControlStatus(error?.message || "Failed to load recent runs.");
      setRecentRuns([]);
    } finally {
      setIsLoadingRecentRuns(false);
    }
  }

  async function loadIntelligenceReadiness(accountId = selectedAccountId) {
    if (!accountId) {
      setIntelligenceReadiness(null);
      return;
    }

    setIsLoadingIntelligenceReadiness(true);

    try {
      const res = await fetch(
        `/api/admin-onboarding-competitive-setup-snapshot?account_id=${encodeURIComponent(accountId)}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${session?.access_token || ""}`,
          },
        }
      );

      const responseText = await res.text();
      let snapshot: any = {};

      try {
        snapshot = responseText ? JSON.parse(responseText) : {};
      } catch {
        snapshot = {
          error: responseText.slice(0, 500) || "Competitive setup snapshot returned a non-JSON response",
        };
      }

      if (!res.ok || !snapshot?.ok) {
        throw new Error(snapshot?.error || "Failed to load competitive setup snapshot.");
      }

      const marketProfile = snapshot.market_profile || null;
      const entityRoles = snapshot.entity_roles || [];
      const brandReview = snapshot.brand_dictionary_seed_review || null;

      const directCompetitors = entityRoles.filter((row: any) => row.entity_role === "direct_competitor");
      const suppressed = entityRoles.filter((row: any) => row.score_treatment === "suppress_everywhere" || row.entity_role === "suppress");
      const customerEntities = entityRoles.filter((row: any) => row.entity_role === "customer" || row.entity_role === "self");

      setIntelligenceReadiness({
        marketProfile,
        entityRoleCount: entityRoles.length,
        directCompetitorCount: directCompetitors.length,
        suppressionCount: suppressed.length,
        customerEntityCount: customerEntities.length,
        brandReview,
      });
    } catch (error: any) {
      console.error("Failed to load intelligence readiness:", error);
      setIntelligenceReadiness({
        error: error?.message || "Failed to load intelligence readiness"
      });
    } finally {
      setIsLoadingIntelligenceReadiness(false);
    }
  }

  async function handleAuditPromptPack() {
    if (!selectedAccountId) {
      alert("Save the account before auditing prompt packs.");
      return;
    }

    const selectedPackIds = promptPackStackIds.filter(Boolean);
    const uniqueSelectedPackIds = Array.from(new Set(selectedPackIds));

    if (selectedPackIds.length === 0) {
      alert("Select a prompt pack to audit.");
      return;
    }

    if (selectedPackIds.length !== uniqueSelectedPackIds.length) {
      alert("Duplicate prompt packs are not allowed.");
      return;
    }

    if (uniqueSelectedPackIds.length > 1) {
      alert("Prompt Agent V1 audits one selected prompt pack at a time. Remove extra packs and audit again.");
      return;
    }

    setIsAuditingPromptPack(true);
    setPromptPackStatus("");
    setPromptPackAudit(null);

    try {
      const res = await fetch("/api/admin-onboarding-audit-prompt-pack", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token || ""}`,
        },
        body: JSON.stringify({
          account_id: selectedAccountId,
          pack_id: uniqueSelectedPackIds[0],
        }),
      });

      const responseText = await res.text();
      let data: any = {};

      try {
        data = responseText ? JSON.parse(responseText) : {};
      } catch {
        data = {
          error: responseText.slice(0, 500) || "Prompt Agent returned a non-JSON response",
        };
      }

      if (!res.ok) {
        console.error("Failed to audit prompt pack:", data);
        alert(data?.error || "Failed to audit prompt pack.");
        setPromptPackStatus(data?.error || "Failed to audit prompt pack.");
        return;
      }

      setPromptPackAudit(data.audit || null);

      const action = data.audit?.recommended_action
        ? ` Recommended action: ${String(data.audit.recommended_action).replaceAll("_", " ")}.`
        : "";

      setPromptPackStatus(`Prompt Agent audit complete.${action}`);
    } catch (error: any) {
      console.error("Failed to audit prompt pack:", error);
      alert(error?.message || "Failed to audit prompt pack.");
      setPromptPackStatus(error?.message || "Failed to audit prompt pack.");
    } finally {
      setIsAuditingPromptPack(false);
    }
  }

  async function handlePreviewLocalizedPromptPack() {
    if (!selectedAccountId) {
      alert("Save the account before previewing localized prompt packs.");
      return;
    }

    const selectedPackIds = promptPackStackIds.filter(Boolean);
    const uniqueSelectedPackIds = Array.from(new Set(selectedPackIds));

    if (uniqueSelectedPackIds.length !== 1) {
      alert("Select exactly one prompt pack to preview localization.");
      return;
    }

    setIsPreviewingLocalizedPromptPack(true);
    setPromptPackStatus("");
    setPromptPackPreview(null);

    try {
      const res = await fetch("/api/admin-onboarding-apply-localized-prompt-pack", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token || ""}`,
        },
        body: JSON.stringify({
          account_id: selectedAccountId,
          pack_id: uniqueSelectedPackIds[0],
          dry_run: true,
        }),
      });

      const responseText = await res.text();
      let data: any = {};

      try {
        data = responseText ? JSON.parse(responseText) : {};
      } catch {
        data = {
          error: responseText.slice(0, 500) || "Localized prompt pack preview returned a non-JSON response",
        };
      }

      if (!res.ok) {
        console.error("Failed to preview localized prompt pack:", data);
        alert(data?.error || "Failed to preview localized prompt pack.");
        setPromptPackStatus(data?.error || "Failed to preview localized prompt pack.");
        return;
      }

      setPromptPackPreview({
        ok: true,
        localized_preview: true,
        pack: {
          ...(data.pack || {}),
          label: data.account_specific_prompt_pack_label || data.pack?.label || "Localized Prompt Pack Preview",
          pack_key: data.prompt_set || data.pack?.pack_key || "localized_prompt_pack_preview",
        },
        prompt_count: data.account_specific_prompt_pack_prompt_count || data.prompts?.length || 0,
        counts_by_category: (data.prompts || []).reduce((acc: Record<string, number>, row: any) => {
          const key = row.category || "uncategorized";
          acc[key] = (acc[key] || 0) + 1;
          return acc;
        }, {}),
        prompts: data.prompts || [],
        geo_context: data.geo_context || null,
        warnings: data.warnings || [],
      });

      const warningText = Array.isArray(data.warnings) && data.warnings.length
        ? ` Warnings: ${data.warnings.join(" ")}`
        : "";

      setPromptPackStatus(
        `Localized preview loaded. ${data.localized_prompt_count || 0} prompts would change.${warningText}`
      );
    } catch (error: any) {
      console.error("Failed to preview localized prompt pack:", error);
      alert(error?.message || "Failed to preview localized prompt pack.");
      setPromptPackStatus(error?.message || "Failed to preview localized prompt pack.");
    } finally {
      setIsPreviewingLocalizedPromptPack(false);
    }
  }

  async function handleApplyLocalizedPromptPack() {
    if (!selectedAccountId) {
      alert("Save the account before applying localized prompt packs.");
      return;
    }

    const selectedPackIds = promptPackStackIds.filter(Boolean);
    const uniqueSelectedPackIds = Array.from(new Set(selectedPackIds));

    if (selectedPackIds.length === 0) {
      alert("Select a prompt pack to localize.");
      return;
    }

    if (selectedPackIds.length !== uniqueSelectedPackIds.length) {
      alert("Duplicate prompt packs are not allowed.");
      return;
    }

    if (uniqueSelectedPackIds.length > 1) {
      alert("Localized Prompt Pack V1 applies one selected prompt pack at a time. Remove extra packs and try again.");
      return;
    }

    if (hasBlockedPromptPackStack) {
      const blockedSummary = blockedPromptPackSafetyResults
        .map((result) => `${result.pack.label}: ${result.safety.reasons.join(" ")}`)
        .join("\n\n");

      alert(
        [
          "Agent Smith blocked this prompt pack.",
          "",
          "This looks source-contaminated for the selected account.",
          "",
          blockedSummary,
          "",
          "Generate and audit an account-specific custom pack instead.",
        ].join("\n")
      );
      return;
    }

    const selectedPackLabel = selectedPromptPackStack[0]?.label || "selected prompt pack";

    const confirmMessage = [
      "This will archive the account's existing active prompts and replace them with a localized version of the selected prompt pack.",
      "",
      `Selected pack: ${selectedPackLabel}`,
      "",
      "Continue?",
    ].join("\n");

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsApplyingLocalizedPromptPack(true);
    setPromptPackStatus("");

    try {
      const res = await fetch("/api/admin-onboarding-apply-localized-prompt-pack", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token || ""}`,
        },
        body: JSON.stringify({
          account_id: selectedAccountId,
          pack_id: uniqueSelectedPackIds[0],
        }),
      });

      const responseText = await res.text();
      let data: any = {};

      try {
        data = responseText ? JSON.parse(responseText) : {};
      } catch {
        data = {
          error: responseText.slice(0, 500) || "Localized prompt pack returned a non-JSON response",
        };
      }

      if (!res.ok) {
        console.error("Failed to apply localized prompt pack:", data);
        alert(data?.error || "Failed to apply localized prompt pack.");
        setPromptPackStatus(data?.error || "Failed to apply localized prompt pack.");
        return;
      }

      const warnings = Array.isArray(data.warnings) && data.warnings.length > 0
        ? ` Warnings: ${data.warnings.join(" ")}`
        : "";

      await loadAccountPromptSummary();

      setPromptPackStatus(
        [
          `Applied localized prompt pack: ${selectedPackLabel}.`,
          `Archived: ${data.archived_prompt_count || 0}.`,
          `Inserted: ${data.inserted_prompt_count || 0}.`,
          `Localized: ${data.localized_prompt_count || 0}.`,
          `Active prompts now: ${data.resulting_active_prompt_count ?? "unknown"}.`,
          warnings,
        ].filter(Boolean).join(" ")
      );
    } catch (error: any) {
      console.error("Failed to apply localized prompt pack:", error);
      alert(error?.message || "Failed to apply localized prompt pack.");
      setPromptPackStatus(error?.message || "Failed to apply localized prompt pack.");
    } finally {
      setIsApplyingLocalizedPromptPack(false);
    }
  }

  async function handleApplyPromptPack() {
    if (!selectedAccountId) {
      alert("Save the account before applying prompt packs.");
      return;
    }

    const selectedPackIds = promptPackStackIds.filter(Boolean);
    const uniqueSelectedPackIds = Array.from(new Set(selectedPackIds));

    if (selectedPackIds.length === 0) {
      alert("Select at least one prompt pack.");
      return;
    }

    if (selectedPackIds.length !== uniqueSelectedPackIds.length) {
      alert("Duplicate prompt packs are not allowed.");
      return;
    }

    if (hasBlockedPromptPackStack) {
      const blockedSummary = blockedPromptPackSafetyResults
        .map((result) => `${result.pack.label}: ${result.safety.reasons.join(" ")}`)
        .join("\n\n");

      alert(
        [
          "Agent Smith blocked this prompt pack stack.",
          "",
          "One or more selected packs look source-contaminated for the selected account.",
          "",
          blockedSummary,
          "",
          "Generate and audit an account-specific custom pack instead.",
        ].join("\n")
      );
      return;
    }

    const selectedPackLabels = selectedPromptPackStack.map((pack) => pack.label).join(", ");

    const confirmMessage = [
      "This will archive the account's existing active prompts and replace them with the selected prompt-pack stack.",
      "",
      `Selected packs: ${selectedPackLabels}`,
      `Projected active prompts: ${projectedPromptStackCount}`,
      "",
      "Continue?",
    ].join("\n");

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsApplyingPromptPack(true);
    setPromptPackStatus("");

    try {
      const res = await fetch("/api/admin-onboarding-apply-prompt-pack-stack", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token || ""}`,
        },
        body: JSON.stringify({
          account_id: selectedAccountId,
          pack_ids: uniqueSelectedPackIds,
        }),
      });

      const responseText = await res.text();
      let data: any = {};

      try {
        data = responseText ? JSON.parse(responseText) : {};
      } catch {
        data = {
          error: responseText.slice(0, 500) || "Agent Smith prompt configuration returned a non-JSON response",
        };
      }

      if (!res.ok) {
        console.error("Failed to apply prompt pack stack:", data);
        alert(data?.error || "Failed to apply prompt pack stack.");
        setPromptPackStatus(data?.error || "Failed to apply prompt pack stack.");
        return;
      }

      const warnings = Array.isArray(data.warnings) && data.warnings.length > 0
        ? ` Warnings: ${data.warnings.join(" ")}`
        : "";

      const appliedPackLabels = Array.isArray(data.packs)
        ? data.packs.map((pack: PromptPack) => pack.label).join(", ")
        : selectedPackLabels;

      setPromptPackStatus(
        [
          `Applied prompt-pack stack: ${appliedPackLabels}.`,
          `Archived: ${data.archived_prompt_count || 0}.`,
          `Inserted: ${data.inserted_prompt_count || 0}.`,
          `Active prompts now: ${data.resulting_active_prompt_count ?? "unknown"}.`,
          warnings,
        ].filter(Boolean).join(" ")
      );
    } catch (error: any) {
      console.error("Failed to apply prompt pack stack:", error);
      alert(error?.message || "Failed to apply prompt pack stack.");
      setPromptPackStatus(error?.message || "Failed to apply prompt pack stack.");
    } finally {
      setIsApplyingPromptPack(false);
    }
  }

  async function loadLatestBrandSeedReview(accountId: string) {
    if (!accountId) return;

    setBrandSeedReviewLoading(true);
    setBrandSeedReviewError(null);

    try {
      const response = await fetch(
        `/api/admin-onboarding-brand-dictionary-seed-review-latest?account_id=${encodeURIComponent(accountId)}`,
        { headers: { Authorization: `Bearer ${session?.access_token || ""}` } }
      );

      const json = await response.json();

      if (!response.ok || !json.ok) {
        throw new Error(json.error || "Failed to load latest brand seed review");
      }

      setBrandSeedReview(json.review_batch || null);
    } catch (error: any) {
      setBrandSeedReviewError(error.message || "Failed to load latest brand seed review");
    } finally {
      setBrandSeedReviewLoading(false);
    }
  }

  function mergeBrandSeedReviewItems(updatedItems: any[]) {
    if (!updatedItems || updatedItems.length === 0) return;

    setBrandSeedReview((current: any) => {
      if (!current?.items) return current;

      const updatedById = new Map(updatedItems.map((item: any) => [item.id, item]));

      return {
        ...current,
        items: current.items.map((item: any) =>
          updatedById.has(item.id) ? updatedById.get(item.id) : item
        )
      };
    });
  }

  async function handleUpdateBrandSeedReviewItem(
    itemId: string,
    patch: Record<string, any>
  ) {
    if (!itemId) return;

    setBrandSeedItemUpdatingId(itemId);
    setBrandSeedItemUpdateError(null);

    try {
      const response = await fetch(
        "/api/admin-onboarding-brand-dictionary-seed-review-item",
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session?.access_token || ""}`
          },
          body: JSON.stringify({
            account_id: selectedAccountId,
            item_id: itemId,
            patch
          })
        }
      );

      const json = await response.json();

      if (!response.ok || !json.ok) {
        throw new Error(json.error || "Failed to update review item");
      }

      if (json.item) {
        mergeBrandSeedReviewItems([json.item]);
      }
    } catch (error: any) {
      setBrandSeedItemUpdateError(error.message || "Failed to update review item");
    } finally {
      setBrandSeedItemUpdatingId(null);
    }
  }

  async function handleBulkUpdateBrandSeedReviewItems(
    itemType: "seed" | "suppression" | "needs_review",
    reviewDecision: "pending" | "approved" | "rejected" | "needs_review"
  ) {
    const reviewBatchId = (brandSeedReview as any)?.id;

    if (!reviewBatchId) {
      setBrandSeedItemUpdateError("Load a saved review batch before bulk updating.");
      return;
    }

    setBrandSeedBulkUpdatingType(itemType);
    setBrandSeedItemUpdateError(null);

    try {
      const response = await fetch(
        "/api/admin-onboarding-brand-dictionary-seed-review-items-bulk",
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session?.access_token || ""}`
          },
          body: JSON.stringify({
            account_id: selectedAccountId,
            review_batch_id: reviewBatchId,
            item_type: itemType,
            review_decision: reviewDecision,
            current_decision: "pending"
          })
        }
      );

      const json = await response.json();

      if (!response.ok || !json.ok) {
        throw new Error(json.error || "Failed to bulk update review items");
      }

      mergeBrandSeedReviewItems(json.items || []);
    } catch (error: any) {
      setBrandSeedItemUpdateError(error.message || "Failed to bulk update review items");
    } finally {
      setBrandSeedBulkUpdatingType(null);
    }
  }

  async function handleGenerateBrandSeeds() {
    if (!selectedAccountId) return;

    setBrandSeedLoading(true);
    setBrandSeedError(null);
    setBrandSeedResult(null);

    try {
      const response = await fetch(
        "/api/admin-onboarding-generate-brand-dictionary-seeds",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session?.access_token || ""}`
          },
          body: JSON.stringify({
            account_id: selectedAccountId
          })
        }
      );

      const json = await response.json();

      if (!response.ok || !json.ok) {
        throw new Error(json.error || "Failed to generate brand seeds");
      }

      setBrandSeedResult(json);
      setBrandSeedReview(json.review_batch || null);
      await loadIntelligenceReadiness(selectedAccountId);

      if (json.item) {
        mergeBrandSeedReviewItems([json.item]);
      }
    } catch (error: any) {
      setBrandSeedError(error.message || "Failed to generate brand seeds");
    } finally {
      setBrandSeedLoading(false);
    }
  }

  function buildRunConfirmationMessage() {
    const latestRun = recentRuns[0] || null;
    const readinessFailed = onboardingReadiness.checks.filter((check: any) => !check.ok);
    const selectedPackLabels = selectedPromptPackStack
      .map((pack: any) => pack.label || pack.pack_key)
      .filter(Boolean);

    return [
      "Start analysis run?",
      "",
      `Account: ${accountName || businessName || "Unnamed account"}`,
      `Account ID: ${selectedAccountId || "Not saved"}`,
      `Run profile: ${selectedModelProfile.label}`,
      `Model profile: ${selectedModelProfile.key}`,
      `Prompt count: ${activePromptSetupSummary?.activePromptCount || 0}`,
      `Readiness: ${onboardingReadiness.status}`,
      `Intelligence: ${intelligenceReadinessSummary.status} (${intelligenceReadinessSummary.passing}/${intelligenceReadinessSummary.total})`,
      selectedPackLabels.length
        ? `Selected packs: ${selectedPackLabels.join(", ")}`
        : "Selected packs: None selected",
      latestRun
        ? `Latest run: ${latestRun.status || "unknown"} · ${latestRun.run_label || "no label"}`
        : "Latest run: None found",
      "",
      readinessFailed.length
        ? [
            "Readiness notes:",
            ...readinessFailed.map((check: any) => `- ${check.label}: ${check.help}`),
            ""
          ].join("\n")
        : "All core readiness checks are passing.\n",
      "Click OK to start the run."
    ].join("\n");
  }

  async function handleRunSelectedAnalysis() {
    if (!selectedAccountId) {
      alert("Select or save an account before running analysis.");
      return;
    }

    const activePromptCount = activePromptSetupSummary?.activePromptCount || 0;

    if (hasActiveRecentRun) {
      alert("This account already has an active run. Wait for it to complete or fail before starting another run.");
      return;
    }

    if (!activePromptCount) {
      alert("This account has no active prompts. Apply a prompt pack before running analysis.");
      return;
    }

    const selectedAccountName = accountName || businessName || selectedAccountId;
    const runId =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

    const confirmMessage = [
      "Run selected analysis now?",
      "",
      `Account: ${selectedAccountName}`,
      `Account ID: ${selectedAccountId}`,
      `Active prompts: ${activePromptCount}`,
      `Run profile: ${selectedModelProfile.label}`,
      `Run ID: ${runId}`,
      "",
      "This will call the live runner and may take several minutes."
    ].join("\n");

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsRunningAnalysis(true);
    setRunControlStatus("Starting analysis run...");

    try {
      const response = await postRunAnalysis({
          account_id: selectedAccountId,
          run_id: runId,
          model_profile: selectedModelProfile.key,
          run_label: selectedModelProfile.runLabel,
        }, { accessToken: session?.access_token, auth: supabase.auth });

      const responseText = await response.text();
      let json: any = {};

      try {
        json = responseText ? JSON.parse(responseText) : {};
      } catch {
        json = {
          error: responseText.slice(0, 500) || "Run endpoint returned a non-JSON response",
        };
      }

      if (!response.ok || json.ok === false || json.success === false) {
        throw new Error(json.error || "Failed to run selected analysis.");
      }

      setRunControlStatus(
        [
          json.status === "started" || json.status === "accepted"
            ? "Run started. Recent run status will auto-refresh while active."
            : "Run response received.",
          `Run ID: ${json.run_id || runId}.`,
          `Status: ${json.status || "submitted"}.`
        ].join(" ")
      );

      await loadAccountPromptSummary(selectedAccountId);
      await loadRecentRuns(selectedAccountId);
    } catch (error: any) {
      console.error("Failed to run selected analysis:", error);
      setRunControlStatus(error?.message || "Failed to run selected analysis.");
      alert(error?.message || "Failed to run selected analysis.");
    } finally {
      setIsRunningAnalysis(false);
    }
  }

  if (isCheckingAdmin) {
    return (
      <div style={{ padding: 32 }}>
        Checking admin access...
      </div>
    );
  }

  if (!hasAdminAccess) {
    return (
      <div style={{ padding: 32 }}>
        <h1>Admin access required</h1>
        <p>You do not have permission to access Account Onboarding.</p>


      </div>
    );
  }

  function buildBrandSeedApplyConfirmationMessage() {
    const reviewBatch = brandSeedReview as any;
    const items = Array.isArray(reviewBatch?.items) ? reviewBatch.items : [];

    const approvedItems = items.filter((item: any) => item.review_decision === "approved");
    const approvedSeeds = approvedItems.filter((item: any) => item.item_type === "seed");
    const approvedSuppressions = approvedItems.filter((item: any) => item.item_type === "suppression");
    const approvedNeedsReview = approvedItems.filter((item: any) => item.item_type === "needs_review");

    return [
      "Apply approved brand dictionary review items?",
      "",
      `Account: ${accountName || businessName || "Unnamed account"}`,
      `Account ID: ${selectedAccountId || "Not saved"}`,
      `Review batch: ${reviewBatch?.id || "Not loaded"}`,
      "",
      `Approved items: ${approvedItems.length}`,
      `Approved brand seeds: ${approvedSeeds.length}`,
      `Approved suppressions: ${approvedSuppressions.length}`,
      `Approved needs-review items: ${approvedNeedsReview.length}`,
      "",
      "This will apply approved brand dictionary seed and suppression items.",
      "It may insert brand dictionary patterns and governed entity roles.",
      "It will not apply prompt packs.",
      "It will not replace active prompts.",
      "It will not start an analysis run.",
      "",
      "Click OK to apply approved brand dictionary items."
    ].join("\n");
  }

  async function handleApplyApprovedBrandSeedReviewItems() {
    const reviewBatchId = (brandSeedReview as any)?.id;

    if (!selectedAccountId || !reviewBatchId) {
      setBrandSeedApplyError("Load a saved review batch before applying approved items.");
      return;
    }

    const confirmed = window.confirm(buildBrandSeedApplyConfirmationMessage());

    if (!confirmed) {
      return;
    }


    setBrandSeedApplyLoading(true);
    setBrandSeedApplyError("");
    setBrandSeedApplyMessage("");

    try {
      const response = await fetch(
        "/api/admin-onboarding-apply-brand-dictionary-seed-review-items",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session?.access_token || ""}`
          },
          body: JSON.stringify({
            account_id: selectedAccountId,
            review_batch_id: reviewBatchId,
            applied_by: "admin_onboarding"
          })
        }
      );

      const json = await response.json();

      if (!response.ok || !json.ok) {
        throw new Error(json.error || "Failed to apply approved review items");
      }

      const summary = json.summary || {};

      setBrandSeedApplyMessage(
        [
          "Applied approved items.",
          `Seeds applied: ${summary.seeds_applied ?? 0}.`,
          `Patterns inserted: ${summary.seed_patterns_inserted ?? 0}.`,
          `Entity roles applied: ${summary.entity_roles_applied ?? 0}.`,
          `Duplicates skipped: ${summary.duplicate_skipped ?? 0}.`,
          `Errors: ${summary.errors ?? 0}.`
        ].join(" ")
      );

      window.setTimeout(() => {
        window.location.reload();
      }, 700);
    } catch (error: any) {
      setBrandSeedApplyError(error.message || "Failed to apply approved review items");
    } finally {
      setBrandSeedApplyLoading(false);
    }
  }


  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f8fafc",
        color: "#0f172a",
        padding: "32px 20px",
        fontFamily:
          'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      }}
    >
      <div style={{ maxWidth: 1480, margin: "0 auto" }}>
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "#0369a1", letterSpacing: "0.08em", textTransform: "uppercase" }}>
            Toren Admin
          </div>
          <h1 style={{ margin: "8px 0 8px", fontSize: 34, lineHeight: 1.1 }}>
            Account Onboarding
          </h1>
          <p style={{ margin: 0, color: "#475569", fontSize: 15, maxWidth: 760 }}>
            Configure a demo or customer account, stage users, choose the run profile, and prepare the account for launch.
          </p>
        </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          padding: 12,
          border: "1px solid #dbeafe",
          background: onboardingMode === "partner" ? "#eff6ff" : "#f8fafc",
          borderRadius: 12,
          marginBottom: 16,
        }}
      >
        <div>
          <div style={{ fontWeight: 900, color: "#0f172a" }}>
            Onboarding mode: {onboardingMode === "partner" ? "Partner-safe" : "Internal admin"}
          </div>
          <div style={{ fontSize: 13, color: "#64748b", marginTop: 2 }}>
            {onboardingMode === "partner"
              ? "Partner-safe mode hides internal review/apply controls while keeping the core onboarding workflow available."
              : "Internal admin mode shows all review, apply, and governance controls."}
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={() => setOnboardingMode("internal")}
            style={{
              border: onboardingMode === "internal" ? "1px solid #4f46e5" : "1px solid #cbd5e1",
              background: onboardingMode === "internal" ? "#4f46e5" : "white",
              color: onboardingMode === "internal" ? "white" : "#334155",
              borderRadius: 999,
              padding: "8px 12px",
              fontWeight: 800,
              cursor: "pointer",
            }}
          >
            Internal
          </button>
          <button
            type="button"
            onClick={() => setOnboardingMode("partner")}
            style={{
              border: onboardingMode === "partner" ? "1px solid #2563eb" : "1px solid #bfdbfe",
              background: onboardingMode === "partner" ? "#2563eb" : "#eff6ff",
              color: onboardingMode === "partner" ? "white" : "#1d4ed8",
              borderRadius: 999,
              padding: "8px 12px",
              fontWeight: 800,
              cursor: "pointer",
            }}
          >
            Partner-safe
          </button>
        </div>
      </div>

        <div
          style={{
            marginTop: 16,
            marginBottom: 16,
            border: onboardingReadiness.tone === "ready" ? "1px solid #bbf7d0" : onboardingReadiness.tone === "blocked" ? "1px solid #fde68a" : "1px solid #fed7aa",
            background: onboardingReadiness.tone === "ready" ? "#ecfdf5" : onboardingReadiness.tone === "blocked" ? "#fffbeb" : "#fff7ed",
            borderRadius: 14,
            padding: 16
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 0.08, color: onboardingReadiness.tone === "ready" ? "#166534" : "#92400e", fontWeight: 900 }}>
                Agent Smith v2
              </div>
              <h3 style={{ margin: "4px 0 4px", fontSize: 18, color: "#0f172a" }}>
                Onboarding readiness
              </h3>
              <div style={{ color: onboardingReadiness.tone === "ready" ? "#166534" : "#92400e", fontSize: 13, fontWeight: 900 }}>
                {onboardingReadiness.status}
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={handleCopyAccountSetupSummary}
                disabled={!selectedAccountId}
                style={{
                  border: "1px solid #cbd5e1",
                  background: "white",
                  color: "#334155",
                  borderRadius: 999,
                  padding: "7px 11px",
                  fontSize: 12,
                  fontWeight: 900,
                  cursor: selectedAccountId ? "pointer" : "not-allowed",
                  opacity: selectedAccountId ? 1 : 0.6
                }}
              >
                Copy setup summary
              </button>

              <button
                type="button"
                onClick={handleCopyPromptGenerationBrief}
                disabled={!selectedAccountId}
                style={{
                  border: "1px solid #bfdbfe",
                  background: "#eff6ff",
                  color: "#1d4ed8",
                  borderRadius: 999,
                  padding: "7px 11px",
                  fontSize: 12,
                  fontWeight: 900,
                  cursor: selectedAccountId ? "pointer" : "not-allowed",
                  opacity: selectedAccountId ? 1 : 0.6
                }}
              >
                Copy prompt generation brief
              </button>

              <button
                type="button"
                onClick={handleGeneratePromptPackDraft}
                disabled={!selectedAccountId || isGeneratingPromptPackDraft || promptGenerationIsActive}
                style={{
                  border: "1px solid #93c5fd",
                  background: "#dbeafe",
                  color: "#1e3a8a",
                  borderRadius: 999,
                  padding: "7px 11px",
                  fontSize: 12,
                  fontWeight: 900,
                  cursor: selectedAccountId && !isGeneratingPromptPackDraft && !promptGenerationIsActive ? "pointer" : "not-allowed",
                  opacity: selectedAccountId && !isGeneratingPromptPackDraft && !promptGenerationIsActive ? 1 : 0.6
                }}
              >
                {isGeneratingPromptPackDraft
                  ? "Submitting..."
                  : promptGenerationViewState === "queued"
                    ? "Queued"
                    : promptGenerationViewState === "generating"
                      ? "Generating..."
                      : "Generate draft prompt pack"}
              </button>

              <span style={{
                padding: "7px 11px",
                borderRadius: 999,
                background: onboardingReadiness.tone === "ready" ? "#dcfce7" : "#fef3c7",
                color: onboardingReadiness.tone === "ready" ? "#166534" : "#92400e",
                fontSize: 12,
                fontWeight: 900
              }}>
                {onboardingReadiness.checks.filter((check) => check.ok).length}/{onboardingReadiness.checks.length} checks passing
              </span>
            </div>
          </div>

          {copySetupSummaryStatus ? (
            <div style={{ marginTop: 10, color: copySetupSummaryStatus.toLowerCase().includes("failed") ? "#991b1b" : "#166534", fontSize: 12, fontWeight: 900 }}>
              {copySetupSummaryStatus}
            </div>
          ) : null}

          {copyPromptGenerationBriefStatus ? (
            <div style={{ marginTop: 10, color: copyPromptGenerationBriefStatus.toLowerCase().includes("failed") ? "#991b1b" : "#166534", fontSize: 12, fontWeight: 900 }}>
              {copyPromptGenerationBriefStatus}
            </div>
          ) : null}

          {promptPackDraftStatus ? (
            <div style={{ marginTop: 10, color: promptGenerationFailure || promptGenerationViewState === "unexpected" ? "#991b1b" : "#166534", fontSize: 12, fontWeight: 900 }}>
              {promptPackDraftStatus}
            </div>
          ) : null}

          {promptGenerationFailure ? (
            <div style={{ marginTop: 6, color: "#991b1b", fontSize: 12, lineHeight: 1.45 }}>
              <strong>{promptGenerationFailure.code}</strong>: {promptGenerationFailure.message}
              {promptGenerationFailure.failedAt ? ` · ${new Date(promptGenerationFailure.failedAt).toLocaleString()}` : ""}
            </div>
          ) : null}

          {recoveredPreviousDraftNotice ? (
            <div style={{
              marginTop: 8,
              padding: 12,
              borderRadius: 12,
              border: "1px solid #fde68a",
              background: "#fffbeb",
              color: "#92400e",
              fontSize: 12,
              lineHeight: 1.5
            }}>
              <div style={{ fontWeight: 950 }}>
                {recoveredPreviousDraftNotice.title}
              </div>
              <div style={{ marginTop: 4, fontWeight: 800 }}>
                {recoveredPreviousDraftNotice.message}
              </div>
              <div style={{ marginTop: 4 }}>
                Job status: {recoveredPreviousDraftNotice.jobStatus} · Draft status: {recoveredPreviousDraftNotice.draftStatus}
                {recoveredPreviousDraftNotice.draftCompletedAt ? ` · Draft completed: ${new Date(recoveredPreviousDraftNotice.draftCompletedAt).toLocaleString()}` : ""}
              </div>
            </div>
          ) : null}

          {promptGenerationTransportWarning ? (
            <div style={{ marginTop: 6, color: "#92400e", fontSize: 12, lineHeight: 1.45 }}>
              {promptGenerationTransportWarning}
            </div>
          ) : null}

          {promptPackReviewIntegrityFailure ? (
            <div style={{
              marginTop: 8,
              padding: 12,
              borderRadius: 12,
              border: "1px solid #fecaca",
              background: "#fef2f2",
              color: "#991b1b",
              fontSize: 12,
              lineHeight: 1.5
            }}>
              <div style={{ fontWeight: 950 }}>
                {promptPackReviewIntegrityFailure.code}
              </div>
              <div style={{ marginTop: 4, fontWeight: 800 }}>
                {promptPackReviewIntegrityFailure.message}
              </div>
              {promptPackReviewIntegrityFailure.details.length ? (
                <ul style={{ margin: "8px 0 0 18px", padding: 0 }}>
                  {promptPackReviewIntegrityFailure.details.slice(0, 4).map((detail) => (
                    <li key={detail}>{detail}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}

              {generatedPromptPackDraft?.draft_prompt_pack ? (
                <div style={{
                  marginTop: 14,
                  border: "1px solid #bfdbfe",
                  background: "#eff6ff",
                  borderRadius: 14,
                  padding: 14
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
                    <div>
                      <div style={{ color: "#1e3a8a", fontSize: 13, fontWeight: 900 }}>
                        {generatedPromptPackDisplay?.header || "Draft Prompt Pack Preview · Review Ready"}
                      </div>
                      <div style={{ color: "#475569", fontSize: 12, marginTop: 4 }}>
                        {generatedPromptPackDisplay?.subtext || `${generatedPromptPackDraft.draft_prompt_pack.label} · ${generatedPromptPackDraft.draft_prompt_pack.prompt_count} prompts · Not saved`}
                      </div>
                    </div>

                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <span style={{
                        borderRadius: 999,
                        background: generatedPromptPackDisplay?.isApplied ? "#dcfce7" : "#dbeafe",
                        color: generatedPromptPackDisplay?.isApplied ? "#166534" : "#1d4ed8",
                        padding: "6px 9px",
                        fontSize: 11,
                        fontWeight: 900
                      }}>
                        {generatedPromptPackDisplay?.badge || "Draft only"}
                      </span>
                    </div>
                  </div>

                  {generatedPromptPackReviewSummary ? (
                <div style={{ marginTop: 12, display: "grid", gap: 8 }}>
                  <div style={{ color: "#334155", fontSize: 12, fontWeight: 800, lineHeight: 1.5 }}>
                    Review-ready draft contains {generatedPromptPackReviewSummary.totalPrompts} persisted prompts across {generatedPromptPackReviewSummary.categoryCounts.length} categories.
                  </div>
                  <div style={{ color: "#334155", fontSize: 12, fontWeight: 800, lineHeight: 1.5 }}>
                    {generatedPromptPackReviewSummary.organicCount} organic / {generatedPromptPackReviewSummary.guidedCount} guided
                  </div>
                  {generatedPromptPackReviewSummary.categoryCounts.length ? (
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {generatedPromptPackReviewSummary.categoryCounts.map((entry) => (
                        <span key={entry.category} style={{
                          padding: "4px 8px",
                          borderRadius: 999,
                          background: "#dbeafe",
                          color: "#1d4ed8",
                          fontSize: 11,
                          fontWeight: 900
                        }}>
                          {entry.category}: {entry.count}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}

              {promptGenerationBrief ? (
                <details style={{ marginTop: 12 }}>
                  <summary style={{ cursor: "pointer", color: "#1d4ed8", fontSize: 12, fontWeight: 900 }}>
                    Generation brief used
                  </summary>
                  <pre style={{
                    marginTop: 10,
                    background: "white",
                    border: "1px solid #bfdbfe",
                    borderRadius: 10,
                    padding: 12,
                    color: "#0f172a",
                    fontSize: 11,
                    overflowX: "auto",
                    maxHeight: 320
                  }}>
                    {JSON.stringify(promptGenerationBrief, null, 2)}
                  </pre>
                </details>
              ) : null}

              {generatedPromptPackDraft.draft_prompt_pack.warnings?.length ? (
                <div style={{ marginTop: 10, color: "#92400e", fontSize: 12, fontWeight: 800, lineHeight: 1.5 }}>
                  Warnings: {generatedPromptPackDraft.draft_prompt_pack.warnings.join(" · ")}
                </div>
              ) : null}

              {generatedPromptPackDraft.draft_prompt_pack.summary ? (
                <p style={{ color: "#334155", fontSize: 12, marginTop: 10, marginBottom: 0, lineHeight: 1.6 }}>
                  {generatedPromptPackDraft.draft_prompt_pack.summary}
                </p>
              ) : null}

              <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={() => void handleOpenLatestPromptPackReview()}
                  disabled={isRefreshingLatestPromptPackReview}
                  style={{
                    border: "1px solid #1d4ed8",
                    background: "#dbeafe",
                    color: "#1d4ed8",
                    borderRadius: 999,
                    padding: "9px 14px",
                    fontSize: 13,
                    fontWeight: 900,
                    cursor: isRefreshingLatestPromptPackReview ? "wait" : "pointer",
                    opacity: isRefreshingLatestPromptPackReview ? 0.7 : 1
                  }}
                >
                  {isRefreshingLatestPromptPackReview
                    ? "Loading latest draft..."
                    : generatedPromptPackDisplay?.reviewButtonLabel || "Review Latest Draft"}
                </button>

                <div style={{ color: "#475569", fontSize: 12, fontWeight: 700, alignSelf: "center" }}>
                  {generatedPromptPackDisplay?.supportingText || "Full review happens in the dialog before saving."}
                </div>
              </div>
            </div>
          ) : null}

          <PromptPackReviewDialog
            isOpen={isPromptPackReviewOpen}
            draftPromptPack={generatedPromptPackDraft?.draft_prompt_pack || null}
            canSave={canSaveGeneratedPromptPackDraft}
            hasBeenApplied={Boolean(
              generatedPromptPackDisplay?.isApplied ||
              savedGeneratedPromptPack?.applied_to_account
            )}
            isSaving={isSavingGeneratedPromptPackDraft}
            saveStatus={generatedPromptPackReviewSaveStatus}
            onClose={() => setIsPromptPackReviewOpen(false)}
            onSave={handleSaveGeneratedPromptPackDraft}
          />

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 8, marginTop: 12 }}>
            {onboardingReadiness.checks.map((check) => (
              <div key={check.key} style={{ padding: 10, borderRadius: 10, background: "white", border: check.ok ? "1px solid #bbf7d0" : "1px solid #fed7aa" }}>
                <div style={{ color: check.ok ? "#166534" : "#92400e", fontSize: 12, fontWeight: 900 }}>
                  {check.ok ? "✓" : "!"} {check.label}
                </div>
                <div style={{ marginTop: 4, color: "#64748b", fontSize: 12, lineHeight: 1.4 }}>
                  {check.help}
                </div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid rgba(15, 23, 42, 0.08)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <div>
                <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 0.08, color: "#475569", fontWeight: 900 }}>
                  Competitive Setup Intelligence
                </div>
                <div style={{ marginTop: 3, color: "#64748b", fontSize: 12 }}>
                  Optional governance signals that improve prompt quality, scoring, and competitor interpretation.
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <span style={{
                  padding: "7px 10px",
                  borderRadius: 999,
                  background:
                    intelligenceReadinessSummary.tone === "strong"
                      ? "#dbeafe"
                      : intelligenceReadinessSummary.tone === "partial"
                        ? "#e0f2fe"
                        : intelligenceReadinessSummary.tone === "light"
                          ? "#fef3c7"
                          : intelligenceReadinessSummary.tone === "error"
                            ? "#fee2e2"
                            : "#f1f5f9",
                  color:
                    intelligenceReadinessSummary.tone === "strong"
                      ? "#1d4ed8"
                      : intelligenceReadinessSummary.tone === "partial"
                        ? "#0369a1"
                        : intelligenceReadinessSummary.tone === "light"
                          ? "#92400e"
                          : intelligenceReadinessSummary.tone === "error"
                            ? "#991b1b"
                            : "#475569",
                  fontSize: 12,
                  fontWeight: 900
                }}>
                  {intelligenceReadinessSummary.status} · {intelligenceReadinessSummary.passing}/{intelligenceReadinessSummary.total}
                </span>

                <button
                  type="button"
                  onClick={() => loadIntelligenceReadiness(selectedAccountId)}
                  disabled={!selectedAccountId || isLoadingIntelligenceReadiness}
                  style={{
                    border: "1px solid #cbd5e1",
                    background: "white",
                    color: "#334155",
                    borderRadius: 999,
                    padding: "7px 10px",
                    fontSize: 12,
                    fontWeight: 900,
                    cursor: !selectedAccountId || isLoadingIntelligenceReadiness ? "not-allowed" : "pointer",
                    opacity: !selectedAccountId || isLoadingIntelligenceReadiness ? 0.6 : 1
                  }}
                >
                  {isLoadingIntelligenceReadiness ? "Refreshing..." : "Refresh intelligence"}
                </button>
              </div>
            </div>

            {intelligenceReadiness?.error ? (
              <div style={{ marginTop: 10, color: "#991b1b", fontSize: 12, fontWeight: 800 }}>
                {intelligenceReadiness.error}
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 8, marginTop: 12 }}>
                {[
                  {
                    key: "market_profile",
                    label: "Approved market profile",
                    ok: String(intelligenceReadiness?.marketProfile?.status || "").toLowerCase() === "approved",
                    help: intelligenceReadiness?.marketProfile
                      ? String(intelligenceReadiness.marketProfile.status || "").toLowerCase() === "approved"
                        ? `Approved · ${intelligenceReadiness.marketProfile.status || "approved"}`
                        : `Present · ${intelligenceReadiness.marketProfile.status || "saved"} (approval required)`
                      : "Add and approve a market profile before prompt generation."
                  },
                  {
                    key: "brand_guidance",
                    label: "Brand guidance",
                    ok: Boolean(intelligenceReadiness?.brandReview),
                    help: intelligenceReadiness?.brandReview
                      ? `Latest review: ${intelligenceReadiness.brandReview.review_status || "saved"}`
                      : "Generate brand seeds when entity normalization matters."
                  },
                  {
                    key: "entity_roles",
                    label: "Entity roles",
                    ok: Number(intelligenceReadiness?.entityRoleCount || 0) > 0,
                    help: `${intelligenceReadiness?.entityRoleCount || 0} active mappings`
                  },
                  {
                    key: "competitors",
                    label: "Competitor mappings",
                    ok: Number(intelligenceReadiness?.directCompetitorCount || 0) > 0,
                    help: `${intelligenceReadiness?.directCompetitorCount || 0} direct competitors`
                  },
                  {
                    key: "suppressions",
                    label: "Suppressions",
                    ok: Number(intelligenceReadiness?.suppressionCount || 0) > 0,
                    help: `${intelligenceReadiness?.suppressionCount || 0} suppression rules`
                  },
                  {
                    key: "customer_entities",
                    label: "Customer entity",
                    ok: Number(intelligenceReadiness?.customerEntityCount || 0) > 0,
                    help: `${intelligenceReadiness?.customerEntityCount || 0} customer/self mappings`
                  }
                ].map((check) => (
                  <div key={check.key} style={{ padding: 10, borderRadius: 10, background: "white", border: check.ok ? "1px solid #bfdbfe" : "1px solid #e5e7eb" }}>
                    <div style={{ color: check.ok ? "#1d4ed8" : "#64748b", fontSize: 12, fontWeight: 900 }}>
                      {check.ok ? "✓" : "·"} {check.label}
                    </div>
                    <div style={{ marginTop: 4, color: "#64748b", fontSize: 12, lineHeight: 1.4 }}>
                      {check.help}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>


        <div
          style={{
            marginTop: 16,
            border: "1px solid #e5e7eb",
            background: "#ffffff",
            borderRadius: 14,
            padding: 16
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: 16,
              fontWeight: 700,
              color: "#0f172a"
            }}
          >
            Brand Dictionary / Normalization
          </h3>
          <p
            style={{
              marginTop: 6,
              marginBottom: 12,
              color: "#64748b",
              fontSize: 13,
              lineHeight: 1.45
            }}
          >
            Generate reviewable brand aliases, suppressions, and entities that need human review before the first run.
          </p>

          <button
            type="button"
            onClick={handleGenerateBrandSeeds}
            disabled={!selectedAccountId || brandSeedLoading}
            style={{
              border: "1px solid #0f172a",
              background: brandSeedLoading || !selectedAccountId ? "#cbd5e1" : "#0f172a",
              color: "#ffffff",
              borderRadius: 12,
              padding: "10px 14px",
              fontSize: 13,
              fontWeight: 700,
              cursor: brandSeedLoading || !selectedAccountId ? "not-allowed" : "pointer"
            }}
          >
            {brandSeedLoading ? "Generating Brand Seeds..." : "Generate Brand Seeds"}
          </button>

          <button
            type="button"
            onClick={() => selectedAccountId && loadLatestBrandSeedReview(selectedAccountId)}
            disabled={!selectedAccountId || brandSeedReviewLoading}
            style={{
              marginLeft: 10,
              border: "1px solid #cbd5e1",
              background: "#ffffff",
              color: "#0f172a",
              borderRadius: 12,
              padding: "10px 14px",
              fontSize: 13,
              fontWeight: 700,
              cursor: !selectedAccountId || brandSeedReviewLoading ? "not-allowed" : "pointer"
            }}
          >
            {brandSeedReviewLoading ? "Loading Review..." : "Load Latest Review"}
          </button>

          {onboardingMode === "internal" && (
            <button
              type="button"
              onClick={handleApplyApprovedBrandSeedReviewItems}
              disabled={!selectedAccountId || !brandSeedReview || brandSeedApplyLoading}
              style={{
                marginLeft: 10,
                border: "1px solid #0284c7",
                background: !selectedAccountId || !brandSeedReview || brandSeedApplyLoading ? "#93c5fd" : "#0284c7",
                color: "#ffffff",
                borderRadius: 12,
                padding: "10px 14px",
                fontSize: 13,
                fontWeight: 800,
                cursor: !selectedAccountId || !brandSeedReview || brandSeedApplyLoading ? "not-allowed" : "pointer"
              }}
            >
              {brandSeedApplyLoading ? "Applying Approved Items..." : "Apply Approved Items"}
            </button>
          )}

          {brandSeedApplyMessage ? (
            <p style={{ marginTop: 10, marginBottom: 0, color: "#166534", fontSize: 13, fontWeight: 800 }}>
              {brandSeedApplyMessage}
            </p>
          ) : null}

          {brandSeedApplyError ? (
            <p style={{ marginTop: 10, marginBottom: 0, color: "#b91c1c", fontSize: 13, fontWeight: 800 }}>
              {brandSeedApplyError}
            </p>
          ) : null}

          {brandSeedError ? (
            <p
              style={{
                marginTop: 10,
                marginBottom: 0,
                color: "#b91c1c",
                fontSize: 13
              }}
            >
              {brandSeedError}
            </p>
          ) : null}

          {brandSeedItemUpdateError ? (
            <p style={{ marginTop: 12, color: "#b91c1c", fontSize: 13 }}>
              {brandSeedItemUpdateError}
            </p>
          ) : null}

          {brandSeedReviewLoading ? (
            <p style={{ marginTop: 12, color: "#64748b", fontSize: 13 }}>
              Loading latest brand seed review...
            </p>
          ) : null}

          {brandSeedReviewError ? (
            <p style={{ marginTop: 12, color: "#b91c1c", fontSize: 13 }}>
              {brandSeedReviewError}
            </p>
          ) : null}

          {brandSeedReview ? (
            <div style={{ marginTop: 14 }}>
              <div
                style={{
                  padding: 12,
                  borderRadius: 12,
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  color: "#334155",
                  fontSize: 13,
                  lineHeight: 1.45
                }}
              >
                <strong>Latest review batch:</strong> {brandSeedReview.id}
                <br />
                Status: {brandSeedReview.review_status} · Seeds: {brandSeedReview.seed_count} · Suppressions: {brandSeedReview.suppression_count} · Needs review: {brandSeedReview.needs_review_count}
                <br />
                Created: {brandSeedReview.created_at}
              </div>

              {brandSeedReview.seed_payload?.warnings?.length ? (
                <div
                  style={{
                    marginTop: 12,
                    padding: 12,
                    borderRadius: 12,
                    background: "#fffbeb",
                    border: "1px solid #fde68a",
                    color: "#92400e",
                    fontSize: 13
                  }}
                >
                  <strong>Warnings</strong>
                  <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
                    {brandSeedReview.seed_payload.warnings.map((warning: string, index: number) => (
                      <li key={`${warning}-${index}`}>{warning}</li>
                    ))}
                  </ul>
                </div>
              ) : null}

              <BrandSeedReviewTable
                title="Brand seed candidates"
                rows={brandSeedReview.seed_payload?.brand_dictionary_seeds || []}
                type="seed"
                updatingItemId={brandSeedItemUpdatingId}
                onUpdateItem={handleUpdateBrandSeedReviewItem}
                bulkUpdatingType={brandSeedBulkUpdatingType}
                onBulkUpdate={handleBulkUpdateBrandSeedReviewItems}
                              reviewBatch={brandSeedReview}
/>

              <BrandSeedReviewTable
                title="Suppression candidates"
                rows={brandSeedReview.seed_payload?.suppressions || []}
                type="suppression"
                updatingItemId={brandSeedItemUpdatingId}
                onUpdateItem={handleUpdateBrandSeedReviewItem}
                bulkUpdatingType={brandSeedBulkUpdatingType}
                onBulkUpdate={handleBulkUpdateBrandSeedReviewItems}
                              reviewBatch={brandSeedReview}
/>

              <BrandSeedReviewTable
                title="Needs human review"
                rows={brandSeedReview.seed_payload?.needs_review || []}
                type="needs_review"
                updatingItemId={brandSeedItemUpdatingId}
                onUpdateItem={handleUpdateBrandSeedReviewItem}
                bulkUpdatingType={brandSeedBulkUpdatingType}
                onBulkUpdate={handleBulkUpdateBrandSeedReviewItems}
                              reviewBatch={brandSeedReview}
/>

              {brandSeedResult ? (
                <details style={{ marginTop: 14 }}>
                  <summary style={{ cursor: "pointer", fontSize: 13, fontWeight: 800, color: "#334155" }}>
                    Raw latest generation response
                  </summary>
                  <pre
                    style={{
                      marginTop: 10,
                      padding: 12,
                      borderRadius: 12,
                      background: "#f8fafc",
                      border: "1px solid #e5e7eb",
                      overflowX: "auto",
                      fontSize: 12,
                      maxHeight: 420
                    }}
                  >
                    {JSON.stringify(brandSeedResult, null, 2)}
                  </pre>
                </details>
              ) : null}
            </div>
          ) : null}
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              selectedAccountId
                ? "minmax(0, 1fr)"
                : "minmax(0, 1fr) 320px",
            gap: 18,
            alignItems: "start",
          }}
        >
          <main style={{ display: "grid", gap: 16 }}>
            <section style={cardStyle}>
              <SectionTitle eyebrow="Step 1" title="Account basics" />
              <div style={gridTwoStyle}>
                <div style={{ gridColumn: "1 / -1" }}>
                  <div style={{ display: "grid", gap: 6, fontSize: 13, fontWeight: 800, color: "#334155" }}>
                    <div>Select existing account</div>
                    <AccountSelector
                      accounts={accountOptions}
                      selectedAccountId={selectedAccountId}
                      isLoading={isLoadingAccounts}
                      onSelect={handleSelectExistingAccount}
                    />
                  </div>

                  <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                    <button
                      type="button"
                      onClick={handleStartNewAccount}
                      style={{
                        border: "1px solid #cbd5e1",
                        background: "white",
                        color: "#334155",
                        borderRadius: 999,
                        padding: "8px 12px",
                        fontSize: 13,
                        fontWeight: 800,
                        cursor: "pointer",
                      }}
                    >
                      Start New Account
                    </button>

                    <button
                      type="button"
                      onClick={refreshAccountOptions}
                      disabled={isLoadingAccounts}
                      style={isLoadingAccounts ? disabledButtonStyle : {
                        border: "1px solid #cbd5e1",
                        background: "white",
                        color: "#334155",
                        borderRadius: 999,
                        padding: "8px 12px",
                        fontSize: 13,
                        fontWeight: 800,
                        cursor: "pointer",
                      }}
                    >
                      Refresh Accounts
                    </button>
                  </div>

                  {accountListStatus ? (
                    <div style={{ marginTop: 8, fontSize: 13, color: "#475569" }}>
                      {accountListStatus}
                    </div>
                  ) : null}
                </div>

                <Field label="Account name">
                  <input
                    value={accountName}
                    onChange={(event) => setAccountName(event.target.value)}
                    placeholder="Peak Auto Group"
                    style={inputStyle}
                  />
                </Field>

                <Field label="Website">
                  <input
                    value={website}
                    onChange={(event) => setWebsite(event.target.value)}
                    placeholder="https://example.com"
                    style={inputStyle}
                  />
                </Field>

                <Field label="Status">
                  <select
                    value={accountStatus}
                    onChange={(event) => setAccountStatus(event.target.value)}
                    style={inputStyle}
                  >
                    <option value="demo">Demo</option>
                    <option value="active">Active / Customer</option>
                    <option value="inactive">Inactive</option>
                    <option value="test">Test</option>
                  </select>
                </Field>

                <Field label="Category">
                  <input
                    value={category}
                    onChange={(event) => setCategory(event.target.value)}
                    placeholder="automotive_dealership"
                    style={inputStyle}
                  />
                </Field>

                <Field label="Account type">
                  <select
                    value={accountType}
                    onChange={(event) => setAccountType(event.target.value as "competitive" | "local" | "retail")}
                    style={inputStyle}
                  >
                    <option value="competitive">Competitive</option>
                    <option value="local">Local</option>
                    <option value="retail">Retail</option>
                  </select>
                </Field>

                <Field label="Business name">
                  <input
                    value={businessName}
                    onChange={(event) => setBusinessName(event.target.value)}
                    placeholder={accountName || "Business display name"}
                    style={inputStyle}
                  />
                </Field>

                {accountType === "local" ? (
                  <>
                    <Field label="Market">
                      <input
                        value={market}
                        onChange={(event) => setMarket(event.target.value)}
                        placeholder="Lexington, KY"
                        style={inputStyle}
                      />
                    </Field>

                    <Field label="Market scope">
                      <select
                        value={marketScope}
                        onChange={(event) => setMarketScope(event.target.value)}
                        style={inputStyle}
                      >
                        <option value="">Select market scope</option>
                        <option value="local">Local</option>
                        <option value="multi_location">Multi-location</option>
                        <option value="regional">Regional</option>
                      </select>
                    </Field>

                    <Field label="Primary geo type">
                      <select
                        value={primaryGeoType}
                        onChange={(event) => setPrimaryGeoType(event.target.value)}
                        style={inputStyle}
                      >
                        <option value="">Select geo type</option>
                        <option value="city">City</option>
                        <option value="metro">Metro</option>
                        <option value="county">County</option>
                        <option value="state">State</option>
                        <option value="region">Region</option>
                        <option value="radius">Radius</option>
                      </select>
                    </Field>

                    <Field label="Primary geo value">
                      <input
                        value={primaryGeoValue}
                        onChange={(event) => setPrimaryGeoValue(event.target.value)}
                        placeholder="Lexington, KY"
                        style={inputStyle}
                      />
                    </Field>

                    <Field label="Secondary geo / service areas">
                      <input
                        value={secondaryGeoValue}
                        onChange={(event) => setSecondaryGeoValue(event.target.value)}
                        placeholder="Nicholasville, Georgetown, Richmond"
                        style={inputStyle}
                      />
                    </Field>

                    <Field label="Geo prompting">
                      <select
                        value={geoPromptingEnabled ? "true" : "false"}
                        onChange={(event) => setGeoPromptingEnabled(event.target.value === "true")}
                        style={inputStyle}
                      >
                        <option value="false">Disabled</option>
                        <option value="true">Enabled</option>
                      </select>
                    </Field>

                    <div style={{ gridColumn: "1 / -1" }}>
                      <Field label="Geo notes">
                        <textarea
                          value={geoNotes}
                          onChange={(event) => setGeoNotes(event.target.value)}
                          placeholder="Optional local notes, service area details, or market caveats."
                          style={{ ...inputStyle, minHeight: 86, resize: "vertical" }}
                        />
                      </Field>
                    </div>
                  </>
                ) : null}

                {accountType === "retail" ? (
                  <div style={{ gridColumn: "1 / -1", padding: 12, borderRadius: 12, background: "#fffbeb", border: "1px solid #fde68a", color: "#92400e", fontSize: 13, lineHeight: 1.45 }}>
                    Retail setup is partially supported. Add category and retail prompt packs, then verify manually before running.
                  </div>
                ) : null}
              </div>

              <div style={{ display: "flex", gap: 10, marginTop: 16, alignItems: "center", flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={handleSaveOnboardingProfile}
                  disabled={isSaving}
                  style={isSaving ? disabledButtonStyle : primaryButtonStyle}
                >
                  {isSaving ? "Saving..." : selectedAccountId ? "Save Account Changes" : "Save Account"}
                </button>

                {selectedAccountId ? (
                  <span style={{ color: "#166534", fontSize: 13, fontWeight: 800 }}>
                    Account ID: {selectedAccountId}
                  </span>
                ) : (
                  <span style={{ color: "#64748b", fontSize: 13 }}>
                    Save account details before attaching prompt packs.
                  </span>
                )}
              </div>

              {saveStatus ? (
                <div style={{ marginTop: 12, padding: 12, borderRadius: 12, background: "#ecfdf5", border: "1px solid #bbf7d0", color: "#166534", fontSize: 13, lineHeight: 1.45 }}>
                  {saveStatus}
                </div>
              ) : null}
            </section>

            <section style={cardStyle}>
              <SectionTitle eyebrow="Step 2" title="Users & access" />
              <Field label="Access preset">
                <select
                  value={accessPresetKey}
                  onChange={(event) => setAccessPresetKey(event.target.value as AccountAccessPresetKey)}
                  style={inputStyle}
                >
                  {ACCOUNT_ACCESS_PRESETS.map((preset) => (
                    <option key={preset.key} value={preset.key}>
                      {preset.label}
                    </option>
                  ))}
                </select>
              </Field>

              <div style={{ marginTop: 12, padding: 14, borderRadius: 12, background: "#f8fafc", border: "1px solid #e2e8f0" }}>
                <div style={{ fontWeight: 800 }}>{selectedPreset.label}</div>
                <div style={{ marginTop: 4, color: "#64748b", fontSize: 13 }}>
                  {selectedPreset.description}
                </div>
                <div style={{ marginTop: 10, fontSize: 13, color: "#334155" }}>
                  role: <strong>{selectedPreset.role}</strong> · OE:{" "}
                  <strong>{selectedPreset.can_view_opportunity_engine ? "enabled" : "disabled"}</strong>
                </div>
              </div>

              <Field label="Batch users" help="Paste one email per line, or comma-separated. V1 preview only.">
                <textarea
                  value={batchUsers}
                  onChange={(event) => setBatchUsers(event.target.value)}
                  placeholder={"jane@example.com\njohn@example.com"}
                  style={{ ...inputStyle, minHeight: 110, resize: "vertical" }}
                />
              </Field>

              <div style={{ fontSize: 13, color: "#475569" }}>
                Parsed users: <strong>{parsedUsers.length}</strong>
              </div>
            </section>

            <section style={cardStyle}>
              {selectedAccountId ? (
                <CompetitiveSetupIntelligence
                  accountId={selectedAccountId}
                  isPartnerMode={onboardingMode === "partner"}
                  onChanged={() => loadIntelligenceReadiness(selectedAccountId)}
                />
              ) : (
                <div
                  style={{
                    border: "1px solid #c7d2fe",
                    background: "#f8fafc",
                    borderRadius: 16,
                    padding: 18,
                    marginTop: 24,
                    marginBottom: 24,
                  }}
                >
                  <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 0.08, color: "#4f46e5", fontWeight: 800 }}>
                    Competitive Setup Intelligence
                  </div>
                  <h2 style={{ margin: "4px 0 6px", fontSize: 22, color: "#0f172a" }}>
                    Save or select an account to configure market profile and entity roles
                  </h2>
                  <p style={{ margin: 0, color: "#475569", fontSize: 14 }}>
                    Agentic market setup is available for all account types once the account has an ID.
                  </p>

                <div
                  style={{
                    display: "flex",
                    gap: 10,
                    alignItems: "center",
                    flexWrap: "wrap",
                    margin: "10px 0 12px"
                  }}
                >
                  <button
                    type="button"
                    disabled={brandSeedApplyLoading}
                    onClick={handleApplyApprovedBrandSeedReviewItems}
                    style={{
                      border: "1px solid #0284c7",
                      background: "#0284c7",
                      color: "#ffffff",
                      borderRadius: 999,
                      padding: "8px 12px",
                      fontSize: 12,
                      fontWeight: 900,
                      opacity: brandSeedApplyLoading ? 0.65 : 1,
                      cursor: brandSeedApplyLoading ? "not-allowed" : "pointer"
                    }}
                  >
                    {brandSeedApplyLoading ? "Applying..." : "Apply Approved Items"}
                  </button>

                  {brandSeedApplyMessage ? (
                    <span style={{ color: "#166534", fontSize: 12, fontWeight: 800 }}>
                      {brandSeedApplyMessage}
                    </span>
                  ) : null}

                  {brandSeedApplyError ? (
                    <span style={{ color: "#b91c1c", fontSize: 12, fontWeight: 800 }}>
                      {brandSeedApplyError}
                    </span>
                  ) : null}
                </div>



                </div>
              )}

              <SectionTitle eyebrow="Step 3" title="Account-specific prompt generation" />

              <p style={{ margin: "0 0 16px", color: "#64748b", lineHeight: 1.5 }}>
                Use the generated account-specific workflow first: confirm readiness, review the active prompt setup, generate a draft, review and save it, then apply before running. The legacy prompt-pack stack remains available below for manual testing and troubleshooting.
              </p>

              {activePromptSetupSummary ? (
                <div style={{
                  marginBottom: 14,
                  padding: 14,
                  borderRadius: 14,
                  border: "1px solid #bbf7d0",
                  background: activePromptSetupSummary.activeStatusLabel === "Ready" ? "#ecfdf5" : "#fff7ed",
                  color: activePromptSetupSummary.activeStatusLabel === "Ready" ? "#14532d" : "#9a3412"
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
                    <div>
                      <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 0.08, fontWeight: 900 }}>
                        Active Prompt Setup
                      </div>
                      <div style={{ marginTop: 4, fontSize: 13, fontWeight: 900 }}>
                        {activePromptSetupSummary.activeStatusLabel}
                        {activePromptSetupSummary.sourceLabel ? ` · ${activePromptSetupSummary.sourceLabel}` : ""}
                      </div>
                    </div>

                    <span style={{
                      padding: "6px 10px",
                      borderRadius: 999,
                      background: activePromptSetupSummary.activeStatusLabel === "Ready" ? "#dcfce7" : "#ffedd5",
                      color: activePromptSetupSummary.activeStatusLabel === "Ready" ? "#166534" : "#9a3412",
                      fontSize: 11,
                      fontWeight: 900
                    }}>
                      {activePromptSetupSummary.activePromptCount} active prompts
                    </span>
                  </div>

                  <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 8 }}>
                    <MiniInfoChip label="Active count" value={activePromptSetupSummary.activePromptCount} />
                    <MiniInfoChip label="Organic" value={activePromptSetupSummary.organicCount ?? "—"} />
                    <MiniInfoChip label="Guided" value={activePromptSetupSummary.guidedCount ?? "—"} />
                    <MiniInfoChip label="Category count" value={activePromptSetupSummary.categoryCount ?? "—"} />
                  </div>

                  <div style={{ marginTop: 10, display: "grid", gap: 6, fontSize: 12, lineHeight: 1.5 }}>
                    <div>
                      Active pack: <strong>{activePromptSetupSummary.activePackLabel || "—"}</strong>
                      {activePromptSetupSummary.activePackId ? ` · ID: ${activePromptSetupSummary.activePackId}` : ""}
                    </div>
                    <div>
                      Last applied/generated: <strong>{formatActivePromptTimestamp(activePromptSetupSummary.lastEventAt) || "—"}</strong>
                    </div>
                  </div>

                  {activePromptSetupSummary.categoryBreakdown.length ? (
                    <div style={{ marginTop: 10, display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {activePromptSetupSummary.categoryBreakdown.map((entry) => (
                        <span key={entry.category} style={{
                          padding: "4px 8px",
                          borderRadius: 999,
                          background: "#dbeafe",
                          color: "#1d4ed8",
                          fontSize: 11,
                          fontWeight: 900
                        }}>
                          {entry.category}: {entry.count}
                        </span>
                      ))}
                    </div>
                  ) : null}

                  {activePromptSetupSummary.promptSets.length > 1 ? (
                    <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
                      {activePromptSetupSummary.promptSets.map((set, index) => (
                        <div key={`${set.prompt_pack_id || set.prompt_set_id || set.prompt_set || index}-${index}`} style={{
                          padding: 10,
                          borderRadius: 12,
                          background: "rgba(255,255,255,0.65)",
                          border: "1px solid rgba(148,163,184,0.25)"
                        }}>
                          <div style={{ fontSize: 13, fontWeight: 900 }}>
                            {set.prompt_pack_label || set.prompt_set || "Unlabeled active set"}
                          </div>
                          <div style={{ marginTop: 4, fontSize: 12, lineHeight: 1.5 }}>
                            {toFiniteNumber(set.active_prompt_count) ?? "—"} active prompts
                            {toFiniteNumber(set.organic_count) !== null ? ` · ${toFiniteNumber(set.organic_count)} organic` : ""}
                            {toFiniteNumber(set.guided_count) !== null ? ` · ${toFiniteNumber(set.guided_count)} guided` : ""}
                            {toFiniteNumber(set.geo_specific_count) !== null ? ` · ${toFiniteNumber(set.geo_specific_count)} geo-specific` : ""}
                            {set.last_created_at ? ` · ${formatActivePromptTimestamp(set.last_created_at)}` : ""}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : selectedAccountId ? (
                <div style={{ marginBottom: 14, padding: 12, borderRadius: 12, background: "#f8fafc", border: "1px solid #e2e8f0", color: "#475569", fontSize: 13, lineHeight: 1.5 }}>
                  Loading active prompt setup for the selected account…
                </div>
              ) : null}

              <details style={{ marginTop: 8, padding: 14, borderRadius: 14, border: "1px solid #e2e8f0", background: "#f8fafc" }}>
                <summary style={{ cursor: "pointer", listStyle: "none", fontWeight: 900, color: "#0f172a" }}>
                  Advanced / Legacy Prompt Tools
                </summary>
                <div style={{ marginTop: 8, color: "#64748b", fontSize: 13, lineHeight: 1.5 }}>
                  Use these tools only for manual prompt-pack testing, legacy packs, localization previews, or troubleshooting. New accounts should use generated account-specific prompt packs.
                </div>

                {!isLoadingPromptPacks && promptPacks.length === 0 ? (
                  <div style={{ marginTop: 12, padding: 12, borderRadius: 12, background: "#fff7ed", border: "1px solid #fed7aa", color: "#9a3412", fontSize: 13, lineHeight: 1.45 }}>
                    No prompt packs loaded. Save/select an account and refresh the page. If this continues, check the prompt-pack API route.
                  </div>
                ) : null}

                <div style={{ display: "grid", gap: 12, marginTop: 12 }}>
                  {promptPackStackIds.map((packId, index) => {
                    const selectedIdsInOtherRows = promptPackStackIds.filter(
                      (currentPackId, currentIndex) => currentIndex !== index && Boolean(currentPackId)
                    );

                    const selectedPackForRow =
                      promptPacks.find((pack) => pack.id === packId) || null;

                    const selectedPackSafety = selectedPackForRow
                      ? getPromptPackSafety({
                          accountName,
                          businessName,
                          displayName: accountOptions.find((account) => account.account_id === selectedAccountId)?.display_name,
                          packKey: selectedPackForRow.pack_key,
                          packLabel: selectedPackForRow.label,
                          sourceAccountName: selectedPackForRow.source_account_name,
                          categoryKey: selectedPackForRow.category_key,
                          productType: selectedPackForRow.product_type,
                        })
                      : null;

                    return (
                      <div
                        key={`${index}-${packId || "empty"}`}
                        style={{
                          padding: 14,
                          borderRadius: 14,
                          border: "1px solid #dbeafe",
                          background: "#f8fafc",
                        }}
                      >
                        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto", gap: 10, alignItems: "end" }}>
                          <Field label={`Prompt pack ${index + 1}`}>
                            <select
                              value={packId}
                              onChange={(event) => handlePromptPackStackChange(index, event.target.value)}
                              disabled={isLoadingPromptPacks}
                              style={{
                                ...inputStyle,
                                cursor: isLoadingPromptPacks ? "not-allowed" : "pointer",
                              }}
                            >
                              <option value="">
                                {isLoadingPromptPacks
                                  ? "Loading prompt packs..."
                                  : promptPacks.length === 0
                                    ? "No prompt packs loaded"
                                    : "Select a prompt pack"}
                              </option>
                              {promptPackOptions
                                .filter((pack) => !selectedIdsInOtherRows.includes(pack.id))
                                .map((pack) => (
                                  <option key={pack.id} value={pack.id}>
                                    {pack.fit_label} · {pack.label} · {pack.actual_prompt_count}/{pack.expected_prompt_count}
                                  </option>
                                ))}
                            </select>
                          </Field>

                          <button
                            type="button"
                            onClick={() => handleRemovePromptPackStackRow(index)}
                            disabled={promptPackStackIds.length === 1 && !packId}
                            style={{
                              border: "1px solid #fecaca",
                              background: "white",
                              color: "#991b1b",
                              borderRadius: 999,
                              padding: "9px 12px",
                              fontSize: 13,
                              fontWeight: 800,
                              cursor: promptPackStackIds.length === 1 && !packId ? "not-allowed" : "pointer",
                              opacity: promptPackStackIds.length === 1 && !packId ? 0.5 : 1,
                              marginBottom: 2,
                            }}
                          >
                            Remove
                          </button>
                        </div>

                        {selectedPackForRow ? (
                          <div style={{ marginTop: 10, padding: 12, borderRadius: 12, background: "#eff6ff", border: "1px solid #bfdbfe" }}>
                            <div style={{ fontWeight: 800 }}>{selectedPackForRow.label}</div>
                            <div style={{ marginTop: 6, color: "#1e3a8a", fontSize: 13, lineHeight: 1.6 }}>
                              Product: <strong>{selectedPackForRow.product_type}</strong>
                              <br />
                              Category: <strong>{selectedPackForRow.category_label || selectedPackForRow.category_key}</strong>
                              <br />
                              Prompts: <strong>{selectedPackForRow.actual_prompt_count}</strong> active / expected{" "}
                              <strong>{selectedPackForRow.expected_prompt_count}</strong>
                              <br />
                              Source: <strong>{selectedPackForRow.source_account_name || "Prompt Pack Catalog"}</strong>
                              <br />
                              Pack key: <strong>{selectedPackForRow.pack_key}</strong>
                              {selectedPackForRow.source_account_id ? (
                                <>
                                  <br />
                                  Type: <strong>Account custom</strong>
                                </>
                              ) : null}
                            </div>
                            {selectedPackSafety && selectedPackSafety.status !== "safe" ? (
                              <div
                                style={{
                                  marginTop: 8,
                                  padding: 10,
                                  borderRadius: 10,
                                  background: selectedPackSafety.status === "blocked" ? "#fef2f2" : "#fffbeb",
                                  border: selectedPackSafety.status === "blocked" ? "1px solid #fecaca" : "1px solid #fde68a",
                                  color: selectedPackSafety.status === "blocked" ? "#991b1b" : "#92400e",
                                  fontSize: 13,
                                  lineHeight: 1.45,
                                  fontWeight: 800,
                                }}
                              >
                                {selectedPackSafety.title}: {selectedPackSafety.message}
                                {selectedPackSafety.reasons.length > 0 ? (
                                  <ul style={{ margin: "8px 0 0 18px", padding: 0 }}>
                                    {selectedPackSafety.reasons.map((reason) => (
                                      <li key={reason}>{reason}</li>
                                    ))}
                                  </ul>
                                ) : null}
                              </div>
                            ) : null}

                            {selectedPackForRow.product_type && accountType && selectedPackForRow.product_type !== accountType ? (
                              <div style={{ marginTop: 8, padding: 10, borderRadius: 10, background: "#fffbeb", border: "1px solid #fde68a", color: "#92400e", fontSize: 13, fontWeight: 800 }}>
                                Product mismatch: this pack is {selectedPackForRow.product_type}, but the selected account is {accountType}.
                              </div>
                            ) : null}
                            {selectedPackForRow.notes ? (
                              <div style={{ marginTop: 8, color: "#334155", fontSize: 13, lineHeight: 1.5 }}>
                                {selectedPackForRow.notes}
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>

                <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 14 }}>
                  <button
                    type="button"
                    onClick={handleAddPromptPackStackRow}
                    disabled={promptPackStackIds.length >= promptPacks.length}
                    style={{
                      border: "1px solid #cbd5e1",
                      background: "white",
                      color: "#334155",
                      borderRadius: 999,
                      padding: "9px 13px",
                      fontSize: 13,
                      fontWeight: 800,
                      cursor: promptPackStackIds.length >= promptPacks.length ? "not-allowed" : "pointer",
                      opacity: promptPackStackIds.length >= promptPacks.length ? 0.6 : 1,
                    }}
                  >
                    + Add another prompt pack
                  </button>

                  <span style={{ color: projectedPromptStackCount > 210 ? "#92400e" : "#475569", fontSize: 13, fontWeight: 800 }}>
                    Projected active prompts: {projectedPromptStackCount}
                  </span>
                </div>

                {projectedPromptStackCount > 210 ? (
                  <div style={{ marginTop: 12, padding: 12, borderRadius: 12, background: "#fffbeb", border: "1px solid #fde68a", color: "#92400e", fontSize: 13, lineHeight: 1.45 }}>
                    This stack is above 210 prompts. It can be useful for larger accounts, but review before running.
                  </div>
                ) : null}

                {hasBlockedPromptPackStack ? (
                  <div style={{ marginTop: 12, padding: 12, borderRadius: 12, background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b", fontSize: 13, lineHeight: 1.45, fontWeight: 800 }}>
                    Agent Smith has blocked the selected prompt pack stack. Generate and audit an account-specific custom pack before applying or running this account.
                  </div>
                ) : null}

                <div style={{ marginTop: 16, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                  <button
                    type="button"
                    onClick={handlePreviewPromptPack}
                    disabled={
                      selectedPromptPackStack.length !== 1 ||
                      isLoadingPromptPackPreview
                    }
                    style={
                      selectedPromptPackStack.length !== 1 || isLoadingPromptPackPreview
                        ? disabledButtonStyle
                        : {
                            ...primaryButtonStyle,
                            background: "#0369a1",
                          }
                    }
                  >
                    {isLoadingPromptPackPreview ? "Loading Preview..." : "Preview Selected Pack"}
                  </button>

                  <button
                    type="button"
                    onClick={handlePreviewLocalizedPromptPack}
                    disabled={
                      !selectedAccountId ||
                      selectedPromptPackStack.length !== 1 ||
                      isPreviewingLocalizedPromptPack
                    }
                    style={
                      !selectedAccountId ||
                      selectedPromptPackStack.length !== 1 ||
                      isPreviewingLocalizedPromptPack
                        ? disabledButtonStyle
                        : {
                            ...primaryButtonStyle,
                            background: "#0f766e",
                          }
                    }
                  >
                    {isPreviewingLocalizedPromptPack ? "Previewing Localized..." : "Preview Localized Pack"}
                  </button>

                  <button
                    type="button"
                    onClick={handleAuditPromptPack}
                    disabled={
                      !selectedAccountId ||
                      selectedPromptPackStack.length === 0 ||
                      selectedPromptPackStack.length > 1 ||
                      isAuditingPromptPack
                    }
                    style={
                      !selectedAccountId ||
                      selectedPromptPackStack.length === 0 ||
                      selectedPromptPackStack.length > 1 ||
                      isAuditingPromptPack
                        ? disabledButtonStyle
                        : {
                            ...primaryButtonStyle,
                            background: "#312e81",
                          }
                    }
                  >
                    {isAuditingPromptPack ? "Auditing..." : "Audit Selected Prompt Pack"}
                  </button>

                  {promptPackPreview?.localized_preview ||
                  promptPackAudit?.recommended_action === "localize_before_run" ||
                  promptPackAudit?.recommended_action === "use_with_edits" ? (
                    <button
                      type="button"
                      onClick={handleApplyLocalizedPromptPack}
                      disabled={
                        !selectedAccountId ||
                        selectedPromptPackStack.length !== 1 ||
                        isApplyingLocalizedPromptPack ||
                        hasBlockedPromptPackStack
                      }
                      style={
                        !selectedAccountId ||
                        selectedPromptPackStack.length !== 1 ||
                        isApplyingLocalizedPromptPack ||
                        hasBlockedPromptPackStack
                          ? disabledButtonStyle
                          : {
                              ...primaryButtonStyle,
                              background: "#0f766e",
                            }
                      }
                    >
                      {isApplyingLocalizedPromptPack
                        ? "Localizing..."
                        : promptPackPreview?.localized_preview
                          ? "Apply Previewed Localized Pack"
                          : "Apply Localized Prompt Pack"}
                    </button>
                  ) : null}

                  <button
                    type="button"
                    onClick={handleApplyPromptPack}
                    disabled={
                      !selectedAccountId ||
                      selectedPromptPackStack.length === 0 ||
                      isApplyingPromptPack ||
                      hasBlockedPromptPackStack
                    }
                    style={
                      !selectedAccountId || selectedPromptPackStack.length === 0 || isApplyingPromptPack || hasBlockedPromptPackStack
                        ? disabledButtonStyle
                        : primaryButtonStyle
                    }
                  >
                    {isApplyingPromptPack ? "Applying..." : "Apply Selected Prompt Packs"}
                  </button>
                </div>

                {promptPackPreview?.localized_preview ? (
                  <div style={{ marginTop: 10, padding: 12, borderRadius: 12, background: "#ecfdf5", border: "1px solid #bbf7d0", color: "#166534", fontSize: 13, lineHeight: 1.45, fontWeight: 800 }}>
                    Localized preview is loaded. Use Apply Previewed Localized Pack to apply the localized prompts. Apply Selected Prompt Packs applies the raw selected pack stack.
                  </div>
                ) : null}

                {selectedPromptPackStack.length > 1 ? (
                  <div style={{ marginTop: 10, color: "#92400e", fontSize: 13 }}>
                    Prompt Agent V1 audits one prompt pack at a time. Remove extra packs to audit before applying a stack.
                  </div>
                ) : null}

                {promptPackPreview ? (
                  <div style={{ marginTop: 14, padding: 14, borderRadius: 14, background: "#f8fafc", border: "1px solid #bae6fd" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
                    <div>
                      <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 0.08, color: "#0369a1", fontWeight: 900 }}>
                        Agent Smith Prompt Agent
                      </div>
                      <div style={{ marginTop: 4, fontSize: 18, fontWeight: 900, color: "#0f172a" }}>
                        {promptPackPreview.localized_preview ? "Localized Prompt Pack Preview" : "Prompt Pack Preview"}
                      </div>
                    </div>
                    <span style={{ padding: "6px 10px", borderRadius: 999, background: "#e0f2fe", color: "#075985", fontSize: 12, fontWeight: 900 }}>
                      {promptPackPreview.prompt_count || 0} prompts
                    </span>
                  </div>

                  {promptPackPreview.pack ? (
                    <div style={{ marginTop: 10, color: "#334155", fontSize: 13, lineHeight: 1.5 }}>
                      <strong>{promptPackPreview.pack.label}</strong>
                      <br />
                      {promptPackPreview.pack.pack_key}
                    </div>
                  ) : null}

                  {promptPackPreview.counts_by_category ? (
                    <div style={{ marginTop: 12, color: "#334155", fontSize: 13, lineHeight: 1.5 }}>
                      <strong>Category mix:</strong>{" "}
                      {Object.entries(promptPackPreview.counts_by_category)
                        .map(([key, value]) => `${key}: ${value}`)
                        .join(" · ")}
                    </div>
                  ) : null}

                  {Array.isArray(promptPackPreview.prompts) && promptPackPreview.prompts.length > 0 ? (
                    <div style={{ display: "grid", gap: 10, marginTop: 12 }}>
                      {promptPackPreview.prompts.slice(0, 12).map((prompt: any, index: number) => (
                        <div key={prompt.id || `preview-${index}`} style={{ padding: 10, borderRadius: 10, background: "white", border: "1px solid #e2e8f0" }}>
                          <div style={{ color: "#64748b", fontSize: 12, lineHeight: 1.45, fontWeight: 800 }}>
                            #{prompt.sort_order || index + 1} · {prompt.category || "uncategorized"} · {prompt.prompt_type || "unspecified"}
                          </div>
                          {prompt.changed ? (
                            <div style={{ marginTop: 6, color: "#166534", fontSize: 12, lineHeight: 1.45, fontWeight: 900 }}>
                              Localized
                            </div>
                          ) : null}
                          {prompt.original_prompt_text && prompt.changed ? (
                            <div style={{ marginTop: 6, color: "#64748b", fontSize: 12, lineHeight: 1.45 }}>
                              Original: {prompt.original_prompt_text}
                            </div>
                          ) : null}
                          <div style={{ marginTop: 6, color: "#0f172a", fontSize: 13, lineHeight: 1.45 }}>
                            {prompt.prompt_text}
                          </div>
                        </div>
                      ))}
                      {promptPackPreview.prompts.length > 12 ? (
                        <div style={{ color: "#64748b", fontSize: 12, fontWeight: 800 }}>
                          Showing first 12 of {promptPackPreview.prompts.length} prompts.
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              ) : null}

              {promptPackAudit ? (
                <div style={{ marginTop: 14, padding: 14, borderRadius: 14, background: "#f8fafc", border: "1px solid #c7d2fe" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
                    <div>
                      <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 0.08, color: "#4f46e5", fontWeight: 900 }}>
                        Agent Smith Prompt Agent
                      </div>
                      <div style={{ marginTop: 4, fontSize: 18, fontWeight: 900, color: "#0f172a" }}>
                        Prompt Pack Audit
                      </div>
                    </div>

                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <span style={{ padding: "6px 10px", borderRadius: 999, background: "#eef2ff", color: "#3730a3", fontSize: 12, fontWeight: 900 }}>
                        Fit: {promptPackAudit.fit_score ?? "n/a"}
                      </span>
                      <span style={{ padding: "6px 10px", borderRadius: 999, background: promptPackAudit.should_run_now ? "#ecfdf5" : "#fffbeb", color: promptPackAudit.should_run_now ? "#166534" : "#92400e", fontSize: 12, fontWeight: 900 }}>
                        {promptPackAudit.should_run_now ? "Run-ready" : "Review before run"}
                      </span>
                    </div>
                  </div>

                  {promptPackAudit.recommended_action ? (
                    <div style={{ marginTop: 12, fontSize: 13, color: "#334155", fontWeight: 900 }}>
                      Recommended action: {String(promptPackAudit.recommended_action).replaceAll("_", " ")}
                    </div>
                  ) : null}

                  {promptPackAudit.summary ? (
                    <div style={{ marginTop: 10, color: "#334155", fontSize: 13, lineHeight: 1.55 }}>
                      {promptPackAudit.summary}
                    </div>
                  ) : null}

                  {promptPackAudit.geography_fit ? (
                    <div style={{ marginTop: 12, padding: 12, borderRadius: 12, background: "#fff7ed", border: "1px solid #fed7aa" }}>
                      <div style={{ fontWeight: 900, color: "#9a3412", fontSize: 13 }}>
                        Geography fit: {promptPackAudit.geography_fit.score ?? "n/a"}
                      </div>
                      {promptPackAudit.geography_fit.account_geo_detected ? (
                        <div style={{ marginTop: 6, color: "#7c2d12", fontSize: 13, lineHeight: 1.5 }}>
                          Detected: {promptPackAudit.geography_fit.account_geo_detected}
                        </div>
                      ) : null}
                      {promptPackAudit.geography_fit.issue ? (
                        <div style={{ marginTop: 6, color: "#7c2d12", fontSize: 13, lineHeight: 1.5 }}>
                          {promptPackAudit.geography_fit.issue}
                        </div>
                      ) : null}
                      {promptPackAudit.geography_fit.recommendation ? (
                        <div style={{ marginTop: 6, color: "#7c2d12", fontSize: 13, lineHeight: 1.5, fontWeight: 800 }}>
                          {promptPackAudit.geography_fit.recommendation}
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  {Array.isArray(promptPackAudit.missing_angles) && promptPackAudit.missing_angles.length > 0 ? (
                    <div style={{ marginTop: 12 }}>
                      <div style={{ fontWeight: 900, color: "#0f172a", fontSize: 13 }}>Missing angles</div>
                      <ul style={{ marginTop: 6, paddingLeft: 20, color: "#334155", fontSize: 13, lineHeight: 1.5 }}>
                        {promptPackAudit.missing_angles.slice(0, 6).map((item: string, index: number) => (
                          <li key={`missing-${index}`}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}

                  {Array.isArray(promptPackAudit.noise_risks) && promptPackAudit.noise_risks.length > 0 ? (
                    <div style={{ marginTop: 12 }}>
                      <div style={{ fontWeight: 900, color: "#0f172a", fontSize: 13 }}>Noise risks</div>
                      <ul style={{ marginTop: 6, paddingLeft: 20, color: "#334155", fontSize: 13, lineHeight: 1.5 }}>
                        {promptPackAudit.noise_risks.slice(0, 5).map((item: string, index: number) => (
                          <li key={`risk-${index}`}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}

                  {Array.isArray(promptPackAudit.suggested_prompt_rewrites) && promptPackAudit.suggested_prompt_rewrites.length > 0 ? (
                    <div style={{ marginTop: 12 }}>
                      <div style={{ fontWeight: 900, color: "#0f172a", fontSize: 13 }}>Suggested rewrites</div>
                      <div style={{ display: "grid", gap: 10, marginTop: 8 }}>
                        {promptPackAudit.suggested_prompt_rewrites.slice(0, 5).map((rewrite: any, index: number) => (
                          <div key={`rewrite-${index}`} style={{ padding: 10, borderRadius: 10, background: "white", border: "1px solid #e2e8f0" }}>
                            {rewrite.original_prompt ? (
                              <div style={{ color: "#64748b", fontSize: 12, lineHeight: 1.45 }}>
                                Original: {rewrite.original_prompt}
                              </div>
                            ) : null}
                            {rewrite.rewritten_prompt ? (
                              <div style={{ marginTop: 6, color: "#0f172a", fontSize: 13, lineHeight: 1.45, fontWeight: 800 }}>
                                Rewrite: {rewrite.rewritten_prompt}
                              </div>
                            ) : null}
                            {rewrite.reason ? (
                              <div style={{ marginTop: 6, color: "#475569", fontSize: 12, lineHeight: 1.45 }}>
                                {rewrite.reason}
                              </div>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {!selectedAccountId ? (
                <div style={{ marginTop: 10, color: "#92400e", fontSize: 13 }}>
                  Save or select an account before applying prompt packs.
                </div>
              ) : null}

              {promptPackStatus ? (
                <div style={{ marginTop: 12, padding: 12, borderRadius: 12, background: "#ecfdf5", border: "1px solid #bbf7d0", color: "#166534", fontSize: 13, lineHeight: 1.45, fontWeight: 800 }}>
                  {promptPackStatus}
                </div>
              ) : null}
              </details>
            </section>

            <section style={cardStyle}>
              <SectionTitle eyebrow="Step 4" title="Run controls" />
              <Field label="Model profile">
                <select
                  value={modelProfileKey}
                  onChange={(event) => setModelProfileKey(event.target.value)}
                  style={inputStyle}
                >
                  {modelProfiles.map((profile) => (
                    <option key={profile.key} value={profile.key}>
                      {profile.label}
                    </option>
                  ))}
                </select>
              </Field>

              <div style={{ marginTop: 12, padding: 14, borderRadius: 12, background: "#f0f9ff", border: "1px solid #bae6fd" }}>
                <div style={{ fontWeight: 800 }}>{selectedModelProfile.label}</div>
                <div style={{ marginTop: 4, color: "#075985", fontSize: 13 }}>
                  {selectedModelProfile.description}
                </div>
                <div style={{ marginTop: 10, fontSize: 13 }}>
                  run_label: <strong>{selectedModelProfile.runLabel}</strong> · model_profile:{" "}
                  <strong>{selectedModelProfile.key}</strong>
                </div>
              </div>

              <div style={{ marginTop: 14, padding: 12, borderRadius: 12, background: "#f8fafc", border: "1px solid #e2e8f0", color: "#475569", fontSize: 13, lineHeight: 1.45 }}>
                Account details are saved in Step 1. Run controls and invite sending will be wired in a later pass.
              </div>

              <div style={{ display: "flex", gap: 10, marginTop: 16, flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={handleRunSelectedAnalysis}
                  disabled={
                    !selectedAccountId ||
                    isRunningAnalysis ||
                    hasActiveRecentRun ||
                    !activePromptSetupSummary?.activePromptCount
                  }
                  style={
                    !selectedAccountId ||
                    isRunningAnalysis ||
                    hasActiveRecentRun ||
                    !activePromptSetupSummary?.activePromptCount
                      ? disabledButtonStyle
                      : {
                          ...primaryButtonStyle,
                          background: "#0f172a",
                        }
                  }
                >
                  {isRunningAnalysis
                    ? "Running analysis..."
                    : hasActiveRecentRun
                      ? "Run already active"
                      : "Run selected analysis"}
                </button>
                <button
                  type="button"
                  onClick={() => loadRecentRuns(selectedAccountId)}
                  disabled={!selectedAccountId || isLoadingRecentRuns}
                  style={!selectedAccountId || isLoadingRecentRuns ? disabledButtonStyle : {
                    border: "1px solid #cbd5e1",
                    background: "white",
                    color: "#334155",
                    borderRadius: 999,
                    padding: "9px 13px",
                    fontSize: 13,
                    fontWeight: 800,
                    cursor: "pointer",
                  }}
                >
                  {isLoadingRecentRuns ? "Refreshing runs..." : "Refresh run status"}
                </button>

                <button type="button" disabled style={disabledButtonStyle}>
                  Send invites
                </button>
              </div>

              {hasActiveRecentRun && latestActiveRun ? (
                <div style={{ marginTop: 12, padding: 12, borderRadius: 12, background: "#fffbeb", border: "1px solid #fde68a", color: "#92400e", fontSize: 13, lineHeight: 1.45, fontWeight: 800 }}>
                  Active run in progress. New runs are disabled until this finishes. Run ID: {latestActiveRun.run_id}
                </div>
              ) : null}

              {runControlStatus ? (
                <div style={{ marginTop: 12, padding: 12, borderRadius: 12, background: runControlStatus.toLowerCase().includes("failed") ? "#fef2f2" : "#ecfdf5", border: runControlStatus.toLowerCase().includes("failed") ? "1px solid #fecaca" : "1px solid #bbf7d0", color: runControlStatus.toLowerCase().includes("failed") ? "#991b1b" : "#166534", fontSize: 13, lineHeight: 1.45, fontWeight: 800 }}>
                  {runControlStatus}
                </div>
              ) : null}

              {selectedAccountId ? <CaiDiscoverySourcesPanel key={selectedAccountId} accountId={selectedAccountId} runs={recentRuns} onRefreshed={() => setDiscoveryRefreshVersion((version) => version + 1)} /> : null}
              {selectedAccountId ? <CaiReviewedAssetPanel key={`${selectedAccountId}:${discoveryRefreshVersion}`} accountId={selectedAccountId} runs={recentRuns} /> : null}

              {selectedAccountId ? (
                <div style={{ marginTop: 14, padding: 14, borderRadius: 14, background: "#f8fafc", border: "1px solid #e2e8f0" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                    <div>
                      <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 0.08, color: "#475569", fontWeight: 900 }}>
                        Run status
                      </div>
                      <div style={{ marginTop: 4, fontWeight: 900, color: "#0f172a" }}>
                        Recent runs
                      </div>
                    </div>
                    <div style={{ color: "#64748b", fontSize: 12, fontWeight: 800 }}>
                      {isLoadingRecentRuns
                        ? "Refreshing..."
                        : recentRuns.some((run: any) => isActiveRunStatus(run.status))
                          ? "Auto-refreshing active run"
                          : `${recentRuns.length} shown`}
                    </div>
                  </div>

                  {recentRuns.length === 0 ? (
                    <div style={{ marginTop: 10, color: "#64748b", fontSize: 13 }}>
                      No recent runs found for this account yet.
                    </div>
                  ) : (
                    <div style={{ display: "grid", gap: 8, marginTop: 12 }}>
                      {recentRuns.map((run: any) => {
                        const status = String(run.status || "unknown");
                        const isFailed = status.toLowerCase().includes("failed") || status.toLowerCase().includes("error");
                        const isComplete = status.toLowerCase().includes("completed");
                        const badgeBackground = isFailed ? "#fef2f2" : isComplete ? "#ecfdf5" : "#fffbeb";
                        const badgeColor = isFailed ? "#991b1b" : isComplete ? "#166534" : "#92400e";

                        return (
                          <div key={run.run_id} style={{ padding: 10, borderRadius: 10, background: "white", border: "1px solid #e2e8f0" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                              <div style={{ color: "#0f172a", fontSize: 13, fontWeight: 900 }}>
                                {run.run_label || "manual run"}
                              </div>
                              <span style={{ padding: "4px 8px", borderRadius: 999, background: badgeBackground, color: badgeColor, fontSize: 12, fontWeight: 900 }}>
                                {status}
                              </span>
                            </div>
                            <div style={{ marginTop: 6, color: "#64748b", fontSize: 12, lineHeight: 1.5 }}>
                              Run ID: <strong>{run.run_id}</strong>
                              <br />
                              Prompts: <strong>{run.prompt_count ?? 0}</strong>
                              {isActiveRunStatus(run.status) ? <> · <strong>Polling every 15s</strong></> : null}
                              {run.model_version ? <> · Model: <strong>{run.model_version}</strong></> : null}
                              {run.created_at ? <> · Created: <strong>{new Date(run.created_at).toLocaleString()}</strong></> : null}
                              {run.completed_at ? <> · Completed: <strong>{new Date(run.completed_at).toLocaleString()}</strong></> : null}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : null}
            </section>
          </main>

          <aside
            style={{
              ...cardStyle,
              position: selectedAccountId && accountType === "competitive" ? "static" : "sticky",
              top: 20,
              maxWidth: selectedAccountId && accountType === "competitive" ? "100%" : 320,
            }}
          >
            <SectionTitle eyebrow="Review" title="Onboarding summary" />
            <SummaryRow label="Account" value={accountName || "Not set"} />
            <SummaryRow label="Account ID" value={selectedAccountId || "Not saved"} />
            <SummaryRow label="Website" value={website || "Not set"} />
            <SummaryRow label="Domain" value={websiteDomain || "Not set"} />
            <SummaryRow label="Status" value={accountStatus} />
            <SummaryRow label="Category" value={category || "Not set"} />
            <SummaryRow label="Account type" value={accountType} />
            <SummaryRow label="Business name" value={businessName || accountName || "Not set"} />
            <SummaryRow label="Market" value={market || "Not set"} />
            <SummaryRow label="Access" value={selectedPreset.label} />
            <SummaryRow label="New users staged" value={String(parsedUsers.length)} />
            <SummaryRow label="Run profile" value={selectedModelProfile.label} />
            <SummaryRow label="Readiness" value={onboardingReadiness.status} />
            <SummaryRow label="Intelligence" value={intelligenceReadinessSummary.status} />
            <SummaryRow label="Admin user" value={session?.user?.email || session?.user?.id || "Signed in"} />

            {saveStatus ? (
              <div style={{ marginTop: 18, padding: 12, borderRadius: 12, background: "#ecfdf5", border: "1px solid #bbf7d0", color: "#166534", fontSize: 13, lineHeight: 1.45 }}>
                {saveStatus}
              </div>
            ) : (
              <div style={{ marginTop: 18, padding: 12, borderRadius: 12, background: "#fff7ed", border: "1px solid #fed7aa", color: "#9a3412", fontSize: 13, lineHeight: 1.45 }}>
                Account save is enabled. User invites and run controls are still disabled until the next wiring pass.
              </div>
            )}

            <a href="/" style={{ display: "inline-block", marginTop: 16, color: "#0369a1", fontWeight: 800, textDecoration: "none" }}>
              ← Back to Dashboard
            </a>
          </aside>
        </div>
      </div>
    </div>
  );
}


function BrandSeedReviewTable({
  title,
  rows,
  type,
  updatingItemId,
  onUpdateItem,
  bulkUpdatingType,
  onBulkUpdate,
  reviewBatch
}: {
  title: string;
  rows: any[];
  type: "seed" | "suppression" | "needs_review";
  updatingItemId?: string | null;
  onUpdateItem?: (itemId: string, patch: Record<string, any>) => void;
  bulkUpdatingType?: string | null;
  onBulkUpdate?: (
    itemType: "seed" | "suppression" | "needs_review",
    reviewDecision: "pending" | "approved" | "rejected" | "needs_review"
  ) => void;
  reviewBatch?: any;
}) {
  const hasMaterializedReviewItems = Array.isArray(reviewBatch?.items);
  const materializedRows = hasMaterializedReviewItems
    ? reviewBatch.items.filter((item: any) => item.item_type === type)
    : [];

  const effectiveRows = hasMaterializedReviewItems ? materializedRows : rows;

  const miniButtonStyle: React.CSSProperties = {
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#0f172a",
    borderRadius: 999,
    padding: "5px 8px",
    fontSize: 11,
    fontWeight: 800,
    cursor: "pointer"
  };

  const pendingCount = effectiveRows.filter((row: any) => (row.review_decision || "pending") === "pending").length;
  const canBulkApprove = type === "seed" || type === "suppression";
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<Record<string, any>>({});

  function listToText(value: any): string {
    return Array.isArray(value) ? value.join(", ") : "";
  }

  function textToList(value: any): string[] {
    return String(value || "")
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }

  function startEditReviewItem(row: any) {
    const displayName = String(row.raw_entity || row.canonical_brand || "").trim();

    setEditingRowId(row.id);
    setEditDraft({
      item_type: row.item_type || type,
      review_decision: row.review_decision || "pending",
      canonical_brand: row.canonical_brand || displayName,
      raw_entity: row.raw_entity || displayName,
      parent_entity: row.parent_entity || "",
      aliases_text: listToText(row.aliases),
      patterns_text: listToText(row.patterns),
      entity_role: row.entity_role || (type === "needs_review" ? "suppress" : ""),
      score_treatment: row.score_treatment || (type === "needs_review" ? "suppress_from_scoring" : ""),
      confidence_score: row.confidence_score ?? "",
      rationale: row.rationale || row.reason || "",
      admin_notes: row.admin_notes || ""
    });
  }

  function updateEditDraft(field: string, value: any) {
    setEditDraft((current) => ({
      ...current,
      [field]: value
    }));
  }

  async function saveEditReviewItem(rowId: string) {
    if (!onUpdateItem) {
      return;
    }

    const confidenceText = String(editDraft.confidence_score ?? "").trim();
    const confidenceValue = confidenceText === "" ? null : Number(confidenceText);

    await onUpdateItem(rowId, {
      item_type: editDraft.item_type,
      review_decision: editDraft.review_decision,
      canonical_brand: String(editDraft.canonical_brand || "").trim() || null,
      raw_entity: String(editDraft.raw_entity || "").trim() || null,
      parent_entity: String(editDraft.parent_entity || "").trim() || null,
      aliases: textToList(editDraft.aliases_text),
      patterns: textToList(editDraft.patterns_text),
      entity_role: String(editDraft.entity_role || "").trim() || null,
      score_treatment: String(editDraft.score_treatment || "").trim() || null,
      confidence_score: Number.isFinite(confidenceValue) ? confidenceValue : null,
      rationale: String(editDraft.rationale || "").trim() || null,
      admin_notes: String(editDraft.admin_notes || "").trim() || null
    });

    setEditingRowId(null);
    setEditDraft({});
  }

  const editInputStyle: React.CSSProperties = {
    width: "100%",
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    padding: "8px 10px",
    fontSize: 12,
    color: "#0f172a",
    background: "#ffffff"
  };

  const editLabelStyle: React.CSSProperties = {
    display: "block",
    marginBottom: 4,
    color: "#475569",
    fontSize: 11,
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: "0.04em"
  };

  if (!effectiveRows || effectiveRows.length === 0) return null;

  return (
    <div style={{ marginTop: 14 }}>
      <div
        style={{
          margin: "0 0 8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12
        }}
      >
        <h4 style={{ margin: 0, fontSize: 14, color: "#0f172a" }}>{title}</h4>

        {canBulkApprove && onBulkUpdate ? (
          <button
            type="button"
            disabled={pendingCount === 0 || bulkUpdatingType === type}
            onClick={() => onBulkUpdate(type, "approved")}
            style={{
              ...miniButtonStyle,
              padding: "6px 10px",
              fontWeight: 900,
              opacity: pendingCount === 0 || bulkUpdatingType === type ? 0.55 : 1,
              cursor: pendingCount === 0 || bulkUpdatingType === type ? "not-allowed" : "pointer",
              whiteSpace: "nowrap"
            }}
          >
            {bulkUpdatingType === type
              ? "Approving..."
              : `Approve all pending (${pendingCount})`}
          </button>
        ) : null}
      </div>
      <div style={{ overflowX: "auto", border: "1px solid #e2e8f0", borderRadius: 12 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ background: "#f8fafc", color: "#475569", textAlign: "left" }}>
              <th style={brandSeedThStyle}>Brand / Entity</th>
              <th style={brandSeedThStyle}>Patterns</th>
              <th style={brandSeedThStyle}>Role</th>
              <th style={brandSeedThStyle}>Treatment</th>
              <th style={brandSeedThStyle}>Confidence</th>
              <th style={brandSeedThStyle}>Decision</th>
              <th style={brandSeedThStyle}>Actions</th>
              <th style={brandSeedThStyle}>Rationale</th>
            </tr>
          </thead>
          <tbody>
            {effectiveRows.map((row, index) => {
              const name = type === "needs_review" ? row.raw_entity : row.canonical_brand;
              const patterns = type === "needs_review" ? "" : (row.patterns || []).join(", ");
              const role = type === "needs_review" ? "needs_review" : row.entity_role;
              const treatment = type === "needs_review" ? "needs_review" : row.score_treatment;
              const confidence = type === "needs_review" ? "" : String(row.confidence_score ?? "");
              const rationale = type === "needs_review" ? row.reason : row.rationale;
              const isEditing = editingRowId === row.id;
              const needsReviewName = String(row.raw_entity || row.canonical_brand || "").trim();
              const resolveAsSuppressionPatch = {
                item_type: "suppression",
                review_decision: "approved",
                canonical_brand: needsReviewName,
                raw_entity: needsReviewName,
                aliases: [],
                patterns: needsReviewName ? [needsReviewName.toLowerCase()] : [],
                entity_role: "suppress",
                score_treatment: "suppress_from_scoring",
                confidence_score: row.confidence_score ?? 0.25,
                rationale:
                  row.rationale ||
                  row.reason ||
                  "Resolved from needs review as a suppressed entity during admin review.",
                admin_notes: "Resolved from needs review in Admin Onboarding."
              };

              return (
                <React.Fragment key={`${name}-${index}`}>
                  <tr style={{ borderTop: "1px solid #e2e8f0" }}>
                  <td style={brandSeedTdStyle}>
                    <strong>{name}</strong>
                    {row.parent_entity ? (
                      <div style={{ color: "#64748b", marginTop: 2 }}>Parent: {row.parent_entity}</div>
                    ) : null}
                    {row.aliases && row.aliases.length > 0 ? (
                      <div style={{ color: "#64748b", marginTop: 2 }}>Aliases: {row.aliases.join(", ")}</div>
                    ) : null}
                  </td>
                  <td style={brandSeedTdStyle}>{patterns}</td>
                  <td style={brandSeedTdStyle}>{role}</td>
                  <td style={brandSeedTdStyle}>{treatment}</td>
                  <td style={brandSeedTdStyle}>{confidence}</td>
                  <td style={brandSeedTdStyle}>
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        borderRadius: 999,
                        padding: "3px 8px",
                        fontSize: 11,
                        fontWeight: 900,
                        background:
                          row.review_decision === "approved"
                            ? "#dcfce7"
                            : row.review_decision === "rejected"
                              ? "#fee2e2"
                              : row.review_decision === "needs_review"
                                ? "#fef3c7"
                                : "#e0f2fe",
                        color:
                          row.review_decision === "approved"
                            ? "#166534"
                            : row.review_decision === "rejected"
                              ? "#991b1b"
                              : row.review_decision === "needs_review"
                                ? "#92400e"
                                : "#075985"
                      }}
                    >
                      {row.review_decision || "pending"}
                    </span>
                  </td>
                  <td style={{ ...brandSeedTdStyle, minWidth: 240 }}>
                    {row.id && onUpdateItem ? (
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        {type === "needs_review" && needsReviewName ? (
                          <button
                            type="button"
                            disabled={updatingItemId === row.id}
                            onClick={() => onUpdateItem(row.id, resolveAsSuppressionPatch)}
                            style={miniButtonStyle}
                          >
                            Resolve as Suppression
                          </button>
                        ) : null}
                        <button
                          type="button"
                          disabled={updatingItemId === row.id}
                          onClick={() => startEditReviewItem(row)}
                          style={miniButtonStyle}
                        >
                          Edit / Resolve
                        </button>
                        <button
                          type="button"
                          disabled={updatingItemId === row.id}
                          onClick={() => onUpdateItem(row.id, { review_decision: "approved" })}
                          style={miniButtonStyle}
                        >
                          Approve
                        </button>
                        <button
                          type="button"
                          disabled={updatingItemId === row.id}
                          onClick={() => onUpdateItem(row.id, { review_decision: "rejected" })}
                          style={miniButtonStyle}
                        >
                          Reject
                        </button>
                        <button
                          type="button"
                          disabled={updatingItemId === row.id}
                          onClick={() => onUpdateItem(row.id, { review_decision: "needs_review" })}
                          style={miniButtonStyle}
                        >
                          Needs Review
                        </button>
                        <button
                          type="button"
                          disabled={updatingItemId === row.id}
                          onClick={() => onUpdateItem(row.id, { review_decision: "pending" })}
                          style={miniButtonStyle}
                        >
                          Pending
                        </button>
                      </div>
                    ) : (
                      <span style={{ color: "#94a3b8" }}>Saved item required</span>
                    )}
                  </td>
                  <td style={{ ...brandSeedTdStyle, minWidth: 260 }}>{rationale}</td>
                </tr>
                {isEditing ? (
                  <tr style={{ background: "#f8fafc", borderTop: "1px solid #e2e8f0" }}>
                    <td colSpan={8} style={{ padding: 14 }}>
                      <div
                        style={{
                          border: "1px solid #dbeafe",
                          borderRadius: 12,
                          padding: 14,
                          background: "#ffffff"
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 12 }}>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 900, color: "#0f172a" }}>
                              Edit Agent Smith review item
                            </div>
                            <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                              Resolve ambiguous entities before applying them.
                            </div>
                          </div>
                          <div style={{ display: "flex", gap: 8 }}>
                            <button
                              type="button"
                              disabled={updatingItemId === row.id}
                              onClick={() => saveEditReviewItem(row.id)}
                              style={miniButtonStyle}
                            >
                              Save Item
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setEditingRowId(null);
                                setEditDraft({});
                              }}
                              style={miniButtonStyle}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>

                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(4, minmax(160px, 1fr))",
                            gap: 10
                          }}
                        >
                          <label>
                            <span style={editLabelStyle}>Item type</span>
                            <select
                              value={editDraft.item_type || "needs_review"}
                              onChange={(event) => updateEditDraft("item_type", event.target.value)}
                              style={editInputStyle}
                            >
                              <option value="seed">seed</option>
                              <option value="suppression">suppression</option>
                              <option value="needs_review">needs_review</option>
                            </select>
                          </label>

                          <label>
                            <span style={editLabelStyle}>Decision</span>
                            <select
                              value={editDraft.review_decision || "pending"}
                              onChange={(event) => updateEditDraft("review_decision", event.target.value)}
                              style={editInputStyle}
                            >
                              <option value="pending">pending</option>
                              <option value="approved">approved</option>
                              <option value="rejected">rejected</option>
                              <option value="needs_review">needs_review</option>
                            </select>
                          </label>

                          <label>
                            <span style={editLabelStyle}>Entity role</span>
                            <select
                              value={editDraft.entity_role || ""}
                              onChange={(event) => updateEditDraft("entity_role", event.target.value)}
                              style={editInputStyle}
                            >
                              <option value="">Select role</option>
                              <option value="direct_competitor">direct_competitor</option>
                              <option value="adjacent_competitor">adjacent_competitor</option>
                              <option value="platform">platform</option>
                              <option value="integration">integration</option>
                              <option value="partner">partner</option>
                              <option value="generic_tool">generic_tool</option>
                              <option value="suppress">suppress</option>
                              <option value="unknown">unknown</option>
                              <option value="needs_review">needs_review</option>
                            </select>
                          </label>

                          <label>
                            <span style={editLabelStyle}>Score treatment</span>
                            <select
                              value={editDraft.score_treatment || ""}
                              onChange={(event) => updateEditDraft("score_treatment", event.target.value)}
                              style={editInputStyle}
                            >
                              <option value="">Select treatment</option>
                              <option value="include_primary">include_primary</option>
                              <option value="include_secondary">include_secondary</option>
                              <option value="show_only">show_only</option>
                              <option value="suppress_from_scoring">suppress_from_scoring</option>
                              <option value="suppress_everywhere">suppress_everywhere</option>
                              <option value="needs_review">needs_review</option>
                            </select>
                          </label>

                          <label>
                            <span style={editLabelStyle}>Canonical brand</span>
                            <input value={editDraft.canonical_brand || ""} onChange={(event) => updateEditDraft("canonical_brand", event.target.value)} style={editInputStyle} />
                          </label>

                          <label>
                            <span style={editLabelStyle}>Raw entity</span>
                            <input value={editDraft.raw_entity || ""} onChange={(event) => updateEditDraft("raw_entity", event.target.value)} style={editInputStyle} />
                          </label>

                          <label>
                            <span style={editLabelStyle}>Parent entity</span>
                            <input value={editDraft.parent_entity || ""} onChange={(event) => updateEditDraft("parent_entity", event.target.value)} style={editInputStyle} />
                          </label>

                          <label>
                            <span style={editLabelStyle}>Confidence</span>
                            <input type="number" min="0" max="1" step="0.01" value={editDraft.confidence_score ?? ""} onChange={(event) => updateEditDraft("confidence_score", event.target.value)} style={editInputStyle} />
                          </label>

                          <label style={{ gridColumn: "span 2" }}>
                            <span style={editLabelStyle}>Patterns, comma-separated</span>
                            <input value={editDraft.patterns_text || ""} onChange={(event) => updateEditDraft("patterns_text", event.target.value)} style={editInputStyle} />
                          </label>

                          <label style={{ gridColumn: "span 2" }}>
                            <span style={editLabelStyle}>Aliases, comma-separated</span>
                            <input value={editDraft.aliases_text || ""} onChange={(event) => updateEditDraft("aliases_text", event.target.value)} style={editInputStyle} />
                          </label>

                          <label style={{ gridColumn: "span 2" }}>
                            <span style={editLabelStyle}>Rationale</span>
                            <textarea rows={3} value={editDraft.rationale || ""} onChange={(event) => updateEditDraft("rationale", event.target.value)} style={editInputStyle} />
                          </label>

                          <label style={{ gridColumn: "span 2" }}>
                            <span style={editLabelStyle}>Admin notes</span>
                            <textarea rows={3} value={editDraft.admin_notes || ""} onChange={(event) => updateEditDraft("admin_notes", event.target.value)} style={editInputStyle} />
                          </label>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : null}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const brandSeedThStyle: React.CSSProperties = {
  padding: "9px 10px",
  fontWeight: 900,
  borderBottom: "1px solid #e2e8f0",
  whiteSpace: "nowrap"
};

const brandSeedTdStyle: React.CSSProperties = {
  padding: "9px 10px",
  verticalAlign: "top",
  color: "#334155"
};


function SectionTitle({ eyebrow, title }: { eyebrow: string; title: string }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 12, fontWeight: 900, color: "#0ea5e9", textTransform: "uppercase", letterSpacing: "0.08em" }}>
        {eyebrow}
      </div>
      <h2 style={{ margin: "4px 0 0", fontSize: 20 }}>{title}</h2>
    </div>
  );
}

function Field({ label, help, children }: { label: string; help?: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "grid", gap: 6, fontSize: 13, fontWeight: 800, color: "#334155" }}>
      {label}
      {children}
      {help ? <span style={{ color: "#64748b", fontSize: 12, fontWeight: 500 }}>{help}</span> : null}
    </label>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "9px 0", borderBottom: "1px solid #e2e8f0", fontSize: 13 }}>
      <span style={{ color: "#64748b" }}>{label}</span>
      <strong style={{ textAlign: "right" }}>{value}</strong>
    </div>
  );
}

function MiniInfoChip({ label, value }: { label: string; value: number | string }) {
  return (
    <div style={{
      padding: "9px 10px",
      borderRadius: 12,
      background: "rgba(255,255,255,0.7)",
      border: "1px solid rgba(148,163,184,0.28)",
      display: "grid",
      gap: 4
    }}>
      <div style={{ color: "#64748b", fontSize: 11, fontWeight: 900, textTransform: "uppercase", letterSpacing: 0.35 }}>
        {label}
      </div>
      <div style={{ color: "#0f172a", fontSize: 15, fontWeight: 950 }}>
        {value}
      </div>
    </div>
  );
}

const cardStyle: React.CSSProperties = {
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: 18,
  padding: 18,
  boxShadow: "0 16px 40px rgba(15, 23, 42, 0.06)",
};

const gridTwoStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: 14,
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  border: "1px solid #cbd5e1",
  borderRadius: 12,
  padding: "10px 12px",
  fontSize: 14,
  color: "#0f172a",
  background: "#ffffff",
};


const primaryButtonStyle: React.CSSProperties = {
  border: "1px solid #0284c7",
  background: "#0284c7",
  color: "#ffffff",
  borderRadius: 999,
  padding: "10px 14px",
  fontSize: 13,
  fontWeight: 800,
  cursor: "pointer",
};

const disabledButtonStyle: React.CSSProperties = {
  border: "1px solid #cbd5e1",
  background: "#f1f5f9",
  color: "#64748b",
  borderRadius: 999,
  padding: "10px 14px",
  fontSize: 13,
  fontWeight: 800,
  cursor: "not-allowed",
};

export default AdminOnboarding;


/*
AGENT_SMITH_V2_SAFETY_NOTE

Prompt pack selection is now considered legacy/manual fallback behavior.
When wiring the pack card or apply handler, call:

  const safety = getPromptPackSafety({
    accountName: selectedAccount?.account_name,
    businessName: selectedAccount?.business_name,
    displayName: selectedAccount?.display_name,
    packKey: selectedPack?.pack_key,
    packLabel: selectedPack?.label,
    sourceAccountName: selectedPack?.source_account_name,
    categoryKey: selectedPack?.category_key,
    productType: selectedPack?.product_type,
  });

Block apply/run when safety.status === "blocked".
Show safety.reasons in the card when safety.status !== "safe".

This protects against the Acura / Quality Chevrolet GMC contamination class.
*/
